import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  ClockIcon,
  BellIcon,
  UserGroupIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  HomeIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  PencilIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { workloadAPI, feedbackAPI } from '../services/api';
import moment from 'moment-timezone';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';
import ConfirmDialog from '../components/ConfirmDialog';
import TeamHierarchyFlow from '../components/TeamHierarchyFlow';
import NotificationBell from '../components/NotificationBell';
import UserProfilePhotoUploadModal from '../components/UserProfilePhotoUploadModal';
import { initPush } from '../utils/pushNotifications';
import { gsap } from 'gsap';
import DashboardSwitcher from '../components/DashboardSwitcher';
import ManagerStatsCards from './managerDashboard/ManagerStatsCards';
import AbsenteesModal from './managerDashboard/AbsenteesModal';
import MemberAttendanceCalendar from './managerDashboard/MemberAttendanceCalendar';
import { createAttendanceViewModeChangeHandler } from './managerDashboard/attendanceViewScroll';
import { filterTeamMembersBySearch } from './managerDashboard/teamMemberSearch';
import { buildTodayAttendanceSummary } from './managerDashboard/todayAttendanceSummary';
import {
  CALENDAR_ATTENDANCE_LEGEND,
  CALENDAR_ATTENDANCE_STYLES,
  buildCalendarAttendanceCounts,
} from './managerDashboard/calendarAttendanceSummary';
import { buildRollingRequestDateRange } from './managerDashboard/requestDateRange';
import useNativePullToRefresh from '../hooks/useNativePullToRefresh';

// Manager Loader Component
function PongLoadingScreen() {
  const containerRef = useRef(null);
  const loaderRef = useRef(null);

  useEffect(() => {
    const themes = ["hsl(16, 40.40%, 10.00%)", "hsl(115, 28.40%, 10.00%)", "hsl(47, 28.40%, 10.00%)", "hsl(219, 28.40%, 10.00%)"];
    let counter = 0;

    const container = containerRef.current;
    const loader = loaderRef.current;
    if (!container || !loader) return;

    const tl = gsap.timeline({ repeat: -1 });
    tl.to(".manager-vertical", { height: "170px", ease: "back.out(2)", delay: 0.5, duration: 0.8, stagger: 0.8 })
      .to(".manager-horizontal", { width: "170px", ease: "back.out(2)", duration: 0.8, stagger: 0.8 }, "-=1.2")
      .to(".manager-vertical", { width: "60px", ease: "back.out(2)", delay: 0.5, duration: 0.8, stagger: 0.8 }, "-=1.1")
      .to(".manager-horizontal", { height: "60px", ease: "back.out(2)", duration: 0.8, stagger: 0.8 }, "-=1.1")
      .to(".manager-vertical", { width: "37.5px", ease: "power2.out", duration: 0.8, stagger: 0.8 }, "-=1.1")
      .to(".manager-horizontal", { height: "37.5px", ease: "power2.out", duration: 0.8, stagger: 0.4 }, "-=1.1")
      .to(".manager-horizontal", { width: "37.5px", ease: "power2.out", duration: 0.8, stagger: 0.2 }, "-=.7")
      .to(".manager-vertical", { height: "37.5px", ease: "power2.out", duration: 0.8, stagger: 0.2 }, "-=.7")
      .to(".manager-loader-content-container", { 
        scale: "0.7", 
        duration: 1, 
        delay: 0.4, 
        ease: "back.inOut(3)", 
        onStart: () => {
          const boxes = container.querySelectorAll(".manager-box");
          boxes.forEach((box, index) => {
            if (index === counter) {
              gsap.to(box, { width: "70px", height: "70px", borderRadius: "100%" });
            } else {
              gsap.to(box, { width: "0px", height: "0px" });
            }
          });
          container.style.backgroundColor = themes[counter];
          counter = (counter + 1) % 4;
        }
      })
      .to(".manager-loader", { rotate: "1080deg", duration: 4, ease: "back.inOut(4)" })
      .to(".manager-loader-content-container", { 
        scale: "1", 
        duration: 1, 
        delay: 0.4, 
        ease: "back.inOut(3)", 
        onStart: () => {
          const boxes = container.querySelectorAll(".manager-box");
          boxes.forEach((box) => {
            gsap.to(box, { width: "37.5px", height: "37.5px", borderRadius: "37.5px" });
          });
          container.style.backgroundColor = "#1e1d1d";
        }
      });

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <>
      <style>{`
        #manager-loader-viewport-container {
          width: 100%;
          height: 100vh;
          position: fixed;
          top: 0;
          left: 0;
          display: grid;
          place-items: center;
          background-color: #1e1d1d;
          transition: all .4s ease;
          z-index: 9999;
        }

        #manager-loader-viewport-container .manager-loader {
          width: 200px;
          aspect-ratio: 1/1;
          display: grid;
          place-items: center;
        }

        #manager-loader-viewport-container .manager-loader-content-container {
          position: relative;
          width: 150px;
          aspect-ratio: 1/1;
        }

        #manager-loader-viewport-container .manager-box {
          height: 37.5px;
          width: 37.5px;
          border-radius: 37.5px;
          position: absolute;
        }

        #manager-loader-viewport-container .manager-box:first-child {
          top: 0px;
          left: 0px;
          z-index: 3;
          transform-origin: top;
          background-color: rgb(255, 68, 0);
        }

        #manager-loader-viewport-container .manager-box:nth-child(2) {
          top: 0;
          right: 0px;
          z-index: 2;
          transform-origin: right;
          background-color: rgb(35, 100, 29);
        }

        #manager-loader-viewport-container .manager-box:nth-child(3) {
          bottom: 0px;
          right: 0px;
          z-index: 1;
          transform-origin: bottom;
          background-color: rgb(160, 133, 24);
        }

        #manager-loader-viewport-container .manager-box:last-child {
          z-index: 4;
          bottom: 0px;
          left: 0px;
          transform-origin: left;
          background-color: rgb(34, 88, 180);
        }
      `}</style>
      <div id="manager-loader-viewport-container" ref={containerRef}>
        <div className="manager-loader" ref={loaderRef}>
          <div className="manager-loader-content-container">
            <div className="manager-box manager-vertical"></div>
            <div className="manager-box manager-horizontal"></div>
            <div className="manager-box manager-vertical"></div>
            <div className="manager-box manager-horizontal"></div>
          </div>
        </div>
      </div>
    </>
  );
}

const getExcludedMemberName = (member) =>
  member?.name ||
  [member?.firstName, member?.lastName].filter(Boolean).join(' ') ||
  member?.userName ||
  'Unknown';

const getExcludedMemberEmail = (member) => member?.officialEmail || member?.email || 'No email';

const getExcludedMemberDepartment = (member) => member?.Department || member?.department || 'N/A';

export function ManagerProfileSummary({ user, onProfilePhotoEdit }) {
  const displayName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User';

  return (
    <div className="flex min-w-0 flex-1 items-center gap-4 pr-2 lg:pr-4">
      <div className="relative flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/35 bg-white/18 shadow-lg ring-1 ring-white/10">
        {user?.profilePhotoUrl ? (
          <img
            src={user.profilePhotoUrl}
            alt={displayName}
            className="h-full w-full object-cover"
          />
        ) : (
          <UserCircleIcon className="h-9 w-9 text-white" />
        )}
        <button
          type="button"
          onClick={onProfilePhotoEdit}
          title="Edit profile photo"
          className="absolute -bottom-1 -right-1 inline-flex h-6 w-6 items-center justify-center rounded-full border border-white bg-white text-slate-800 shadow-md transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/65">
          Manager Dashboard
        </p>
        <h1 className="truncate text-xl font-semibold text-white sm:text-2xl">
          {user?.firstName} {user?.lastName}
        </h1>
        <p className="truncate text-sm font-medium text-white/75">
          {user?.department || 'Manager Workspace'}
        </p>
      </div>
    </div>
  );
}

const getMemberDisplayName = (member = {}) =>
  member.name ||
  [member.firstName, member.lastName].filter(Boolean).join(' ') ||
  member.officialEmail ||
  member.email ||
  'User';

const getMemberInitials = (member = {}) => {
  const name = getMemberDisplayName(member);
  const initials = name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  return initials || 'U';
};

export function TeamMemberAvatar({
  member,
  className = 'h-8 w-8',
  fallbackClassName = 'bg-slate-100 text-slate-700',
  textClassName = 'text-xs font-bold',
  roundedClassName = 'rounded-full',
}) {
  const displayName = getMemberDisplayName(member);

  return (
    <div className={`${className} ${roundedClassName} flex flex-shrink-0 items-center justify-center overflow-hidden ${member?.profilePhotoUrl ? 'bg-white' : fallbackClassName}`}>
      {member?.profilePhotoUrl ? (
        <img
          src={member.profilePhotoUrl}
          alt={displayName}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className={textClassName}>{getMemberInitials(member)}</span>
      )}
    </div>
  );
}

export function ExcludedMembersSection({ excludedMembers = [] }) {
  const [isOpen, setIsOpen] = useState(false);

  if (excludedMembers.length === 0) {
    return null;
  }

  return (
    <div
      id="excluded-members"
      className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4"
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="excluded-members-panel"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full flex-col gap-3 text-left sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h4 className="text-sm font-semibold text-amber-900">
            Excluded Members
          </h4>
          <p className="text-xs text-amber-700">
            These team members are intentionally excluded from attendance stats.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-center">
          <div className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
            {excludedMembers.length} excluded
          </div>
          {isOpen ? (
            <ChevronUpIcon className="h-5 w-5 text-amber-700" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-amber-700" />
          )}
        </div>
      </button>

      {isOpen && (
        <div
          id="excluded-members-panel"
          className="mt-3 grid grid-cols-1 gap-2 lg:grid-cols-2"
        >
          {excludedMembers.map((member, index) => {
            const memberName = getExcludedMemberName(member);
            const memberEmail = getExcludedMemberEmail(member);
            const memberDepartment = getExcludedMemberDepartment(member);

            return (
              <div
                key={member?.id || member?._id || memberEmail || `${memberName}-${index}`}
                className="rounded-lg border border-amber-200 bg-white px-3 py-2"
              >
                <div className="text-sm font-medium text-gray-900">{memberName}</div>
                <div className="text-xs text-gray-600">{memberEmail}</div>
                <div className="mt-1 text-xs text-amber-700">{memberDepartment}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CustomSummaryEmailSection({
  summaryEmailForm,
  showSummaryAdvanced,
  sendingSummaryEmail,
  summaryEmailResult,
  onSummaryFieldChange,
  onToggleAdvanced,
  onSend,
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls="custom-summary-email-panel"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <div>
          <h4 className="text-sm font-semibold text-gray-900">Custom Summary Email</h4>
          <p className="text-xs text-gray-500">Send a filtered attendance summary to any email.</p>
        </div>
        {isOpen ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div id="custom-summary-email-panel" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 mb-4">
            <button
              type="button"
              onClick={onToggleAdvanced}
              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {showSummaryAdvanced ? 'Hide Advanced' : 'Advanced Filters'}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To Email</label>
              <input
                type="email"
                value={summaryEmailForm.toEmail}
                onChange={(e) => onSummaryFieldChange('toEmail', e.target.value)}
                placeholder="manager@company.com"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">CC Emails</label>
              <input
                type="text"
                value={summaryEmailForm.ccEmails}
                onChange={(e) => onSummaryFieldChange('ccEmails', e.target.value)}
                placeholder="email1@company.com, email2@company.com"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
              <input
                type="text"
                value={summaryEmailForm.subject}
                onChange={(e) => onSummaryFieldChange('subject', e.target.value)}
                placeholder="Attendance Summary - Below 80% (Optional)"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Attendance %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={summaryEmailForm.maxAttendancePercent}
                onChange={(e) => onSummaryFieldChange('maxAttendancePercent', e.target.value)}
                placeholder="80"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min Attendance %</label>
              <input
                type="number"
                min="0"
                max="100"
                value={summaryEmailForm.minAttendancePercent}
                onChange={(e) => onSummaryFieldChange('minAttendancePercent', e.target.value)}
                placeholder="Optional"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Department</label>
              <input
                type="text"
                value={summaryEmailForm.department}
                onChange={(e) => onSummaryFieldChange('department', e.target.value)}
                placeholder="Single department"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Departments (comma)</label>
              <input
                type="text"
                value={summaryEmailForm.departments}
                onChange={(e) => onSummaryFieldChange('departments', e.target.value)}
                placeholder="Sales, Ops, Tech"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <input
                type="text"
                value={summaryEmailForm.search}
                onChange={(e) => onSummaryFieldChange('search', e.target.value)}
                placeholder="Name, email, or department"
                className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {showSummaryAdvanced && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Expected Hours</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.minExpectedHours}
                  onChange={(e) => onSummaryFieldChange('minExpectedHours', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Expected Hours</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.maxExpectedHours}
                  onChange={(e) => onSummaryFieldChange('maxExpectedHours', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Total Hours</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.minTotalHours}
                  onChange={(e) => onSummaryFieldChange('minTotalHours', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Total Hours</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.maxTotalHours}
                  onChange={(e) => onSummaryFieldChange('maxTotalHours', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Days Present</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.minDaysPresent}
                  onChange={(e) => onSummaryFieldChange('minDaysPresent', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Days Present</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.maxDaysPresent}
                  onChange={(e) => onSummaryFieldChange('maxDaysPresent', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Min Working Days</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.minWorkingDays}
                  onChange={(e) => onSummaryFieldChange('minWorkingDays', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Max Working Days</label>
                <input
                  type="number"
                  min="0"
                  value={summaryEmailForm.maxWorkingDays}
                  onChange={(e) => onSummaryFieldChange('maxWorkingDays', e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm text-sm px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={summaryEmailForm.includeIndirect}
                onChange={(e) => onSummaryFieldChange('includeIndirect', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Include indirect reports
            </label>

            <button
              type="button"
              onClick={() => onSummaryFieldChange('maxAttendancePercent', 80)}
              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Set Max 80%
            </button>

            <button
              type="button"
              onClick={onSend}
              disabled={sendingSummaryEmail}
              className="ml-auto inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {sendingSummaryEmail ? 'Sending...' : 'Send Summary Email'}
            </button>
          </div>

          {summaryEmailResult && (
            <div className="mt-3 text-xs text-gray-500">
              Last sent: {summaryEmailResult.summary?.filteredMembers ?? 0} of {summaryEmailResult.summary?.totalTeamMembers ?? 0} members matched.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ManagerDashboard() {
  const navigate = useNavigate();
  const { user: authUser, logout, updateUser } = useAuth();
  const [user, setUser] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [todayTeamMembers, setTodayTeamMembers] = useState([]);
  const [excludedMembers, setExcludedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('team');
  const [processingRequest, setProcessingRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Checkout request stats and history
  const [checkoutStats, setCheckoutStats] = useState(null);
  const [checkoutHistory, setCheckoutHistory] = useState([]);
  const [checkoutHistoryLoading, setCheckoutHistoryLoading] = useState(false);
  const [checkoutStatsPeriod, setCheckoutStatsPeriod] = useState(30); // days
  const [checkoutHistoryPage, setCheckoutHistoryPage] = useState(1);
  const [checkoutHistoryFilters, setCheckoutHistoryFilters] = useState({
    status: 'approved',
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  const [startDate, setStartDate] = useState(moment().startOf('month').toDate());
  const [endDate, setEndDate] = useState(moment().endOf('month').toDate());
  const [expandedMembers, setExpandedMembers] = useState({});
  const [attendanceViewMode, setAttendanceViewMode] = useState('list'); // 'list' or 'calendar'
  const [attendanceSearchQuery, setAttendanceSearchQuery] = useState('');
  const [teamAttendanceLoading, setTeamAttendanceLoading] = useState(false);
  const [cancellingApprovedLeaveId, setCancellingApprovedLeaveId] = useState(null);
  const filteredTeamMembers = useMemo(
    () => filterTeamMembersBySearch(teamMembers, attendanceSearchQuery),
    [teamMembers, attendanceSearchQuery]
  );
  const attendanceResultsRef = useRef(null);
  const handleAttendanceViewModeChange = useMemo(
    () => createAttendanceViewModeChangeHandler({
      setAttendanceViewMode,
      attendanceResultsRef,
    }),
    [setAttendanceViewMode]
  );

  // Custom summary email states
  const [summaryEmailForm, setSummaryEmailForm] = useState({
    toEmail: '',
    ccEmails: '',
    subject: '',
    minAttendancePercent: '',
    maxAttendancePercent: 80,
    minExpectedHours: '',
    maxExpectedHours: '',
    minTotalHours: '',
    maxTotalHours: '',
    minDaysPresent: '',
    maxDaysPresent: '',
    minWorkingDays: '',
    maxWorkingDays: '',
    department: '',
    departments: '',
    search: '',
    includeIndirect: true
  });
  const [sendingSummaryEmail, setSendingSummaryEmail] = useState(false);
  const [summaryEmailResult, setSummaryEmailResult] = useState(null);
  const [showSummaryAdvanced, setShowSummaryAdvanced] = useState(false);

  // Custom summary email preview states
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [loadingEmailPreview, setLoadingEmailPreview] = useState(false);
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [emailSortBy, setEmailSortBy] = useState('name');
  const [emailSortOrder, setEmailSortOrder] = useState('asc');
  
  // Attendance correction modal states
  const [showAttendanceCorrectionModal, setShowAttendanceCorrectionModal] = useState(false);
  const [selectedMemberForCorrection, setSelectedMemberForCorrection] = useState(null);
  const [selectedDateForCorrection, setSelectedDateForCorrection] = useState(null);
  const [correctionCheckInTime, setCorrectionCheckInTime] = useState('');
  const [correctionCheckOutTime, setCorrectionCheckOutTime] = useState('');
  const [correctionPurpose, setCorrectionPurpose] = useState('');
  const [savingCorrection, setSavingCorrection] = useState(false);
  
  // Leave request states
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);
  const [leaveReviewNotes, setLeaveReviewNotes] = useState('');
  const [processingLeave, setProcessingLeave] = useState(null);
  const [leaveStats, setLeaveStats] = useState(null);
  const [leaveStatsLoading, setLeaveStatsLoading] = useState(false);
  const [leaveStatsPeriod, setLeaveStatsPeriod] = useState(30); // days
  const [showLeaveStatsModal, setShowLeaveStatsModal] = useState(false);
  const [selectedLeaveStatsUser, setSelectedLeaveStatsUser] = useState(null);
  const [selectedLeaveStatsRequests, setSelectedLeaveStatsRequests] = useState([]);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [showApprovedLeavesPanel, setShowApprovedLeavesPanel] = useState(false);
  const [showAllApprovedLeaves, setShowAllApprovedLeaves] = useState(false);
  
  // Extra hours request states
  const [pendingExtraHours, setPendingExtraHours] = useState([]);
  const [selectedExtraHoursRequest, setSelectedExtraHoursRequest] = useState(null);
  const [extraHoursReviewNotes, setExtraHoursReviewNotes] = useState('');
  const [extraHoursApprovedHours, setExtraHoursApprovedHours] = useState(null);
  const [processingExtraHours, setProcessingExtraHours] = useState(null);

  // Attendance correction request states
  const [pendingCorrections, setPendingCorrections] = useState([]);
  const [selectedCorrectionRequest, setSelectedCorrectionRequest] = useState(null);
  const [correctionReviewNotes, setCorrectionReviewNotes] = useState('');
  const [correctionStats, setCorrectionStats] = useState(null);
  const [correctionStatsLoading, setCorrectionStatsLoading] = useState(false);
  const [correctionStatsPeriod, setCorrectionStatsPeriod] = useState(30); // days
  const [processingCorrection, setProcessingCorrection] = useState(null);
  const [showCorrectionStatsModal, setShowCorrectionStatsModal] = useState(false);
  const [selectedCorrectionStatsUser, setSelectedCorrectionStatsUser] = useState(null);
  const [selectedCorrectionStatsRequests, setSelectedCorrectionStatsRequests] = useState([]);
  
  // Extra hours stats and history
  const [extraHoursStats, setExtraHoursStats] = useState(null);
  const [extraHoursHistory, setExtraHoursHistory] = useState([]);
  const [extraHoursHistoryLoading, setExtraHoursHistoryLoading] = useState(false);
  const [extraHoursStatsPeriod, setExtraHoursStatsPeriod] = useState(30); // days
  const [extraHoursHistoryPage, setExtraHoursHistoryPage] = useState(1);
  const [extraHoursHistoryFilters, setExtraHoursHistoryFilters] = useState({
    status: 'approved',
    startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  });
  
  // CC selection states
  const [showCCModal, setShowCCModal] = useState(false);
  const [loadingCCRecipients, setLoadingCCRecipients] = useState(false);
  const [ccRecipients, setCCRecipients] = useState([]);
  const [selectedCCEmails, setSelectedCCEmails] = useState([]);
  const [ccSearchTerm, setCCSearchTerm] = useState('');
  const [pendingApproval, setPendingApproval] = useState(null); // Store request to approve after CC selection

  const normalizeEmail = (email) => (email || '').trim().toLowerCase();

  const isLeaveCancelableWithinWeek = (leave) => {
    if (!leave) return false;
    const refDate = leave.reviewedAt || leave.updatedAt || leave.createdAt;
    if (!refDate) return false;
    return moment().diff(moment(refDate), 'days') <= 7;
  };

  const handleSummaryFieldChange = (field, value) => {
    setSummaryEmailForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSendSummaryEmail = async () => {
    try {
      if (!startDate || !endDate) {
        toast.error('Please select a date range');
        return;
      }

      const recipientEmail = summaryEmailForm.toEmail || user?.officialEmail || user?.email;
      if (!recipientEmail) {
        toast.error('Recipient email is required');
        return;
      }

      setSendingSummaryEmail(true);

      const payload = {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        toEmail: recipientEmail,
        ccEmails: summaryEmailForm.ccEmails,
        subject: summaryEmailForm.subject,
        includeIndirect: summaryEmailForm.includeIndirect,
        minAttendancePercent: summaryEmailForm.minAttendancePercent,
        maxAttendancePercent: summaryEmailForm.maxAttendancePercent,
        minExpectedHours: summaryEmailForm.minExpectedHours,
        maxExpectedHours: summaryEmailForm.maxExpectedHours,
        minTotalHours: summaryEmailForm.minTotalHours,
        maxTotalHours: summaryEmailForm.maxTotalHours,
        minDaysPresent: summaryEmailForm.minDaysPresent,
        maxDaysPresent: summaryEmailForm.maxDaysPresent,
        minWorkingDays: summaryEmailForm.minWorkingDays,
        maxWorkingDays: summaryEmailForm.maxWorkingDays,
        department: summaryEmailForm.department,
        departments: summaryEmailForm.departments,
        search: summaryEmailForm.search,
        sortBy: emailSortBy,
        sortOrder: emailSortOrder
      };

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/attendance/team-summary/email', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSummaryEmailResult(response.data);
      toast.success('Summary email sent');
      setShowEmailPreviewModal(false);
    } catch (error) {
      console.error('Send summary email error:', error);
      toast.error(error.response?.data?.error || 'Failed to send summary email');
    } finally {
      setSendingSummaryEmail(false);
    }
  };

  const fetchEmailPreview = async (sortByVal = emailSortBy, sortOrderVal = emailSortOrder) => {
    try {
      setLoadingEmailPreview(true);
      setEmailPreviewHtml('');

      const payload = {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        toEmail: summaryEmailForm.toEmail || user?.officialEmail || user?.email,
        ccEmails: summaryEmailForm.ccEmails,
        subject: summaryEmailForm.subject,
        includeIndirect: summaryEmailForm.includeIndirect,
        minAttendancePercent: summaryEmailForm.minAttendancePercent,
        maxAttendancePercent: summaryEmailForm.maxAttendancePercent,
        minExpectedHours: summaryEmailForm.minExpectedHours,
        maxExpectedHours: summaryEmailForm.maxExpectedHours,
        minTotalHours: summaryEmailForm.minTotalHours,
        maxTotalHours: summaryEmailForm.maxTotalHours,
        minDaysPresent: summaryEmailForm.minDaysPresent,
        maxDaysPresent: summaryEmailForm.maxDaysPresent,
        minWorkingDays: summaryEmailForm.minWorkingDays,
        maxWorkingDays: summaryEmailForm.maxWorkingDays,
        department: summaryEmailForm.department,
        departments: summaryEmailForm.departments,
        search: summaryEmailForm.search,
        sortBy: sortByVal,
        sortOrder: sortOrderVal
      };

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/attendance/team-summary/preview', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEmailPreviewHtml(response.data.html);
    } catch (error) {
      console.error('Email preview error:', error);
      toast.error(error.response?.data?.error || 'Failed to generate email preview');
    } finally {
      setLoadingEmailPreview(false);
    }
  };

  const handleOpenEmailPreview = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select a date range');
      return;
    }

    const recipientEmail = summaryEmailForm.toEmail || user?.officialEmail || user?.email;
    if (!recipientEmail) {
      toast.error('Recipient email is required');
      return;
    }

    setEmailSortBy('name');
    setEmailSortOrder('asc');
    setShowEmailPreviewModal(true);
    fetchEmailPreview('name', 'asc');
  };

  // Fetch feedback unread count
  const fetchFeedbackUnreadCount = async () => {
    try {
      const response = await feedbackAPI.getUnreadCount();
      setFeedbackUnreadCount(response.data.unreadCount || 0);
    } catch (error) {
      // Silently fail if user doesn't have access (403) or other errors
      // Don't logout on 403 - it's just a permission issue
      if (error.response?.status === 403) {
        console.log('Feedback access not available for this user');
        setFeedbackUnreadCount(0);
      } else if (error.response?.status === 401) {
        // Only logout on 401 (unauthorized)
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        console.error('Error fetching feedback unread count:', error);
        setFeedbackUnreadCount(0);
      }
    }
  };

  // Debug effect to track selectedCCEmails changes
  useEffect(() => {
    console.log('🔄 selectedCCEmails changed:', selectedCCEmails, 'length:', selectedCCEmails.length);
  }, [selectedCCEmails]);

  // Prefill summary email recipient with manager's email
  useEffect(() => {
    if (user && !summaryEmailForm.toEmail) {
      const email = user?.officialEmail || user?.email || '';
      if (email) {
        setSummaryEmailForm(prev => ({ ...prev, toEmail: email }));
      }
    }
  }, [user, summaryEmailForm.toEmail]);
  
  // Confirmation dialog states
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning'
  });
  
  // ClickUp integration states
  const [clickupTaskCounts, setClickupTaskCounts] = useState({});
  const [clickupLoading, setClickupLoading] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  
  // Feedback unread count
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const [selectedUserTasks, setSelectedUserTasks] = useState(null);
  const [loadingTaskDetails, setLoadingTaskDetails] = useState(false);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Performance tracking states
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceDays, setPerformanceDays] = useState(7);
  
  // Workload management states
  const [workloadData, setWorkloadData] = useState(null);
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [taskEditForm, setTaskEditForm] = useState({
    taskId: '',
    taskName: '',
    due_date: '',
    start_date: '',
    teamId: ''
  });
  const [savingTask, setSavingTask] = useState(false);
  const [workloadView, setWorkloadView] = useState('timeline'); // 'timeline' or 'cards'
  const [timelineStartDate, setTimelineStartDate] = useState(moment().startOf('week').toDate());
  const [timelineDays, setTimelineDays] = useState(14);
  const [draggingTask, setDraggingTask] = useState(null);
  const [showActiveTodayModal, setShowActiveTodayModal] = useState(false);
  const [showAbsenteesModal, setShowAbsenteesModal] = useState(false);
  const [showProfilePhotoUpload, setShowProfilePhotoUpload] = useState(false);
  
  // Team Hierarchy states
  const [hierarchyData, setHierarchyData] = useState(null);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const pullToRefresh = useNativePullToRefresh({
    disabled: loading || teamAttendanceLoading || !user,
    onRefresh: async () => {
      await loadDashboardData({ showLoader: false });
    },
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      setLoading(false);
      navigate('/login', { replace: true });
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      loadDashboardData();
      
      // Initialize push notifications for mobile devices
      initPush(token);
    } catch (err) {
      console.error('Error parsing user data:', err);
      setLoading(false);
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!authUser) return;
    setUser((currentUser) => ({
      ...(currentUser || {}),
      ...authUser,
      profilePhotoUrl: authUser.profilePhotoUrl || currentUser?.profilePhotoUrl || '',
    }));
  }, [authUser]);

  const handleProfileSaved = (updatedUser) => {
    if (!updatedUser) return;
    setUser((currentUser) => ({
      ...(currentUser || authUser || {}),
      ...updatedUser,
    }));
    updateUser?.(updatedUser);
    setShowProfilePhotoUpload(false);
  };

  // Load team attendance separately when date filter changes (without reloading entire page)
  useEffect(() => {
    // Only load if user is set (component initialized) - skip initial mount
    if (user) {
      loadTeamAttendance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const loadTodayTeamMembers = async (token) => {
    const todayKey = moment().format('YYYY-MM-DD');
    const todayResponse = await axios.get('/api/checkout-requests/team-attendance', {
      params: {
        startDate: todayKey,
        endDate: todayKey
      },
      headers: { Authorization: `Bearer ${token}` }
    });

    setTodayTeamMembers(todayResponse.data.teamMembers || []);
  };

  const loadDashboardData = async ({ showLoader = true } = {}) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      const token = localStorage.getItem('token');
      
      // Load pending checkout requests
      const requestsResponse = await axios.get('/api/checkout-requests/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingRequests(requestsResponse.data.requests || []);

      // Load pending leave requests
      const leaveResponse = await axios.get('/api/leave-requests/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingLeaves(leaveResponse.data.requests || []);
      
      // Load pending extra hours requests
      const extraHoursResponse = await axios.get('/api/extra-hours-requests/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingExtraHours(extraHoursResponse.data.requests || []);

      // Load pending attendance correction requests
      const correctionsResponse = await axios.get('/api/attendance-corrections/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCorrections(correctionsResponse.data.requests || []);
      
      // Load leave statistics
      loadLeaveStats();
      // Load correction statistics
      loadCorrectionStats();

      // Load team attendance with date range (inline for initial load)
      const teamResponse = await axios.get('/api/checkout-requests/team-attendance', {
        params: {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD')
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      const teamMembersData = teamResponse.data.teamMembers || [];
      const excludedMembersData = teamResponse.data.excludedMembers || [];
      setTeamMembers(teamMembersData);
      setExcludedMembers(excludedMembersData);
      await loadTodayTeamMembers(token);
      
      // Load ClickUp task counts for team members
      if (teamMembersData.length > 0) {
        loadClickUpTaskCounts(teamMembersData);
      }
      
      // Load CC recipients
      loadCCRecipients();
      
      // Load feedback unread count (if user has admin access)
      fetchFeedbackUnreadCount();

    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else if (err.response?.status === 403) {
        // 403 errors are permission issues, not authentication - don't logout
        console.warn('Access denied:', err.response?.data?.error);
        setError(err.response?.data?.error || 'Access denied');
      } else {
        setError(err.response?.data?.error || 'Failed to load dashboard data');
      }
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  // Load only team attendance data (used when date filter changes)
  const loadTeamAttendance = async () => {
    try {
      setTeamAttendanceLoading(true);
      const token = localStorage.getItem('token');
      
      const teamResponse = await axios.get('/api/checkout-requests/team-attendance', {
        params: {
          startDate: moment(startDate).format('YYYY-MM-DD'),
          endDate: moment(endDate).format('YYYY-MM-DD')
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      const teamMembersData = teamResponse.data.teamMembers || [];
      const excludedMembersData = teamResponse.data.excludedMembers || [];
      setTeamMembers(teamMembersData);
      setExcludedMembers(excludedMembersData);
      await loadTodayTeamMembers(token);
      
      // Load ClickUp task counts for team members
      if (teamMembersData.length > 0) {
        loadClickUpTaskCounts(teamMembersData);
      }
    } catch (err) {
      console.error('Error loading team attendance:', err);
      toast.error('Failed to load team attendance data');
    } finally {
      setTeamAttendanceLoading(false);
    }
  };

  const loadClickUpTaskCounts = async (members) => {
    try {
      setClickupLoading(true);
      const token = localStorage.getItem('token');
      
      // Extract emails from team members
      const emails = members
        .map(m => m.officialEmail)
        .filter(email => email);
      
      console.log('🔍 Frontend - Loading task counts for emails:', emails);
      
      if (emails.length === 0) {
        console.log('⚠️ No emails found to fetch task counts');
        return;
      }
      
      const clickupTaskCounts = {};
      const orbitTaskCounts = {};
      
      // Try ClickUp API (backend will filter by department configuration)
      try {
        console.log('📡 Frontend - Calling /api/clickup/task-counts...');
        const clickupResponse = await axios.get('/api/clickup/task-counts', {
          params: { emails: emails.join(',') },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('📥 Frontend - ClickUp API response:', clickupResponse.data);
        
        if (clickupResponse.data.success && clickupResponse.data.taskCounts) {
          // Only keep valid results (found: true) from ClickUp
          Object.keys(clickupResponse.data.taskCounts).forEach(email => {
            const taskData = clickupResponse.data.taskCounts[email];
            if (taskData && taskData.found === true) {
              clickupTaskCounts[email] = taskData;
            }
          });
        }
      } catch (clickupErr) {
        // Silently fail - ClickUp integration is optional
        console.log('⚠️ Frontend - ClickUp integration error (optional):', clickupErr.message);
      }
      
      // Try Orbit API (backend will filter by department configuration)
      try {
        console.log('📡 Frontend - Calling /api/orbit/task-counts...');
        const orbitResponse = await axios.get('/api/orbit/task-counts', {
          params: { emails: emails.join(',') },
          headers: { Authorization: `Bearer ${token}` }
        });
        
        console.log('📥 Frontend - Orbit API response:', orbitResponse.data);
        
        if (orbitResponse.data.success && orbitResponse.data.taskCounts) {
          // Only keep valid results (found: true) from Orbit
          Object.keys(orbitResponse.data.taskCounts).forEach(email => {
            const taskData = orbitResponse.data.taskCounts[email];
            if (taskData && taskData.found === true) {
              orbitTaskCounts[email] = taskData;
            }
          });
        }
      } catch (orbitErr) {
        // Silently fail - Orbit integration is optional
        console.log('⚠️ Frontend - Orbit integration error (optional):', orbitErr.message);
      }
      
      // Merge results: ClickUp takes priority, then Orbit (no overwriting)
      // Only add Orbit data for emails that don't have ClickUp data
      const mergedTaskCounts = { ...clickupTaskCounts };
      Object.keys(orbitTaskCounts).forEach(email => {
        if (!mergedTaskCounts[email]) {
          mergedTaskCounts[email] = orbitTaskCounts[email];
        }
      });
      
      if (Object.keys(mergedTaskCounts).length > 0) {
        console.log('✅ Frontend - Setting merged task counts:', mergedTaskCounts);
        setClickupTaskCounts(mergedTaskCounts);
      } else {
        console.log('⚠️ Frontend - No task counts found from either API');
      }
    } catch (err) {
      // Silently fail - Task integration is optional
      console.log('❌ Frontend - Task integration error:', err.message);
      console.error('Full error:', err);
    } finally {
      setClickupLoading(false);
    }
  };

  const loadUserTaskDetails = async (email, userName, page = 0, append = false) => {
    try {
      setLoadingTaskDetails(true);
      setShowTaskModal(true);
      setSelectedUserTasks(prev => ({
        email,
        userName,
        tasks: append ? (prev?.tasks || []) : [],
        loading: true,
        page: append ? prev?.page || 0 : 0,
        hasMore: append ? prev?.hasMore : false
      }));

      const token = localStorage.getItem('token');
      
      console.log('📋 Fetching detailed tasks for:', email);

      const response = await axios.get(`/api/clickup/user/${email}/tasks`, {
        params: { page },
        headers: { Authorization: `Bearer ${token}` }
      });
      const apiType = 'clickup';

      if (response && response.data.success) {
        setSelectedUserTasks(prev => ({
          email,
          userName,
          clickupUser: response.data.clickupUser || null,
          orbitId: response.data.orbitId || null,
          apiType,
          tasks: append ? [...(prev?.tasks || []), ...(response.data.tasks || [])] : (response.data.tasks || []),
          taskCount: append
            ? (prev?.taskCount || 0) + (response.data.taskCount || 0)
            : (response.data.taskCount || 0),
          page: typeof response.data.page === 'number' ? response.data.page : page,
          hasMore: Boolean(response.data.hasMore),
          nextPage: typeof response.data.nextPage === 'number' ? response.data.nextPage : null,
          loading: false
        }));
      }
    } catch (err) {
      console.error('Error loading task details:', err);
      toast.error('Failed to load task details: ' + (err.message || 'Unknown error'));
      setSelectedUserTasks({
        email,
        userName,
        tasks: [],
        error: err.response?.data?.error || err.message,
        loading: false
      });
    } finally {
      setLoadingTaskDetails(false);
    }
  };

  const handleLoadMoreTasks = () => {
    if (!selectedUserTasks || !selectedUserTasks.hasMore) return;
    const nextPage = typeof selectedUserTasks.page === 'number' ? selectedUserTasks.page + 1 : 1;
    loadUserTaskDetails(selectedUserTasks.email, selectedUserTasks.userName, nextPage, true);
  };

  const downloadClickupHistoryCsv = () => {
    if (!selectedUserTasks?.tasks?.length) {
      toast.error('No ClickUp tasks to download');
      return;
    }

    const rows = buildClickupHistoryRows(selectedUserTasks.tasks);
    const headers = ['S. No', 'Parent Task', 'Subtask Name', 'Subtask URL', 'Status'];
    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (/[",\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvLines = [
      headers.join(','),
      ...rows.map((row) => [
        row.serial,
        row.parentName,
        row.subtaskName,
        row.url,
        row.status
      ].map(escapeCsv).join(','))
    ];

    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const safeName = (selectedUserTasks.userName || 'user').replace(/[^a-z0-9-_]+/gi, '_');
    const dateStamp = new Date().toISOString().slice(0, 10);
    link.href = URL.createObjectURL(blob);
    link.download = `clickup-history-${safeName}-${dateStamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const buildClickupHistoryRows = (tasks = []) => {
    if (!tasks.length) return [];

    const taskById = {};
    tasks.forEach(task => {
      if (task && task.id) {
        taskById[task.id] = task;
      }
    });

    const parentMap = {};

    tasks.forEach(task => {
      if (!task) return;
      if (task.parent) {
        const parentId = task.parent;
        const parentTask = taskById[parentId] || {
          id: parentId,
          name: task.parentName || '(Parent not loaded)',
          url: ''
        };
        if (!parentMap[parentId]) {
          parentMap[parentId] = { parent: parentTask, subtasks: [] };
        }
        parentMap[parentId].subtasks.push(task);
      } else {
        const parentId = task.id;
        if (!parentMap[parentId]) {
          parentMap[parentId] = { parent: task, subtasks: [] };
        }
      }
    });

    tasks.forEach(task => {
      if (task?.parent && parentMap[task.parent]) {
        if (!parentMap[task.parent].subtasks.some(st => st.id === task.id)) {
          parentMap[task.parent].subtasks.push(task);
        }
      }
    });

    const entries = Object.values(parentMap).sort((a, b) => (a.parent?.name || '').localeCompare(b.parent?.name || ''));
    const rows = [];

    let serial = 1;
    entries.forEach(entry => {
      const parent = entry.parent || {};
      const subtasks = entry.subtasks || [];
      if (!subtasks.length) {
          rows.push({
            serial,
            parentName: parent.name || '',
            subtaskName: '(No subtasks)',
            url: parent.url || '',
            status: parent.status?.status || parent.status || ''
          });
        serial += 1;
      } else {
        subtasks.forEach((subtask, index) => {
          rows.push({
            serial,
            parentName: index === 0 ? (parent.name || '') : '',
            subtaskName: subtask.name || '',
            url: subtask.url || '',
            status: subtask.status?.status || subtask.status || ''
          });
        });
        serial += 1;
      }
    });

    return rows;
  };

  const loadPerformanceData = async () => {
    try {
      setPerformanceLoading(true);
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      // Get user's department
      const userDept = userData?.department || "Masters' Union";
      
      console.log('📊 Loading performance data for department:', userDept);
      
      const response = await axios.get(`/api/clickup/performance/department/${encodeURIComponent(userDept)}`, {
        params: { days: performanceDays },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('✅ Performance data loaded:', response.data);

      if (response.data.success) {
        setPerformanceData(response.data);
      }
    } catch (err) {
      console.error('❌ Error loading performance data:', err);
      toast.error('Failed to load performance data');
    } finally {
      setPerformanceLoading(false);
    }
  };

  // Load checkout stats
  const loadCheckoutStats = async () => {
    try {
      setCheckoutHistoryLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = buildRollingRequestDateRange(checkoutStatsPeriod);
      
      const response = await axios.get('/api/checkout-requests/team-requests', {
        params: {
          status: 'approved',
          startDate,
          endDate,
          limit: 1000 // Get all for stats
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        const requests = response.data.requests;
        
        // Calculate statistics
        const totalRequests = requests.length;
        const uniqueUsers = new Set(requests.map(r => r.userId));
        
        // Group by user
        const byUser = {};
        requests.forEach(r => {
          const userId = r.userId;
          if (!byUser[userId]) {
            byUser[userId] = {
              user: r.user,
              count: 0
            };
          }
          byUser[userId].count += 1;
        });
        
        // Top users by request count
        const topUsers = Object.values(byUser)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5);
        
        setCheckoutStats({
          totalRequests,
          uniqueUsers: uniqueUsers.size,
          avgRequestsPerUser: uniqueUsers.size > 0 ? totalRequests / uniqueUsers.size : 0,
          topUsers
        });
      }
    } catch (err) {
      console.error('Error loading checkout stats:', err);
      toast.error('Failed to load checkout statistics');
    } finally {
      setCheckoutHistoryLoading(false);
    }
  };

  // Load checkout history
  const loadCheckoutHistory = async () => {
    try {
      setCheckoutHistoryLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get('/api/checkout-requests/team-requests', {
        params: {
          ...checkoutHistoryFilters,
          page: checkoutHistoryPage,
          limit: 20
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        setCheckoutHistory(response.data.requests);
      }
    } catch (err) {
      console.error('Error loading checkout history:', err);
      toast.error('Failed to load checkout history');
    } finally {
      setCheckoutHistoryLoading(false);
    }
  };

  // Load extra hours stats
  const loadExtraHoursStats = async () => {
    try {
      setExtraHoursHistoryLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = buildRollingRequestDateRange(extraHoursStatsPeriod);
      
      const response = await axios.get('/api/extra-hours-requests/team-requests', {
        params: {
          status: 'approved',
          startDate,
          endDate,
          limit: 1000 // Get all for stats
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        const requests = response.data.requests;
        
        // Calculate statistics
        const totalApprovedHours = requests.reduce((sum, r) => sum + (r.approvedHours || r.requestedHours || 0), 0);
        const totalRequestedHours = requests.reduce((sum, r) => sum + (r.requestedHours || 0), 0);
        const uniqueUsers = new Set(requests.map(r => r.userId));
        const avgHoursPerRequest = requests.length > 0 ? totalApprovedHours / requests.length : 0;
        const avgHoursPerUser = uniqueUsers.size > 0 ? totalApprovedHours / uniqueUsers.size : 0;
        
        // Group by user
        const byUser = {};
        requests.forEach(r => {
          const userId = r.userId;
          if (!byUser[userId]) {
            byUser[userId] = {
              user: r.user,
              totalApproved: 0,
              totalRequested: 0,
              count: 0
            };
          }
          byUser[userId].totalApproved += r.approvedHours || r.requestedHours || 0;
          byUser[userId].totalRequested += r.requestedHours || 0;
          byUser[userId].count += 1;
        });
        
        // Top users by approved hours
        const topUsers = Object.values(byUser)
          .sort((a, b) => b.totalApproved - a.totalApproved)
          .slice(0, 5);
        
        setExtraHoursStats({
          totalRequests: requests.length,
          totalApprovedHours,
          totalRequestedHours,
          uniqueUsers: uniqueUsers.size,
          avgHoursPerRequest,
          avgHoursPerUser,
          topUsers
        });
      }
    } catch (err) {
      console.error('Error loading extra hours stats:', err);
      toast.error('Failed to load extra hours statistics');
    } finally {
      setExtraHoursHistoryLoading(false);
    }
  };

  // Load extra hours history
  const loadExtraHoursHistory = async () => {
    try {
      setExtraHoursHistoryLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get('/api/extra-hours-requests/team-requests', {
        params: {
          ...extraHoursHistoryFilters,
          page: extraHoursHistoryPage,
          limit: 20
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        setExtraHoursHistory(response.data.requests);
      }
    } catch (err) {
      console.error('Error loading extra hours history:', err);
      toast.error('Failed to load extra hours history');
    } finally {
      setExtraHoursHistoryLoading(false);
    }
  };

  // Load performance data when tab is selected
  useEffect(() => {
    if (activeTab === 'performance') {
      loadPerformanceData();
    } else if (activeTab === 'workload') {
      loadWorkloadData();
    } else if (activeTab === 'hierarchy' && user) {
      loadHierarchyData(user.id);
    } else if (activeTab === 'extra-hours') {
      loadExtraHoursStats();
      loadExtraHoursHistory();
    } else if (activeTab === 'checkout') {
      loadCheckoutStats();
      loadCheckoutHistory();
    } else if (activeTab === 'leave') {
      loadLeaveStats();
    } else if (activeTab === 'corrections') {
      loadCorrectionStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    performanceDays,
    extraHoursStatsPeriod,
    extraHoursHistoryPage,
    extraHoursHistoryFilters,
    checkoutStatsPeriod,
    checkoutHistoryPage,
    checkoutHistoryFilters,
    leaveStatsPeriod,
    correctionStatsPeriod,
  ]);
  
  // Reload hierarchy when ClickUp data changes
  useEffect(() => {
    if (activeTab === 'hierarchy' && user && hierarchyData && Object.keys(clickupTaskCounts).length > 0) {
      loadHierarchyData(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickupTaskCounts]);
  
  // New: lightweight members list for Cards accordion
  const [workloadMembers, setWorkloadMembers] = useState(null);
  const [openMemberIds, setOpenMemberIds] = useState(new Set());
  const [memberTasks, setMemberTasks] = useState({}); // userId -> { tasks, taskCount, teamId, loading }
  const [openUnscheduledIds, setOpenUnscheduledIds] = useState(new Set());

  const toggleMemberAccordion = async (memberId) => {
    setOpenMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
      return next;
    });

    // Lazy load tasks when opening - try ClickUp first, then Orbit
    if (!memberTasks[memberId]) {
      setMemberTasks(prev => ({ ...prev, [memberId]: { loading: true } }));
      try {
        // Try ClickUp API first (prioritize ClickUp when configured)
        let res = null;
        try {
          res = await workloadAPI.getMemberTasks(memberId);
          if (res.data && res.data.success) {
            console.log('✅ ClickUp member tasks loaded');
          }
        } catch (clickupErr) {
          // If ClickUp fails, try Orbit
          console.log('⚠️ ClickUp member tasks failed, trying Orbit...', clickupErr.message);
          res = await workloadAPI.getMemberTasksOrbit(memberId);
          if (res.data && res.data.success) {
            console.log('✅ Orbit member tasks loaded');
          }
        }
        
        if (res && res.data && res.data.success) {
          setMemberTasks(prev => ({
            ...prev,
            [memberId]: {
              loading: false,
              tasks: res.data.tasks,
              taskCount: res.data.taskCount,
              teamId: res.data.teamId || res.data.orbitId || null,
              orbitId: res.data.orbitId || null,
              name: res.data.name,
              email: res.data.email
            }
          }));
        } else {
          setMemberTasks(prev => ({ ...prev, [memberId]: { loading: false, tasks: [], taskCount: 0 } }));
        }
      } catch (e) {
        setMemberTasks(prev => ({ ...prev, [memberId]: { loading: false, tasks: [], taskCount: 0, error: 'Failed to load tasks' } }));
      }
    }
  };

  const toggleUnscheduledAccordion = (memberId) => {
    setOpenUnscheduledIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId); else next.add(memberId);
      return next;
    });
  };

  // Update only one member after editing
  const refreshSingleMember = async (memberId) => {
    try {
      setMemberTasks(prev => ({ ...prev, [memberId]: { ...(prev[memberId]||{}), loading: true } }));
      
      // Try ClickUp API first (prioritize ClickUp when configured)
      let res = null;
      try {
        res = await workloadAPI.getMemberTasks(memberId);
        if (res.data && res.data.success) {
          console.log('✅ ClickUp member tasks loaded');
        }
      } catch (clickupErr) {
        // If ClickUp fails, try Orbit
        console.log('⚠️ ClickUp member tasks failed, trying Orbit...', clickupErr.message);
        try {
          res = await workloadAPI.getMemberTasksOrbit(memberId);
          if (res.data && res.data.success) {
            console.log('✅ Orbit member tasks loaded');
          }
        } catch (orbitErr) {
          throw orbitErr;
        }
      }
      
      if (res && res.data && res.data.success) {
        setMemberTasks(prev => ({
          ...prev,
          [memberId]: {
            loading: false,
            tasks: res.data.tasks,
            taskCount: res.data.taskCount,
            teamId: res.data.teamId || res.data.orbitId || null,
            orbitId: res.data.orbitId || null,
            name: res.data.name,
            email: res.data.email
          }
        }));
      } else {
        setMemberTasks(prev => ({ ...prev, [memberId]: { loading: false, tasks: [], taskCount: 0 } }));
      }
    } catch (_) {
      setMemberTasks(prev => ({ ...prev, [memberId]: { loading: false, tasks: [], taskCount: 0, error: 'Failed to load tasks' } }));
    }
  };

  const loadWorkloadData = async () => {
    try {
      setWorkloadLoading(true);
      const token = localStorage.getItem('token');
      
      console.log('📊 Loading team workload data...');
      
      const clickupWorkloadData = [];
      const orbitWorkloadData = [];
      let clickupMembersResponse = null;
      let orbitMembersResponse = null;
      
      // Try ClickUp API (backend will filter by department configuration)
      try {
        console.log('📡 Frontend - Calling /api/clickup/team-workload...');
        const clickupResponse = await axios.get('/api/clickup/team-workload', { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        
        console.log('📥 Frontend - ClickUp workload response:', clickupResponse.data);
        
        if (clickupResponse.data.success && clickupResponse.data.workloadData) {
          // Only keep valid results (no error) from ClickUp
          clickupResponse.data.workloadData.forEach(member => {
            if (member && !member.error) {
              clickupWorkloadData.push(member);
            }
          });
        }
        
        // Try ClickUp members endpoint
        try {
          clickupMembersResponse = await workloadAPI.getMembers();
          if (clickupMembersResponse.data && clickupMembersResponse.data.success) {
            console.log('✅ ClickUp members loaded');
          }
        } catch (err) {
          console.log('⚠️ ClickUp members failed:', err.message);
        }
      } catch (clickupErr) {
        // Silently fail - ClickUp integration is optional
        console.log('⚠️ Frontend - ClickUp workload error (optional):', clickupErr.message);
      }
      
      // Try Orbit API (backend will filter by department configuration)
      try {
        console.log('📡 Frontend - Calling /api/orbit/team-workload...');
        const orbitResponse = await axios.get('/api/orbit/team-workload', { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        
        console.log('📥 Frontend - Orbit workload response:', orbitResponse.data);
        
        if (orbitResponse.data.success && orbitResponse.data.workloadData) {
          // Only keep valid results (no error) from Orbit
          orbitResponse.data.workloadData.forEach(member => {
            if (member && !member.error) {
              orbitWorkloadData.push(member);
            }
          });
        }
        
        // Try Orbit members endpoint
        try {
          orbitMembersResponse = await workloadAPI.getMembersOrbit();
          if (orbitMembersResponse.data && orbitMembersResponse.data.success) {
            console.log('✅ Orbit members loaded');
          }
        } catch (err) {
          console.log('⚠️ Orbit members failed:', err.message);
        }
      } catch (orbitErr) {
        // Silently fail - Orbit integration is optional
        console.log('⚠️ Frontend - Orbit workload error (optional):', orbitErr.message);
      }
      
      // Merge results: ClickUp takes priority, then Orbit (no overwriting)
      // Create a map by email to avoid duplicates
      const mergedWorkloadMap = new Map();
      
      // Add ClickUp data first (takes priority)
      clickupWorkloadData.forEach(member => {
        if (member.email) {
          mergedWorkloadMap.set(member.email, member);
        }
      });
      
      // Add Orbit data only for emails that don't have ClickUp data
      orbitWorkloadData.forEach(member => {
        if (member.email && !mergedWorkloadMap.has(member.email)) {
          mergedWorkloadMap.set(member.email, member);
        }
      });
      
      const mergedWorkloadData = Array.from(mergedWorkloadMap.values());
      
      // Sort by taskCount descending
      mergedWorkloadData.sort((a, b) => (b.taskCount || 0) - (a.taskCount || 0));
      
      // Calculate summary
      const totalTasks = mergedWorkloadData.reduce((sum, member) => sum + (member.taskCount || 0), 0);
      const totalUsers = mergedWorkloadData.length;
      const averageTasksPerUser = totalUsers > 0 ? (totalTasks / totalUsers).toFixed(2) : 0;
      
      if (mergedWorkloadData.length > 0) {
        console.log('✅ Frontend - Setting merged workload data:', mergedWorkloadData.length, 'members');
        setWorkloadData({
          success: true,
          teamSize: totalUsers,
          workloadData: mergedWorkloadData,
          generatedAt: new Date().toISOString(),
          summary: {
            totalTasks,
            totalUsers,
            averageTasksPerUser: parseFloat(averageTasksPerUser)
          }
        });
        
        // Use ClickUp members if available, otherwise Orbit members
        if (clickupMembersResponse && clickupMembersResponse.data && clickupMembersResponse.data.success) {
          setWorkloadMembers(clickupMembersResponse.data);
        } else if (orbitMembersResponse && orbitMembersResponse.data && orbitMembersResponse.data.success) {
          setWorkloadMembers(orbitMembersResponse.data);
        }
      } else {
        console.log('⚠️ Frontend - No workload data found from either API');
      }
    } catch (err) {
      console.error('❌ Error loading workload data:', err);
      toast.error('Failed to load workload data');
    } finally {
      setWorkloadLoading(false);
    }
  };
  
  const loadHierarchyData = async (userId) => {
    try {
      setHierarchyLoading(true);
      const token = localStorage.getItem('token');
      
      console.log('Loading hierarchy for user ID:', userId);
      console.log('Current user object:', user);
      
      const response = await axios.get(`/api/managers/hierarchy/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Hierarchy API response:', response.data);
      
      // Enrich hierarchy data with ClickUp stats from existing clickupTaskCounts
      const enrichHierarchyWithClickUp = (data) => {
        if (!data) return data;
        
        // Helper to add ClickUp stats to a user object
        const addClickUpStats = (user) => {
          if (user && user.email && clickupTaskCounts[user.email]) {
            user.stats = user.stats || {};
            user.stats.clickup = {
              taskCount: clickupTaskCounts[user.email].count || 0,
              clickupEnabled: clickupTaskCounts[user.email].found || false
            };
          }
          return user;
        };
        
        // Add to current user
        if (data.user) {
          addClickUpStats(data.user);
        }
        
        // Add to upper chain
        if (data.upperChain && Array.isArray(data.upperChain)) {
          data.upperChain.forEach(manager => addClickUpStats(manager));
        }
        
        // Add to subordinates tree recursively
        const enrichSubordinates = (subordinates) => {
          if (!subordinates || !Array.isArray(subordinates)) return;
          subordinates.forEach(sub => {
            addClickUpStats(sub);
            if (sub.subordinates) {
              enrichSubordinates(sub.subordinates);
            }
          });
        };
        
        if (data.subordinatesTree) {
          enrichSubordinates(data.subordinatesTree);
        }
        
        // Enrich allSubordinates flat list
        if (data.allSubordinates && Array.isArray(data.allSubordinates)) {
          data.allSubordinates.forEach(sub => addClickUpStats(sub));
        }
        
        return data;
      };
      
      const enrichedData = enrichHierarchyWithClickUp(response.data);
      setHierarchyData(enrichedData);
      
      // Auto-expand first level nodes
      const newExpanded = new Set();
      newExpanded.add(userId);
      setExpandedNodes(newExpanded);
    } catch (err) {
      console.error('Error loading hierarchy data:', err);
      toast.error('Failed to load team hierarchy');
    } finally {
      setHierarchyLoading(false);
    }
  };
  
  const openEditTaskModal = (task, memberName, teamId, memberId) => {
    setEditingTask({ ...task, memberName, teamId, memberId });
    setTaskEditForm({
      taskId: task.id,
      taskName: task.name,
      due_date: task.due_date ? moment(parseInt(task.due_date)).format('YYYY-MM-DD') : '',
      start_date: task.start_date ? moment(parseInt(task.start_date)).format('YYYY-MM-DD') : '',
      teamId: teamId
    });
    setShowEditTaskModal(true);
  };
  
  const handleSaveTaskDates = async () => {
    try {
      setSavingTask(true);
      const token = localStorage.getItem('token');
      
      // Determine API type based on teamId or orbitId
      const apiType = taskEditForm.teamId ? 'clickup' : 
                      (editingTask?.orbitId || workloadData?.workloadData?.find(m => m.userId === editingTask?.memberId)?.orbitId) ? 'orbit' : 'clickup';
      
      const updateData = {};
      
      // Add teamId for ClickUp or orbitId for Orbit
      if (apiType === 'clickup') {
        updateData.teamId = taskEditForm.teamId;
      } else {
        const orbitId = editingTask?.orbitId || 
                       workloadData?.workloadData?.find(m => m.userId === editingTask?.memberId)?.orbitId;
        if (orbitId) {
          updateData.orbitId = orbitId;
        }
      }
      
      // Convert dates to appropriate format for API
      // ClickUp expects timestamps (milliseconds), Orbit will convert from timestamps to ISO
      if (taskEditForm.due_date) {
        updateData.due_date = new Date(taskEditForm.due_date).getTime();
      }
      if (taskEditForm.start_date) {
        updateData.start_date = new Date(taskEditForm.start_date).getTime();
      }
      
      console.log('📝 Updating task:', taskEditForm.taskId, 'API:', apiType, updateData);
      
      // Call appropriate API endpoint
      const endpoint = apiType === 'clickup' 
        ? `/api/clickup/tasks/${taskEditForm.taskId}`
        : `/api/orbit/tasks/${taskEditForm.taskId}`;
      
      await axios.put(
        endpoint,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Task dates updated successfully!');
      setShowEditTaskModal(false);
      setEditingTask(null);
      
      // Optimistic update to avoid full reloads
      if (editingTask && editingTask.memberId) {
        if (activeTab === 'workload' && workloadView === 'cards') {
          // Only refresh that member in cards view
          await refreshSingleMember(editingTask.memberId);
        } else if (activeTab === 'workload' && workloadView === 'timeline') {
          // Update local workloadData for timeline view
          setWorkloadData((prev) => {
            if (!prev) return prev;
            const newStartTs = taskEditForm.start_date ? new Date(taskEditForm.start_date).getTime().toString() : null;
            const newDueTs = taskEditForm.due_date ? new Date(taskEditForm.due_date).getTime().toString() : null;
            const updated = {
              ...prev,
              workloadData: prev.workloadData.map((m) => {
                if (m.userId !== editingTask.memberId) return m;
                return {
                  ...m,
                  tasks: m.tasks.map((t) =>
                    t.id === taskEditForm.taskId
                      ? {
                          ...t,
                          start_date: newStartTs !== null ? newStartTs : t.start_date,
                          due_date: newDueTs !== null ? newDueTs : t.due_date,
                        }
                      : t
                  ),
                };
              }),
            };
            return updated;
          });
        }
      }
      
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error(err.response?.data?.error || 'Failed to update task dates');
    } finally {
      setSavingTask(false);
    }
  };

  const handleTaskDragStart = (e, task, memberName, teamId, mode = 'move') => {
    setDraggingTask({ task, memberName, teamId, mode });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
    e.currentTarget.style.opacity = '0.5';
  };

  const handleTaskDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggingTask(null);
  };

  const handleDateCellDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDateCellDrop = async (e, dropDate) => {
    e.preventDefault();
    
    if (!draggingTask) return;

    const { task, memberName, teamId, mode } = draggingTask;
    
    // Calculate task duration
    const originalStart = moment(parseInt(task.start_date));
    const originalEnd = moment(parseInt(task.due_date));
    let duration = originalEnd.diff(originalStart, 'days');
    
    // Calculate new dates based on drop position
    let newStartDate;
    let newEndDate;
    if (mode === 'resize-left') {
      // Set start date to the drop date, keep original end date
      newStartDate = moment(dropDate);
      newEndDate = originalEnd.clone();
      duration = newEndDate.diff(newStartDate, 'days');
    } else if (mode === 'resize-right') {
      // Set end date to the drop date, keep original start date
      newStartDate = originalStart.clone();
      newEndDate = moment(dropDate);
      if (newEndDate.isBefore(newStartDate)) {
        // prevent invalid; clamp to start
        newEndDate = newStartDate.clone();
      }
      duration = newEndDate.diff(newStartDate, 'days');
    } else {
      // Move: keep duration, shift to drop date as new start
      newStartDate = moment(dropDate);
      newEndDate = moment(dropDate).add(duration, 'days');
    }
    
    console.log('📅 Dropping task:', task.name);
    console.log('Original dates:', originalStart.format('YYYY-MM-DD'), 'to', originalEnd.format('YYYY-MM-DD'));
    console.log('New dates:', newStartDate.format('YYYY-MM-DD'), 'to', newEndDate.format('YYYY-MM-DD'));
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `/api/clickup/tasks/${task.id}`,
        {
          teamId: teamId,
          start_date: newStartDate.toISOString(),
          due_date: newEndDate.toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const actionVerb = mode === 'move' ? 'moved' : (mode === 'resize-left' || mode === 'resize-right') ? 'resized' : 'updated';
      toast.success(`Task "${task.name}" ${actionVerb}: ${newStartDate.format('MMM DD')} → ${newEndDate.format('MMM DD')}`);
      
      // For DnD move/resize we still reload, but you can switch to the same optimistic update if needed
      
    } catch (err) {
      console.error('Error updating task dates:', err);
      toast.error('Failed to update task dates');
    }
    
    setDraggingTask(null);
  };

  const loadCCRecipients = async (requestId = null, requestType = 'checkout', shouldShowModal = false) => {
    try {
      setLoadingCCRecipients(true);
      const token = localStorage.getItem('token');
      const params = requestId ? { requestId, requestType } : {};
      const response = await axios.get('/api/checkout-requests/cc-recipients', {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      const rawRecipients = response.data.recipients || [];
      const recipientMap = new Map();

      rawRecipients.forEach(recipient => {
        const normalizedEmail = normalizeEmail(recipient.officialEmail);
        if (!normalizedEmail) return;

        let inferredType = recipient.type;
        if (!inferredType && normalizedEmail === normalizeEmail(user?.email || user?.officialEmail)) {
          inferredType = 'current_manager';
        }

        recipientMap.set(normalizedEmail, {
          ...recipient,
          type: inferredType || recipient.type,
        });
      });

      const managerEmail = normalizeEmail(user?.email || user?.officialEmail);
      if (managerEmail) {
        if (!recipientMap.has(managerEmail)) {
          recipientMap.set(managerEmail, {
            id: user?.id || `manager-${managerEmail}`,
            firstName: user?.firstName || user?.name?.split(' ')?.[0] || '',
            lastName: user?.lastName || user?.name?.split(' ')?.slice(1).join(' ') || '',
            officialEmail: user?.officialEmail || user?.email,
            Department: user?.Department || user?.department || '',
            type: 'current_manager',
            source: 'inferred_manager'
          });
        } else {
          const managerRecipient = recipientMap.get(managerEmail);
          if (managerRecipient.type !== 'current_manager') {
            recipientMap.set(managerEmail, { ...managerRecipient, type: 'current_manager' });
          }
        }
      }

      teamMembers.forEach(member => {
        const normalizedEmail = normalizeEmail(member.officialEmail);
        if (!normalizedEmail) return;

        if (!recipientMap.has(normalizedEmail)) {
          recipientMap.set(normalizedEmail, {
            id: member.id,
            firstName: member.firstName,
            lastName: member.lastName,
            officialEmail: member.officialEmail,
            Department: member.Department,
            type: 'team_member',
            source: 'fallback_team_members'
          });
        } else {
          const existingRecipient = recipientMap.get(normalizedEmail);
          if (!existingRecipient.type || existingRecipient.type === 'same_department') {
            recipientMap.set(normalizedEmail, {
              ...existingRecipient,
              type: 'team_member'
            });
          }
        }
      });

      const mergedRecipients = Array.from(recipientMap.values()).map(recipient => {
        if (recipient.type === 'request_sender') {
          return recipient;
        }

        if (recipient.source === 'api' || recipient.source === 'fallback_team_members' || recipient.source === 'inferred_manager') {
          return recipient;
        }

        const originalRecipient = rawRecipients.find(r => normalizeEmail(r.officialEmail) === normalizeEmail(recipient.officialEmail));
        if (originalRecipient) {
          return recipient;
        }

        return { ...recipient, type: 'team_member' };
      });
      setCCRecipients(mergedRecipients);
      
      console.log('📋 All recipients count:', mergedRecipients.length);
      console.log('📋 First 5 recipients:', mergedRecipients.slice(0, 5).map(r => ({ name: `${r.firstName} ${r.lastName}`, email: r.officialEmail, type: r.type || 'NO TYPE', id: r.id, source: r.source || 'api' })));
      console.log('🔍 All recipient types:', [...new Set(mergedRecipients.map(r => r.type || 'NO TYPE'))]);
      
      // Gather team-related recipients (team members, approver, request sender)
      const recipientTeamEmails = mergedRecipients
        .filter(r => ['team_member', 'current_manager', 'request_sender'].includes(r.type))
        .map(r => normalizeEmail(r.officialEmail))
        .filter(e => e);

      // Include request sender (if not already present)
      const requestSenderEmail = normalizeEmail(
        mergedRecipients.find(r => r.type === 'request_sender')?.officialEmail
      );
      if (requestSenderEmail) {
        recipientTeamEmails.push(requestSenderEmail);
      }

      if (recipientTeamEmails.length === 0) {
        console.log('⚠️ No team-related emails identified for pre-selection.');
      }
      
      // Ensure uniqueness while preserving insertion order
      const uniqueTeamMemberEmails = Array.from(new Set(recipientTeamEmails));

      console.log('✅ Team members to pre-select:', uniqueTeamMemberEmails);
      
      // Set selected emails BEFORE showing modal
      console.log('🔄 About to call setSelectedCCEmails with:', uniqueTeamMemberEmails);
      setSelectedCCEmails(uniqueTeamMemberEmails);
      console.log('✅ setSelectedCCEmails called');
      
      console.log('✅ CC Recipients loaded:', { total: mergedRecipients.length, teamMembers: uniqueTeamMemberEmails.length, teamEmails: uniqueTeamMemberEmails });
      
      // Show modal after state is set
      if (shouldShowModal) {
        setShowCCModal(true);
      }
    } catch (err) {
      console.error('Failed to load CC recipients:', err);
      toast.error('Failed to load CC recipients');
    } finally {
      setLoadingCCRecipients(false);
    }
  };

  // --- Bulk approval (multi-select) state + helpers ---
  const [selectedCheckoutIds, setSelectedCheckoutIds] = useState([]);
  const [selectedLeaveIds, setSelectedLeaveIds] = useState([]);

  const toggleCheckoutSelect = (id) => {
    setSelectedCheckoutIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllCheckouts = () => {
    setSelectedCheckoutIds((prev) => (prev.length === pendingRequests.length ? [] : pendingRequests.map((r) => r.id)));
  };
  const toggleLeaveSelect = (id) => {
    setSelectedLeaveIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllLeaves = () => {
    setSelectedLeaveIds((prev) => (prev.length === pendingLeaves.length ? [] : pendingLeaves.map((r) => r.id)));
  };

  // Bulk approve opens the SAME CC modal once; the chosen CC emails + note apply
  // to every selected request. CC recipients are loaded from the first selected
  // request as a sensible default the manager can adjust.
  const handleBulkApproveCheckout = async () => {
    if (selectedCheckoutIds.length === 0) return;
    setPendingApproval({ type: 'checkout', bulk: true, requestIds: [...selectedCheckoutIds], notes: '' });
    setSelectedCCEmails([]);
    setCCSearchTerm('');
    await loadCCRecipients(selectedCheckoutIds[0], 'checkout', true);
  };
  const handleBulkApproveLeave = async () => {
    if (selectedLeaveIds.length === 0) return;
    setPendingApproval({ type: 'leave', bulk: true, requestIds: [...selectedLeaveIds], notes: '' });
    setSelectedCCEmails([]);
    setCCSearchTerm('');
    await loadCCRecipients(selectedLeaveIds[0], 'leave', true);
  };

  const handleApprove = async (requestId) => {
    // Store the request and show CC selection modal
    const request = pendingRequests.find(r => r.id === requestId);
    setPendingApproval({ type: 'checkout', requestId, notes: reviewNotes, request });
    // Reset selections
    setSelectedCCEmails([]);
    setCCSearchTerm('');
    // Load CC recipients with request ID to get upper managers and same department users
    await loadCCRecipients(requestId, 'checkout', true);
  };

  const handleApproveWithCC = async () => {
    if (!pendingApproval) return;

    try {
      setProcessingRequest(pendingApproval.requestId || 'bulk');
      setShowCCModal(false);
      const token = localStorage.getItem('token');

      // Bulk approval: one call with all selected ids sharing the chosen CC/note
      if (pendingApproval.bulk) {
        const ids = pendingApproval.requestIds || [];
        const { data } = await axios.post(
          '/api/checkout-requests/approve-bulk',
          { requestIds: ids, notes: pendingApproval.notes, ccEmails: selectedCCEmails },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const okIds = (data.results || []).filter(r => r.success).map(r => r.requestId);
        setPendingRequests(prev => prev.filter(r => !okIds.includes(r.id)));
        setSelectedCheckoutIds([]);
        setReviewNotes('');
        setSelectedRequest(null);
        setPendingApproval(null);
        if (data.failed > 0) {
          toast.warning(`${data.message} (${data.failed} could not be approved.)`);
        } else {
          toast.success(`${data.message} Emails sent to employees and selected recipients.`);
        }
        return;
      }

      await axios.post(
        `/api/checkout-requests/approve/${pendingApproval.requestId}`,
        {
          notes: pendingApproval.notes,
          ccEmails: selectedCCEmails
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove approved request from pending list without reloading entire page
      setPendingRequests(prev => prev.filter(r => r.id !== pendingApproval.requestId));
      setReviewNotes('');
      setSelectedRequest(null);
      setPendingApproval(null);
      toast.success('Request approved successfully! Email sent to employee and selected recipients.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!reviewNotes.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }

    // Show confirmation dialog
    setConfirmConfig({
      title: 'Reject Checkout Request',
      message: 'Are you sure you want to reject this checkout request? The employee will be notified.',
      confirmText: 'Reject',
      type: 'danger',
      onConfirm: async () => {
        try {
          setProcessingRequest(requestId);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/checkout-requests/reject/${requestId}`,
            { notes: reviewNotes },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Remove rejected request from pending list without reloading entire page
          setPendingRequests(prev => prev.filter(r => r.id !== requestId));
          setReviewNotes('');
          setSelectedRequest(null);
          toast.success('Request rejected. Employee has been notified.');
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject request');
        } finally {
          setProcessingRequest(null);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleLogout = () => {
    // Use AuthContext logout which clears both cookies and localStorage
    logout();
  };

  const toggleMemberExpanded = (memberId) => {
    setExpandedMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }));
  };

  // Open attendance correction modal
  const handleOpenAttendanceCorrection = async (member, dateStr, correctionRequest = null) => {
    try {
      setSelectedMemberForCorrection(member);
      setSelectedDateForCorrection(dateStr);
      setSelectedCorrectionRequest(correctionRequest);
      if (correctionRequest) {
        setCorrectionPurpose(correctionRequest.reason || '');
      }
      
      // Fetch current attendance data for this user and date
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/attendance/daily/${dateStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Find the user's attendance record from the daily hours array
      const userAttendance = response.data.dailyHours?.find(
        day => day.user?.id === member.id || day.user?.uidNumber === member.uidNumber
      );
      
      // Set current check-in and checkout times if available
      if (correctionRequest?.requestedCheckInTime) {
        setCorrectionCheckInTime(moment(correctionRequest.requestedCheckInTime).format('YYYY-MM-DDTHH:mm'));
      } else if (userAttendance?.firstCheckIn) {
        setCorrectionCheckInTime(moment(userAttendance.firstCheckIn).format('YYYY-MM-DDTHH:mm'));
      } else {
        // Default to 9 AM if no check-in
        setCorrectionCheckInTime(moment(dateStr).hour(9).minute(0).format('YYYY-MM-DDTHH:mm'));
      }
      
      if (correctionRequest?.requestedCheckOutTime) {
        setCorrectionCheckOutTime(moment(correctionRequest.requestedCheckOutTime).format('YYYY-MM-DDTHH:mm'));
      } else if (userAttendance?.lastCheckOut) {
        setCorrectionCheckOutTime(moment(userAttendance.lastCheckOut).format('YYYY-MM-DDTHH:mm'));
      } else {
        // Default to 6 PM if no checkout
        setCorrectionCheckOutTime(moment(dateStr).hour(18).minute(0).format('YYYY-MM-DDTHH:mm'));
      }
      
      if (!correctionRequest) {
        setCorrectionPurpose('');
      }
      setShowAttendanceCorrectionModal(true);
    } catch (err) {
      console.error('Error fetching attendance data:', err);
      // Still open modal with default times
      setCorrectionCheckInTime(moment(dateStr).hour(9).minute(0).format('YYYY-MM-DDTHH:mm'));
      setCorrectionCheckOutTime(moment(dateStr).hour(18).minute(0).format('YYYY-MM-DDTHH:mm'));
      if (!correctionRequest) {
        setCorrectionPurpose('');
      }
      setShowAttendanceCorrectionModal(true);
    }
  };

  // Save attendance correction
  const handleSaveAttendanceCorrection = async () => {
    if (!selectedMemberForCorrection || !selectedDateForCorrection) return;
    
    if (!correctionCheckInTime || !correctionCheckOutTime) {
      toast.error('Please provide both check-in and checkout times');
      return;
    }
    
    if (!correctionPurpose.trim()) {
      toast.error('Please provide a reason for the correction');
      return;
    }
    
    if (moment(correctionCheckOutTime).isBefore(moment(correctionCheckInTime))) {
      toast.error('Checkout time must be after check-in time');
      return;
    }
    
    try {
      setSavingCorrection(true);
      const token = localStorage.getItem('token');

      if (selectedCorrectionRequest?.id) {
        await axios.put(`/api/attendance-corrections/approve/${selectedCorrectionRequest.id}`, {
          reviewerNotes: correctionPurpose.trim(),
          checkInTime: correctionCheckInTime,
          checkOutTime: correctionCheckOutTime
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPendingCorrections(prev => prev.filter(r => r.id !== selectedCorrectionRequest.id));
        setSelectedCorrectionRequest(null);
        setCorrectionReviewNotes('');
      } else {
        const correctionUserId = selectedMemberForCorrection?.id || selectedCorrectionRequest?.userId;

        if (!correctionUserId) {
          toast.error('Missing user for attendance correction. Please refresh and try again.');
          return;
        }

        await axios.post('/api/attendance/correct', {
          userId: correctionUserId,
          date: selectedDateForCorrection,
          checkInTime: correctionCheckInTime,
          checkOutTime: correctionCheckOutTime,
          purpose: correctionPurpose.trim()
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast.success('Attendance corrected successfully!');
      setShowAttendanceCorrectionModal(false);
      
      // Reload team attendance to reflect changes
      await loadTeamAttendance();
      await loadCorrectionStats();
    } catch (err) {
      console.error('Error saving attendance correction:', err);
      toast.error(err.response?.data?.error || 'Failed to save attendance correction');
    } finally {
      setSavingCorrection(false);
    }
  };

  const handleRejectCorrectionRequest = async (requestId) => {
    if (!correctionReviewNotes.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    try {
      setProcessingCorrection(requestId);
      const token = localStorage.getItem('token');
      await axios.put(`/api/attendance-corrections/reject/${requestId}`, {
        reviewerNotes: correctionReviewNotes.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPendingCorrections(prev => prev.filter(r => r.id !== requestId));
      setSelectedCorrectionRequest(null);
      setCorrectionReviewNotes('');
      toast.success('Correction request rejected.');
    } catch (err) {
      console.error('Error rejecting correction request:', err);
      toast.error(err.response?.data?.error || 'Failed to reject correction request');
    } finally {
      setProcessingCorrection(null);
    }
  };

  // Helper function to get attendance status for a member on a specific date
  const getStatusForDate = (member, dateStr) => {
    const isOnWorkFromHome = member.leaveRequests?.some(leave => {
      if (leave.status !== 'approved') return false;
      if (leave.leaveType !== 'Work from Home') return false;
      const startDate = moment(leave.startDate).format('YYYY-MM-DD');
      const endDate = moment(leave.endDate).format('YYYY-MM-DD');
      return dateStr >= startDate && dateStr <= endDate;
    }) || false;

    // Check if on approved leave
    const isOnLeave = member.leaveRequests?.some(leave => {
      if (leave.status !== 'approved') return false;
      // Skip Short Leave and Work from Home as they don't reduce expected hours
      if (leave.leaveType === 'Short Leave' || leave.leaveType === 'Work from Home') return false;
      const startDate = moment(leave.startDate).format('YYYY-MM-DD');
      const endDate = moment(leave.endDate).format('YYYY-MM-DD');
      return dateStr >= startDate && dateStr <= endDate;
    }) || false;

    // Check if checked in (has hours worked on that date)
    const dayRecord = member.dailyHours?.find(d => moment(d.date).format('YYYY-MM-DD') === dateStr);
    const hasHours = dayRecord && ((dayRecord.hoursWorked || 0) + (dayRecord.extraHours || 0) > 0);

    if (isOnLeave) return 'on-leave';
    if (isOnWorkFromHome) return 'work-from-home';
    if (hasHours) return 'checked-in';
    return 'not-checked-in';
  };

  // Helper function to organize team member data by date
  const organizeDataByDate = (teamMembersList, startDateParam, endDateParam) => {
    const dataByDate = {};
    const start = moment(startDateParam);
    const end = moment(endDateParam);
    const today = moment().startOf('day');

    // Initialize all dates in range
    for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'day')) {
      const dateStr = date.format('YYYY-MM-DD');
      dataByDate[dateStr] = [];
    }
    const dateKeys = Object.keys(dataByDate);

    // Process each team member
    teamMembersList.forEach(member => {
      // First, process leave requests to mark leave days
      if (member.leaveRequests && member.leaveRequests.length > 0) {
        member.leaveRequests.forEach(leave => {
          if (leave.status !== 'approved') return;
          // Skip short leave for calendar day marking
          if (leave.leaveType === 'Short Leave') return;

          const leaveStart = moment(leave.startDate);
          const leaveEnd = moment(leave.endDate);
          const periodStart = moment(startDateParam);
          const periodEnd = moment(endDateParam);

          const effectiveStart = moment.max(leaveStart, periodStart);
          const effectiveEnd = moment.min(leaveEnd, periodEnd);

          for (let date = effectiveStart.clone(); date.isSameOrBefore(effectiveEnd); date.add(1, 'day')) {
            const dateStr = date.format('YYYY-MM-DD');
            if (dataByDate[dateStr]) {
              // Check if member already has entry for this date
              const existingIndex = dataByDate[dateStr].findIndex(entry => entry.member.id === member.id);
              if (existingIndex < 0) {
                // Add new entry for leave day (no attendance record yet)
                const taskCount = clickupTaskCounts[member.officialEmail]?.count || 0;
                const status = leave.leaveType === 'Work from Home' ? 'work-from-home' : 'on-leave';
                dataByDate[dateStr].push({
                  member,
                  hours: 0,
                  status,
                  leave,
                  tasks: taskCount,
                  dayRecord: null
                });
              }
            }
          }
        });
      }

      // Then process dailyHours (will update leave entries if they have hours, or create new entries)
      if (member.dailyHours && member.dailyHours.length > 0) {
        member.dailyHours.forEach(day => {
          const dateStr = moment(day.date).format('YYYY-MM-DD');
          if (dataByDate[dateStr]) {
            const hours = (day.hoursWorked || 0) + (day.extraHours || 0);
            const taskCount = clickupTaskCounts[member.officialEmail]?.count || 0;

            // Check if member already has entry for this date (from leave processing)
            const existingIndex = dataByDate[dateStr].findIndex(entry => entry.member.id === member.id);
            
            if (existingIndex >= 0) {
              // Update existing entry (likely a leave entry)
              dataByDate[dateStr][existingIndex].hours = hours;
              dataByDate[dateStr][existingIndex].dayRecord = day;
              // Keep leave status if they're on leave, otherwise update to checked-in
              if (!dataByDate[dateStr][existingIndex].leave) {
                dataByDate[dateStr][existingIndex].status = hours > 0 ? 'checked-in' : 'not-checked-in';
              }
              dataByDate[dateStr][existingIndex].tasks = taskCount;
            } else {
              // Add new entry for attendance day
              const status = hours > 0 ? 'checked-in' : 'not-checked-in';
              dataByDate[dateStr].push({
                member,
                hours,
                status,
                leave: null,
                tasks: taskCount,
                dayRecord: day
              });
            }
          }
        });
      }

      // Fill missing days as absent (not checked in) for past/working weekdays
      dateKeys.forEach(dateStr => {
        const dateMoment = moment(dateStr, 'YYYY-MM-DD');
        const isFuture = dateMoment.isAfter(today, 'day');
        const isWeekend = dateMoment.day() === 0 || dateMoment.day() === 6;

        if (isFuture || isWeekend) return;

        const existingIndex = dataByDate[dateStr].findIndex(entry => entry.member.id === member.id);
        if (existingIndex >= 0) return;

        const status = getStatusForDate(member, dateStr);
        if (status !== 'not-checked-in') return;

        const dayRecord = member.dailyHours?.find(d => moment(d.date).format('YYYY-MM-DD') === dateStr) || null;
        const hours = dayRecord ? (dayRecord.hoursWorked || 0) + (dayRecord.extraHours || 0) : 0;
        const taskCount = clickupTaskCounts[member.officialEmail]?.count || 0;

        dataByDate[dateStr].push({
          member,
          hours,
          status: 'not-checked-in',
          leave: null,
          tasks: taskCount,
          dayRecord
        });
      });
    });

    // Sort members within each date (by status: checked-in first, then on-leave, then not-checked-in)
    Object.keys(dataByDate).forEach(dateStr => {
      dataByDate[dateStr].sort((a, b) => {
        const statusOrder = { 'checked-in': 0, 'work-from-home': 1, 'on-leave': 2, 'not-checked-in': 3 };
        return statusOrder[a.status] - statusOrder[b.status];
      });
    });

    return dataByDate;
  };

  // CC selection functions
  const toggleCCRecipient = (email) => {
    const normalizedEmail = normalizeEmail(email);
    if (!normalizedEmail) return;
    setSelectedCCEmails(prev => 
      prev.includes(normalizedEmail) 
        ? prev.filter(e => e !== normalizedEmail)
        : [...prev, normalizedEmail]
    );
  };

  const toggleAllCC = () => {
    // Get filtered recipients based on search term
    const filteredRecipients = ccRecipients.filter(recipient => {
      if (!ccSearchTerm) return true;
      const searchText = `${recipient.firstName} ${recipient.lastName} ${recipient.officialEmail} ${recipient.Department || ''}`.toLowerCase();
      return searchText.includes(ccSearchTerm);
    });
    const filteredEmails = filteredRecipients
      .map(r => normalizeEmail(r.officialEmail))
      .filter(e => e);
    const allFilteredSelected = filteredEmails.length > 0 && filteredEmails.every(email => selectedCCEmails.includes(email));
    
    if (allFilteredSelected) {
      // Deselect all filtered recipients
      setSelectedCCEmails(prev => prev.filter(email => !filteredEmails.includes(email)));
    } else {
      // Select all filtered recipients
      setSelectedCCEmails(prev => {
        const newEmails = filteredEmails.filter(email => !prev.includes(email));
        return [...prev, ...new Set(newEmails)];
      });
    }
  };

  const handleFinalApproval = () => {
    if (!pendingApproval) return;
    
    if (pendingApproval.type === 'checkout') {
      handleApproveWithCC();
    } else if (pendingApproval.type === 'leave') {
      handleApproveLeaveWithCC();
    } else if (pendingApproval.type === 'extra-hours') {
      handleApproveExtraHoursWithCC();
    }
  };

  const handleApproveLeave = async (requestId) => {
    // Store the request and show CC selection modal
    const request = pendingLeaves.find(r => r.id === requestId);
    setPendingApproval({ type: 'leave', requestId, notes: leaveReviewNotes, request });
    // Reset selections
    setSelectedCCEmails([]);
    setCCSearchTerm('');
    // Load CC recipients with request ID and type to get upper managers and same department users
    await loadCCRecipients(requestId, 'leave', true);
  };

  const handleCancelApprovedLeave = async (leave, member) => {
    if (!leave?.id) return;
    const memberName = member ? `${member.firstName || ''} ${member.lastName || ''}`.trim() : 'this employee';
    if (!window.confirm(`Cancel approved leave for ${memberName}?`)) {
      return;
    }
    const reason = window.prompt('Reason for cancellation (optional):') || '';
    try {
      setCancellingApprovedLeaveId(leave.id);
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/leave-requests/cancel/${leave.id}`,
        { reason: reason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Approved leave cancelled.');
      await loadTeamAttendance();
      await loadLeaveStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel approved leave');
    } finally {
      setCancellingApprovedLeaveId(null);
    }
  };

  const handleApproveLeaveWithCC = async () => {
    if (!pendingApproval) return;

    try {
      setProcessingLeave(pendingApproval.requestId || 'bulk');
      setShowCCModal(false);
      const token = localStorage.getItem('token');

      // Bulk approval: one call with all selected ids sharing the chosen CC/note
      if (pendingApproval.bulk) {
        const ids = pendingApproval.requestIds || [];
        const { data } = await axios.post(
          '/api/leave-requests/approve-bulk',
          { requestIds: ids, notes: pendingApproval.notes, ccEmails: selectedCCEmails },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const okIds = (data.results || []).filter(r => r.success).map(r => r.requestId);
        setPendingLeaves(prev => prev.filter(r => !okIds.includes(r.id)));
        setSelectedLeaveIds([]);
        setLeaveReviewNotes('');
        setSelectedLeaveRequest(null);
        setPendingApproval(null);
        if (data.failed > 0) {
          toast.warning(`${data.message} (${data.failed} could not be approved.)`);
        } else {
          toast.success(`${data.message} Emails sent to employees and selected recipients.`);
        }
        return;
      }

      await axios.post(
        `/api/leave-requests/approve/${pendingApproval.requestId}`,
        {
          notes: pendingApproval.notes,
          ccEmails: selectedCCEmails
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove approved request from pending list without reloading entire page
      setPendingLeaves(prev => prev.filter(r => r.id !== pendingApproval.requestId));
      setLeaveReviewNotes('');
      setSelectedLeaveRequest(null);
      setPendingApproval(null);
      toast.success('Leave request approved successfully! Email sent to employee and selected recipients.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve leave request');
    } finally {
      setProcessingLeave(null);
    }
  };

  const handleRejectLeave = async (requestId) => {
    if (!leaveReviewNotes.trim()) {
      toast.warning('Please provide a reason for rejecting this leave');
      return;
    }

    // Show confirmation dialog
    setConfirmConfig({
      title: 'Reject Leave Request',
      message: 'Are you sure you want to reject this leave request? The employee will be notified.',
      confirmText: 'Reject',
      type: 'danger',
      onConfirm: async () => {
        try {
          setProcessingLeave(requestId);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/leave-requests/reject/${requestId}`,
            { notes: leaveReviewNotes },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Remove rejected request from pending list without reloading entire page
          setPendingLeaves(prev => prev.filter(r => r.id !== requestId));
          setLeaveReviewNotes('');
          setSelectedLeaveRequest(null);
          toast.success('Leave request rejected. Employee has been notified.');
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject leave request');
        } finally {
          setProcessingLeave(null);
        }
      }
    });
    setShowConfirm(true);
  };

  const handleApproveExtraHours = async (requestId) => {
    // Store the request and show CC selection modal
    const request = pendingExtraHours.find(r => r.id === requestId);
    setPendingApproval({ 
      type: 'extra-hours', 
      requestId, 
      notes: extraHoursReviewNotes, 
      approvedHours: extraHoursApprovedHours || request.requestedHours,
      request 
    });
    // Reset selections
    setSelectedCCEmails([]);
    setCCSearchTerm('');
    // Load CC recipients - using checkout endpoint as it has similar structure
    await loadCCRecipients(requestId, 'checkout', true);
  };

  const handleApproveExtraHoursWithCC = async () => {
    if (!pendingApproval) return;

    try {
      setProcessingExtraHours(pendingApproval.requestId);
      setShowCCModal(false);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `/api/extra-hours-requests/approve/${pendingApproval.requestId}`,
        { 
          notes: pendingApproval.notes,
          approvedHours: pendingApproval.approvedHours,
          ccEmails: selectedCCEmails 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove approved request from pending list without reloading entire page
      setPendingExtraHours(prev => prev.filter(r => r.id !== pendingApproval.requestId));
      setExtraHoursReviewNotes('');
      setExtraHoursApprovedHours(null);
      setSelectedExtraHoursRequest(null);
      setPendingApproval(null);
      toast.success('Extra hours request approved successfully! Email sent to employee and selected recipients.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setProcessingExtraHours(null);
    }
  };

  const handleRejectExtraHours = async (requestId) => {
    if (!extraHoursReviewNotes.trim()) {
      toast.warning('Please provide a reason for rejecting this request (mandatory)');
      return;
    }

    // Show confirmation dialog
    setConfirmConfig({
      title: 'Reject Extra Hours Request',
      message: 'Are you sure you want to reject this extra hours request? The employee will be notified with your reason.',
      confirmText: 'Reject',
      type: 'danger',
      onConfirm: async () => {
        try {
          setProcessingExtraHours(requestId);
          const token = localStorage.getItem('token');
          
          await axios.post(
            `/api/extra-hours-requests/reject/${requestId}`,
            { notes: extraHoursReviewNotes },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          // Remove rejected request from pending list without reloading entire page
          setPendingExtraHours(prev => prev.filter(r => r.id !== requestId));
          setExtraHoursReviewNotes('');
          setSelectedExtraHoursRequest(null);
          toast.success('Extra hours request rejected. Employee has been notified.');
        } catch (err) {
          toast.error(err.response?.data?.error || 'Failed to reject extra hours request');
        } finally {
          setProcessingExtraHours(null);
        }
      }
    });
    setShowConfirm(true);
  };

  const loadLeaveStats = async () => {
    try {
      setLeaveStatsLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = buildRollingRequestDateRange(leaveStatsPeriod);
      
      const response = await axios.get('/api/leave-requests/team-requests', {
        params: {
          startDate,
          endDate,
          limit: 1000 // Get all for stats
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        const requests = response.data.requests;
        setLeaveHistory(requests);
        
        // Calculate statistics
        const stats = {
          total: requests.length,
          pending: requests.filter(r => r.status === 'pending').length,
          approved: requests.filter(r => r.status === 'approved').length,
          rejected: requests.filter(r => r.status === 'rejected').length,
          
          // By leave type
          byType: {},
          
          // By user
          byUser: {},
          byUserDetails: {},
          
          // Timeline data (last 30 days)
          timeline: [],
          timelineDetails: {}
        };

        // Group by type
        requests.forEach(req => {
          stats.byType[req.leaveType] = (stats.byType[req.leaveType] || 0) + 1;
          
          // Group by user
          const userName = `${req.user?.firstName} ${req.user?.lastName}`;
          if (!stats.byUser[userName]) {
            stats.byUser[userName] = {
              total: 0,
              approved: 0,
              pending: 0,
              rejected: 0,
              days: 0
            };
          }
          if (!stats.byUserDetails[userName]) {
            stats.byUserDetails[userName] = [];
          }
          stats.byUser[userName].total++;
          stats.byUser[userName][req.status]++;
          if (req.status === 'approved') {
            stats.byUser[userName].days += req.totalDays || 0;
          }
          stats.byUserDetails[userName].push(req);
        });

        // Sort detail lists by start date descending for easier reading
        Object.keys(stats.byUserDetails).forEach(userName => {
          stats.byUserDetails[userName].sort((a, b) => moment(b.startDate).diff(moment(a.startDate)));
        });

        // Timeline data - group by date
        const timelineMap = {};
        for (let i = leaveStatsPeriod - 1; i >= 0; i--) {
          const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
          timelineMap[date] = { date, count: 0, approved: 0, pending: 0, rejected: 0 };
          stats.timelineDetails[date] = [];
        }

        requests.forEach(req => {
          const startDate = moment(req.startDate).format('YYYY-MM-DD');
          if (timelineMap[startDate]) {
            timelineMap[startDate].count++;
            timelineMap[startDate][req.status]++;
            const userName = `${req.user?.firstName} ${req.user?.lastName}`.trim();
            stats.timelineDetails[startDate].push({
              name: userName || 'Unknown User',
              status: req.status,
              type: req.leaveType,
              totalDays: req.totalDays,
              reason: req.reason
            });
          }
        });

        stats.timeline = Object.values(timelineMap);
        
        setLeaveStats(stats);
      }
    } catch (err) {
      console.error('Error loading leave stats:', err);
    } finally {
      setLeaveStatsLoading(false);
    }
  };

  const loadCorrectionStats = async () => {
    try {
      setCorrectionStatsLoading(true);
      const token = localStorage.getItem('token');
      const { startDate, endDate } = buildRollingRequestDateRange(correctionStatsPeriod);

      const response = await axios.get('/api/attendance-corrections/team-requests', {
        params: {
          startDate,
          endDate,
          limit: 2000
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.requests) {
        const requests = response.data.requests;

        const stats = {
          total: requests.length,
          pending: requests.filter(r => r.status === 'pending').length,
          approved: requests.filter(r => r.status === 'approved').length,
          rejected: requests.filter(r => r.status === 'rejected').length,
          byUser: {},
          byUserDetails: {},
          timeline: [],
          timelineDetails: {}
        };

        requests.forEach(req => {
          const userName = `${req.user?.firstName || ''} ${req.user?.lastName || ''}`.trim() || 'Unknown User';
          if (!stats.byUser[userName]) {
            stats.byUser[userName] = {
              total: 0,
              approved: 0,
              pending: 0,
              rejected: 0
            };
          }
          if (!stats.byUserDetails[userName]) {
            stats.byUserDetails[userName] = [];
          }
          stats.byUser[userName].total++;
          stats.byUser[userName][req.status] = (stats.byUser[userName][req.status] || 0) + 1;
          stats.byUserDetails[userName].push(req);
        });

        Object.keys(stats.byUserDetails).forEach(userName => {
          stats.byUserDetails[userName].sort((a, b) => moment(b.requestDate).diff(moment(a.requestDate)));
        });

        const timelineMap = {};
        for (let i = correctionStatsPeriod - 1; i >= 0; i--) {
          const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
          timelineMap[date] = { date, count: 0, approved: 0, pending: 0, rejected: 0 };
          stats.timelineDetails[date] = [];
        }

        requests.forEach(req => {
          const requestDate = moment(req.requestDate).format('YYYY-MM-DD');
          if (timelineMap[requestDate]) {
            timelineMap[requestDate].count++;
            timelineMap[requestDate][req.status]++;
            const userName = `${req.user?.firstName || ''} ${req.user?.lastName || ''}`.trim() || 'Unknown User';
            stats.timelineDetails[requestDate].push({
              name: userName,
              status: req.status,
              reason: req.reason
            });
          }
        });

        stats.timeline = Object.values(timelineMap);
        setCorrectionStats(stats);
      }
    } catch (err) {
      console.error('Error loading correction stats:', err);
    } finally {
      setCorrectionStatsLoading(false);
    }
  };

  const handleOpenCorrectionStatsDetails = (userName) => {
    if (!correctionStats?.byUserDetails) return;
    const userRequests = correctionStats.byUserDetails[userName] || [];
    if (userRequests.length === 0) return;

    const primaryRequest = userRequests[0];
    const userInfo = primaryRequest.user || {};

    setSelectedCorrectionStatsUser({
      name: userName,
      email: userInfo.officialEmail || userInfo.email || '',
      department: userInfo.Department || userInfo.department || '',
      metrics: correctionStats.byUser?.[userName] || {}
    });
    setSelectedCorrectionStatsRequests(userRequests);
    setShowCorrectionStatsModal(true);
  };

  const handleCloseCorrectionStatsModal = () => {
    setShowCorrectionStatsModal(false);
    setSelectedCorrectionStatsUser(null);
    setSelectedCorrectionStatsRequests([]);
  };

  const getCorrectionStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 border border-gray-200';
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200';
    }
  };

  const handleOpenLeaveStatsDetails = (userName) => {
    if (!leaveStats || !leaveStats.byUserDetails) return;
    const userRequests = leaveStats.byUserDetails[userName] || [];
    if (userRequests.length === 0) return;

    const primaryRequest = userRequests[0];
    const userInfo = primaryRequest.user || {};

    setSelectedLeaveStatsUser({
      name: userName,
      email: userInfo.officialEmail || userInfo.email || '',
      department: userInfo.Department || userInfo.department || '',
      title: userInfo.Designation || userInfo.title || userInfo.role || '',
      totalRequests: leaveStats.byUser?.[userName]?.total || userRequests.length,
      metrics: leaveStats.byUser?.[userName] || {}
    });
    setSelectedLeaveStatsRequests(userRequests);
    setShowLeaveStatsModal(true);
  };

  const handleCloseLeaveStatsModal = () => {
    setShowLeaveStatsModal(false);
    setSelectedLeaveStatsUser(null);
    setSelectedLeaveStatsRequests([]);
  };

  const getLeaveStatusClass = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'approved':
        return 'bg-green-100 text-green-800 border border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border border-gray-200';
    }
  };

  const scrollTabsIntoView = () => {
    setTimeout(() => {
      const tabsElement = document.querySelector('[role="tablist"]') || document.querySelector('.flex.space-x-1');
      if (tabsElement) {
        tabsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const todayAttendanceSummary = useMemo(() => buildTodayAttendanceSummary(todayTeamMembers), [todayTeamMembers]);

  if (loading) {
    return (
      <PongLoadingScreen />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">{error}</div>
          <button
            onClick={loadDashboardData}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div
      ref={pullToRefresh.containerRef}
      className="min-h-screen bg-gray-50"
    >
      {pullToRefresh.isEnabled && (
        <div className="sticky top-0 z-40 px-4 pt-3 sm:px-6 lg:px-8 pointer-events-none">
          <div
            className="mx-auto max-w-md overflow-hidden rounded-2xl border border-indigo-200/80 bg-white/90 shadow-lg backdrop-blur"
            style={{
              height: `${pullToRefresh.indicatorHeight}px`,
              opacity: pullToRefresh.indicatorHeight > 0 ? 1 : 0,
              transition: pullToRefresh.indicatorTransition,
            }}
          >
            <div className="flex h-14 items-center justify-center gap-3 text-sm font-semibold text-slate-700">
              <div
                className={`h-5 w-5 rounded-full border-2 border-indigo-500 border-t-transparent ${
                  pullToRefresh.isRefreshing ? 'animate-spin' : ''
                }`}
              />
              <span>
                {pullToRefresh.isRefreshing
                  ? 'Refreshing dashboard...'
                  : pullToRefresh.progress >= 1
                    ? 'Release to refresh'
                    : 'Pull to refresh'}
              </span>
            </div>
          </div>
        </div>
      )}
      {/* Header - Modern Gradient Design */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="w-full rounded-[28px] border border-white/20 bg-white/10 px-4 py-4 shadow-[0_24px_70px_rgba(51,22,95,0.28)] backdrop-blur-xl sm:px-5 lg:px-6">
              <div className="flex items-center gap-4 lg:gap-6">
                <ManagerProfileSummary
                  user={user}
                  onProfilePhotoEdit={() => setShowProfilePhotoUpload(true)}
                />

                <div className="hidden min-w-0 flex-1 items-center justify-end gap-2 lg:flex">
                  <button
                    onClick={() => navigate('/kpi/manager')}
                    className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white/95 transition duration-200 hover:bg-white/16 focus:outline-none focus:ring-4 focus:ring-white/20"
                    title="Review KPI submissions for your team"
                  >
                    <ChartBarIcon className="h-5 w-5" />
                    <span>KPI Review</span>
                  </button>
                  <button
                    onClick={() => navigate('/device-security')}
                    className="inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white/95 transition duration-200 hover:bg-white/16 focus:outline-none focus:ring-4 focus:ring-white/20"
                    title="Review device locks and unlock requests"
                  >
                    <ShieldCheckIcon className="h-5 w-5" />
                    <span>Device Security</span>
                  </button>
                  <button
                    onClick={() => {
                      navigate('/feedback-inbox');
                    }}
                    className="relative inline-flex items-center gap-2 rounded-2xl border border-white/18 bg-black/10 px-3.5 py-3 text-sm font-medium text-white/90 transition duration-200 hover:bg-white/14 focus:outline-none focus:ring-4 focus:ring-white/20"
                    title={feedbackUnreadCount > 0 ? `${feedbackUnreadCount} unread feedback message${feedbackUnreadCount > 1 ? 's' : ''}` : 'View feedback inbox'}
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    <span>Feedback Inbox</span>
                    {feedbackUnreadCount > 0 && (
                      <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {feedbackUnreadCount > 99 ? '99+' : feedbackUnreadCount}
                      </span>
                    )}
                  </button>
                  <div className="ml-1 flex items-center gap-2 rounded-2xl border border-white/14 bg-black/10 px-2 py-2">
                    <DashboardSwitcher />
                    <NotificationBell />
                    <button
                      onClick={handleLogout}
                      className="inline-flex items-center gap-2 rounded-xl border border-white/14 bg-white/10 px-3 py-2.5 text-sm font-medium text-white/88 transition duration-200 hover:bg-white/14 focus:outline-none focus:ring-4 focus:ring-white/20"
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      <span className="hidden xl:inline">Logout</span>
                    </button>
                  </div>
                </div>

                <div className="md:hidden flex items-center gap-2 flex-shrink-0">
                  <NotificationBell />
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/25 bg-white/10 text-white shadow-lg backdrop-blur-md transition duration-200 hover:bg-white/16 focus:outline-none focus:ring-4 focus:ring-white/20"
                    aria-label="Toggle menu"
                  >
                    {mobileMenuOpen ? (
                      <XMarkIcon className="h-6 w-6" />
                    ) : (
                      <Bars3Icon className="h-6 w-6" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <div className="rounded-[24px] border border-white/18 bg-white/10 p-3 shadow-2xl backdrop-blur-xl">
                <div className="mb-3 px-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/65">
                    Quick Actions
                  </p>
                </div>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      navigate('/kpi/manager');
                      setMobileMenuOpen(false);
                    }}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white/95 transition duration-200 hover:bg-white/16 focus:outline-none focus:ring-4 focus:ring-white/20"
                  >
                    <ChartBarIcon className="h-5 w-5" />
                    KPI Review
                  </button>
                  <button
                    onClick={() => {
                      navigate('/device-security');
                      setMobileMenuOpen(false);
                    }}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/25 bg-white/10 px-4 py-3 text-sm font-semibold text-white/95 transition duration-200 hover:bg-white/16 focus:outline-none focus:ring-4 focus:ring-white/20"
                  >
                    <ShieldCheckIcon className="h-5 w-5" />
                    Device Security
                  </button>
                  <button
                    onClick={() => {
                      navigate('/feedback-inbox');
                      setMobileMenuOpen(false);
                    }}
                    className="relative inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/18 bg-black/10 px-4 py-3 text-sm font-medium text-white/90 transition duration-200 hover:bg-white/14 focus:outline-none focus:ring-4 focus:ring-white/20"
                  >
                    <ChatBubbleLeftRightIcon className="h-5 w-5" />
                    Feedback Inbox
                    {feedbackUnreadCount > 0 && (
                      <span className="absolute right-3 top-1/2 inline-flex h-5 min-w-[1.25rem] -translate-y-1/2 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {feedbackUnreadCount > 99 ? '99+' : feedbackUnreadCount}
                      </span>
                    )}
                  </button>
                </div>
                <div className="my-3 h-px bg-white/12" />
                <div className="space-y-2">
                  <div className="rounded-2xl border border-white/14 bg-black/10 px-3 py-2">
                    <DashboardSwitcher />
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-white/18 bg-black/10 px-4 py-3 text-sm font-medium text-white/88 transition duration-200 hover:bg-white/14 focus:outline-none focus:ring-4 focus:ring-white/20"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <ManagerStatsCards
          pendingRequests={pendingRequests}
          pendingLeaves={pendingLeaves}
          pendingExtraHours={pendingExtraHours}
          teamMembers={teamMembers}
          excludedMembers={excludedMembers}
          todayAttendanceSummary={todayAttendanceSummary}
          onPendingClick={() => {
            const getPendingTab = () => {
              if (pendingRequests.length > 0) return 'checkout';
              if (pendingLeaves.length > 0) return 'leave';
              if (pendingExtraHours.length > 0) return 'extra-hours';
              return 'checkout';
            };

            const tab = getPendingTab();
            setActiveTab(tab);
            scrollTabsIntoView();
          }}
          onTeamClick={() => {
            setActiveTab('team');
            scrollTabsIntoView();
          }}
          onExcludedClick={() => {
            setActiveTab('team');
            scrollTabsIntoView();
            setTimeout(() => {
              const el = document.getElementById('excluded-members');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }}
          onActiveTodayClick={() => setShowActiveTodayModal(true)}
          onAbsenteesClick={() => setShowAbsenteesModal(true)}
        />

        {/* Tabs - Modern Design */}
        <div className="mb-8">
          <div className="relative bg-white rounded-xl shadow-lg p-1 sm:p-2 border border-gray-200 overflow-x-auto">
            <div className="flex space-x-1 sm:space-x-2 min-w-max sm:min-w-0">
              <button
                onClick={() => setActiveTab('checkout')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'checkout'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Checkout</span><span className="sm:hidden ml-1">Out</span>
                  {pendingRequests.length > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                      activeTab === 'checkout' ? 'bg-white text-orange-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      {pendingRequests.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('leave')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'leave'
                    ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <CalendarDaysIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Leave</span><span className="sm:hidden ml-1">Leave</span>
                  {pendingLeaves.length > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                      activeTab === 'leave' ? 'bg-white text-purple-600' : 'bg-purple-100 text-purple-600'
                    }`}>
                      {pendingLeaves.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('extra-hours')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'extra-hours'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Extra Hours</span><span className="sm:hidden ml-1">Extra</span>
                  {pendingExtraHours.length > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                      activeTab === 'extra-hours' ? 'bg-white text-cyan-600' : 'bg-cyan-100 text-cyan-600'
                    }`}>
                      {pendingExtraHours.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('corrections')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'corrections'
                    ? 'bg-gradient-to-r from-red-500 to-orange-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ExclamationTriangleIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Corrections</span><span className="sm:hidden ml-1">Fix</span>
                  {pendingCorrections.length > 0 && (
                    <span className={`ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
                      activeTab === 'corrections' ? 'bg-white text-orange-600' : 'bg-orange-100 text-orange-600'
                    }`}>
                      {pendingCorrections.length}
                    </span>
                  )}
                </div>
              </button>

              <button
                onClick={() => setActiveTab('team')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'team'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Attendance</span><span className="sm:hidden ml-1">Attend</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('performance')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'performance'
                    ? 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ChartBarIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Performance</span><span className="sm:hidden ml-1">Perf</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('workload')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'workload'
                    ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Workload</span><span className="sm:hidden ml-1">Work</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('hierarchy')}
                className={`relative flex-1 py-2 sm:py-3 px-2 sm:px-4 md:px-6 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === 'hierarchy'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center">
                  <UserGroupIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Hierarchy</span><span className="sm:hidden ml-1">Tree</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Checkout Requests Tab */}
        {activeTab === 'checkout' && (
          <div className="space-y-6">
            {/* Pending Requests Section - Top Priority */}
            {pendingRequests.length > 0 && (
              <div className="bg-gradient-to-br from-orange-50 via-orange-100 to-red-50 shadow-xl rounded-xl border-2 border-orange-300 overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-600 px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <BellIcon className="h-6 w-6 text-white mr-3 animate-pulse" />
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        Pending Checkout Requests
                      </h2>
                      <span className="ml-3 inline-flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full bg-white text-orange-600">
                        {pendingRequests.length}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        checked={pendingRequests.length > 0 && selectedCheckoutIds.length === pendingRequests.length}
                        onChange={toggleSelectAllCheckouts}
                      />
                      Select all
                    </label>
                    {selectedCheckoutIds.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600">{selectedCheckoutIds.length} selected</span>
                        <button
                          onClick={handleBulkApproveCheckout}
                          disabled={loadingCCRecipients || processingRequest === 'bulk'}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-md"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          {loadingCCRecipients ? 'Loading...' : processingRequest === 'bulk' ? 'Approving...' : `Approve selected (${selectedCheckoutIds.length})`}
                        </button>
                      </>
                    )}
                  </div>
                  <div className="space-y-4">
                    {pendingRequests.map((request) => (
                      <div
                        key={request.id}
                        className="relative border-2 border-orange-200 rounded-xl p-4 sm:p-6 hover:border-orange-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-orange-50 transform hover:scale-[1.01]"
                      >
                        <div className="flex items-start justify-between">
                          <input
                            type="checkbox"
                            className="mt-1 mr-3 h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 flex-shrink-0"
                            checked={selectedCheckoutIds.includes(request.id)}
                            onChange={() => toggleCheckoutSelect(request.id)}
                          />
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-10 h-10 sm:w-12 sm:h-12"
                                fallbackClassName="bg-gradient-to-br from-orange-400 to-red-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className="ml-auto px-3 py-1 text-xs font-semibold rounded-full bg-orange-200 text-orange-800 border border-orange-300 animate-pulse">
                                ⏳ Pending
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div className="flex items-center p-2 bg-orange-50 rounded-lg">
                                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.requestDate).format('MMMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center p-2 bg-orange-50 rounded-lg">
                                <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Checkout Time</div>
                                  <div className="text-sm font-semibold text-orange-700">
                                    {moment(request.requestedCheckoutTime).format('hh:mm A')}
                                  </div>
                                </div>
                              </div>
                              {request.user?.Department && (
                                <div className="flex items-center p-2 bg-orange-50 rounded-lg sm:col-span-2">
                                  <span className="text-xs text-gray-500 mr-2">Department:</span>
                                  <span className="text-sm font-medium text-gray-900">{request.user?.Department}</span>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-orange-100 rounded-lg border border-orange-200">
                                <div className="text-xs font-semibold text-orange-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            <div className="text-xs text-gray-500 mb-3">
                              Submitted {moment(request.createdAt).fromNow()}
                            </div>

                            {selectedRequest === request.id && (
                              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-orange-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Notes (optional for approval, required for rejection)
                                </label>
                                <textarea
                                  value={reviewNotes}
                                  onChange={(e) => setReviewNotes(e.target.value)}
                                  rows={3}
                                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 text-sm"
                                  placeholder="Add notes about your decision..."
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
                          {selectedRequest === request.id ? (
                            <>
                              <button
                                onClick={() => handleApprove(request.id)}
                                disabled={processingRequest === request.id || loadingCCRecipients}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                {loadingCCRecipients ? 'Loading...' : processingRequest === request.id ? 'Processing...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => handleReject(request.id)}
                                disabled={processingRequest === request.id}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <XCircleIcon className="h-4 w-4 mr-2" />
                                {processingRequest === request.id ? 'Processing...' : 'Reject'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedRequest(null);
                                  setReviewNotes('');
                                }}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setSelectedRequest(request.id)}
                              className="inline-flex items-center px-4 py-2 border-2 border-orange-400 text-sm font-medium rounded-lg text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                            >
                              <ClockIcon className="h-4 w-4 mr-2" />
                              Review Request
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Section */}
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">Checkout Request Statistics</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={checkoutStatsPeriod}
                      onChange={(e) => {
                        setCheckoutStatsPeriod(parseInt(e.target.value, 10));
                      }}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
                    >
                      <option value={7}>Last 7 Days</option>
                      <option value={30}>Last 30 Days</option>
                      <option value={90}>Last 90 Days</option>
                      <option value={365}>Last Year</option>
                    </select>
                    <button
                      onClick={loadCheckoutStats}
                      disabled={checkoutHistoryLoading}
                      className="inline-flex items-center px-4 py-2 bg-white text-orange-600 rounded-lg hover:bg-orange-50 transition-all disabled:opacity-50 text-sm font-semibold shadow-md hover:shadow-lg transform hover:scale-105"
                    >
                      <svg className={`w-4 h-4 mr-2 ${checkoutHistoryLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
              
              {checkoutHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-orange-200 border-t-orange-600 rounded-full"></div>
                </div>
              ) : checkoutStats ? (
                <div className="p-6 space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 border-2 border-orange-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <UserGroupIcon className="h-8 w-8 text-orange-600 mb-3" />
                      <dt className="text-sm font-medium text-orange-900">Total Requests</dt>
                      <dd className="text-3xl font-bold text-orange-700 mt-2">{checkoutStats.totalRequests}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-6 border-2 border-red-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <UserCircleIcon className="h-8 w-8 text-red-600 mb-3" />
                      <dt className="text-sm font-medium text-red-900">Unique Users</dt>
                      <dd className="text-3xl font-bold text-red-700 mt-2">{checkoutStats.uniqueUsers}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl p-6 border-2 border-pink-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <ChartBarIcon className="h-8 w-8 text-pink-600 mb-3" />
                      <dt className="text-sm font-medium text-pink-900">Avg per User</dt>
                      <dd className="text-3xl font-bold text-pink-700 mt-2">{checkoutStats.avgRequestsPerUser.toFixed(1)}</dd>
                    </div>
                  </div>

                  {/* Top Users */}
                  {checkoutStats.topUsers && checkoutStats.topUsers.length > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-orange-50 rounded-xl p-6 border-2 border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2 text-orange-600" />
                        Top Requesters
                      </h3>
                      <div className="space-y-3">
                        {checkoutStats.topUsers.map((userData, index) => (
                          <div
                            key={userData.user?.id || index}
                            className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300 transform hover:scale-[1.02]"
                          >
                            <div className="flex items-center">
                              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 text-white font-bold text-lg mr-4">
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {userData.user?.firstName} {userData.user?.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{userData.user?.officialEmail}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-orange-700">{userData.count}</div>
                              <div className="text-sm text-gray-500">request{userData.count !== 1 ? 's' : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No checkout data available. Click refresh to load statistics.</p>
                </div>
              )}
            </div>

            {/* History Section */}
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-orange-500 to-red-600 px-6 py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center">
                    <ClockIcon className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">Approved Checkout Request History</h2>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={checkoutHistoryFilters.status || 'approved'}
                      onChange={(e) => setCheckoutHistoryFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
                    >
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="">All</option>
                    </select>
                    <input
                      type="date"
                      value={checkoutHistoryFilters.startDate}
                      onChange={(e) => setCheckoutHistoryFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm placeholder-white/70"
                      style={{ colorScheme: 'dark' }}
                    />
                    <input
                      type="date"
                      value={checkoutHistoryFilters.endDate}
                      onChange={(e) => setCheckoutHistoryFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm placeholder-white/70"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                </div>
              </div>

              <div className="p-6">
                {checkoutHistoryLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-12 w-12 border-4 border-orange-200 border-t-orange-600 rounded-full"></div>
                  </div>
                ) : checkoutHistory.length > 0 ? (
                  <div className="space-y-4">
                    {checkoutHistory.map((request, index) => (
                      <div
                        key={request.id}
                        className="border-2 border-gray-200 rounded-xl p-5 hover:border-orange-400 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-gray-50 transform hover:scale-[1.01]"
                        style={{
                          animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-12 h-12"
                                fallbackClassName="bg-gradient-to-br from-orange-400 to-red-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className={`ml-auto px-3 py-1 text-xs font-semibold rounded-full ${
                                request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-3">
                              <div className="flex items-center">
                                <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.requestDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center">
                                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Checkout Time</div>
                                  <div className="text-sm font-semibold text-orange-700">
                                    {moment(request.requestedCheckoutTime).format('h:mm A')}
                                  </div>
                                </div>
                              </div>
                              {request.reviewedAt && (
                                <div className="flex items-center">
                                  <CheckCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-xs text-gray-500">Reviewed</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {moment(request.reviewedAt).fromNow()}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {request.manager && (
                                <div className="flex items-center">
                                  <UserCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-xs text-gray-500">Approved By</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {`${request.manager.firstName || ''} ${request.manager.lastName || ''}`.trim() || request.manager.officialEmail}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="text-xs font-semibold text-orange-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            {request.reviewerNotes && (
                              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                                <div className="text-xs font-semibold text-red-900 mb-1">Manager Notes</div>
                                <p className="text-sm text-gray-700 italic">{request.reviewerNotes}</p>
                              </div>
                            )}

                            <div className="mt-3 text-xs text-gray-500">
                              Requested {moment(request.createdAt).fromNow()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No checkout requests found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your filters or date range.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Leave Requests Tab */}
        {activeTab === 'leave' && (
          <div className="space-y-6">
            {/* Pending Leave Requests - Top Priority */}
            {pendingLeaves.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50 via-purple-100 to-pink-50 shadow-xl rounded-xl border-2 border-purple-300 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-6 w-6 text-white mr-3 animate-pulse" />
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        Pending Leave Requests
                      </h2>
                      <span className="ml-3 inline-flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full bg-white text-purple-600">
                        {pendingLeaves.length}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="mb-4 flex flex-wrap items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        checked={pendingLeaves.length > 0 && selectedLeaveIds.length === pendingLeaves.length}
                        onChange={toggleSelectAllLeaves}
                      />
                      Select all
                    </label>
                    {selectedLeaveIds.length > 0 && (
                      <>
                        <span className="text-sm text-gray-600">{selectedLeaveIds.length} selected</span>
                        <button
                          onClick={handleBulkApproveLeave}
                          disabled={loadingCCRecipients || processingLeave === 'bulk'}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-md"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          {loadingCCRecipients ? 'Loading...' : processingLeave === 'bulk' ? 'Approving...' : `Approve selected (${selectedLeaveIds.length})`}
                        </button>
                      </>
                    )}
                  </div>
                  <div className="space-y-4">
                    {pendingLeaves.map((request) => (
                      <div
                        key={request.id}
                        className="relative border-2 border-purple-200 rounded-xl p-4 sm:p-6 hover:border-purple-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-purple-50 transform hover:scale-[1.01]"
                      >
                        <div className="flex items-start justify-between">
                          <input
                            type="checkbox"
                            className="mt-1 mr-3 h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 flex-shrink-0"
                            checked={selectedLeaveIds.includes(request.id)}
                            onChange={() => toggleLeaveSelect(request.id)}
                          />
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-10 h-10 sm:w-12 sm:h-12"
                                fallbackClassName="bg-gradient-to-br from-purple-400 to-pink-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className="ml-auto px-3 py-1 text-xs font-semibold rounded-full bg-yellow-200 text-yellow-800 border border-yellow-300 animate-pulse">
                                ⏳ Pending
                              </span>
                              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                                request.leaveType === 'Short Leave' ? 'bg-teal-100 text-teal-800 border border-teal-300' :
                                request.leaveType === 'Half Day' ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                                'bg-purple-200 text-purple-800 border border-purple-300'
                              }`}>
                                {request.leaveType === 'Short Leave' && '⏱️ '}
                                {request.leaveType === 'Half Day' && '🕐 '}
                                {request.leaveType}
                                {request.leaveType === 'Short Leave' && ' (No hour impact)'}
                                {request.leaveType === 'Half Day' && ' (4h credit)'}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div className="flex items-center p-2 bg-purple-50 rounded-lg">
                                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">From Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.startDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center p-2 bg-purple-50 rounded-lg">
                                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">To Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.endDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center p-2 bg-purple-50 rounded-lg sm:col-span-2">
                                <span className="text-xs text-gray-500 mr-2">Duration:</span>
                                <span className="text-sm font-semibold text-purple-700">
                                  {request.totalDays} working day{request.totalDays !== 1 ? 's' : ''}
                                </span>
                              </div>
                              {request.user?.Department && (
                                <div className="flex items-center p-2 bg-purple-50 rounded-lg sm:col-span-2">
                                  <span className="text-xs text-gray-500 mr-2">Department:</span>
                                  <span className="text-sm font-medium text-gray-900">{request.user?.Department}</span>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-purple-100 rounded-lg border border-purple-200">
                                <div className="text-xs font-semibold text-purple-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            <div className="text-xs text-gray-500 mb-3">
                              Submitted {moment(request.createdAt).fromNow()}
                            </div>

                            {selectedLeaveRequest === request.id && (
                              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-purple-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Notes (optional for approval, required for rejection)
                                </label>
                                <textarea
                                  value={leaveReviewNotes}
                                  onChange={(e) => setLeaveReviewNotes(e.target.value)}
                                  rows={3}
                                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500 text-sm"
                                  placeholder="Add notes about your decision..."
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
                          {selectedLeaveRequest === request.id ? (
                            <>
                              <button
                                onClick={() => handleApproveLeave(request.id)}
                                disabled={processingLeave === request.id || loadingCCRecipients}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                {loadingCCRecipients ? 'Loading...' : processingLeave === request.id ? 'Processing...' : 'Approve Leave'}
                              </button>
                              <button
                                onClick={() => handleRejectLeave(request.id)}
                                disabled={processingLeave === request.id}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <XCircleIcon className="h-4 w-4 mr-2" />
                                {processingLeave === request.id ? 'Processing...' : 'Reject Leave'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedLeaveRequest(null);
                                  setLeaveReviewNotes('');
                                }}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setSelectedLeaveRequest(request.id)}
                              className="inline-flex items-center px-4 py-2 border-2 border-purple-400 text-sm font-medium rounded-lg text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                            >
                              <CalendarDaysIcon className="h-4 w-4 mr-2" />
                              Review Request
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Approved Leave Requests */}
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Approved Leave Requests</h3>
                  <p className="text-xs text-gray-500">
                    Last {leaveStatsPeriod} days • Quick actions
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowApprovedLeavesPanel(prev => !prev)}
                    className="inline-flex items-center px-3 py-2 text-xs font-semibold bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                  >
                    {showApprovedLeavesPanel ? 'Hide' : 'Show'}
                  </button>
                  <button
                    onClick={loadLeaveStats}
                    disabled={leaveStatsLoading}
                    className="inline-flex items-center px-3 py-2 text-xs font-semibold bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    {leaveStatsLoading ? 'Loading...' : 'Refresh'}
                  </button>
                </div>
              </div>

              {showApprovedLeavesPanel && (
                <div className="mt-4">
                  {leaveStatsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin h-10 w-10 border-4 border-purple-200 border-t-purple-600 rounded-full"></div>
                    </div>
                  ) : leaveHistory.filter(r => r.status === 'approved').length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500">
                      No approved leave requests in this period.
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                        {leaveHistory
                          .filter(r => r.status === 'approved')
                          .slice(0, showAllApprovedLeaves ? undefined : 5)
                          .map((request) => (
                            <div
                              key={request.id}
                              className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {request.user?.firstName} {request.user?.lastName}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {request.user?.officialEmail}
                                  </span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                  <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
                                    {request.leaveType}
                                  </span>
                                  <span>
                                    {moment(request.startDate).format('MMM DD')} – {moment(request.endDate).format('MMM DD, YYYY')}
                                  </span>
                                  <span>
                                    {request.totalDays} day{request.totalDays !== 1 ? 's' : ''}
                                  </span>
                                  <span className="text-gray-400">
                                    Approved by {request.manager ? `${request.manager.firstName} ${request.manager.lastName}` : '-'}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleCancelApprovedLeave(request, request.user)}
                                  disabled={cancellingApprovedLeaveId === request.id || !isLeaveCancelableWithinWeek(request)}
                                  title={!isLeaveCancelableWithinWeek(request) ? 'Can only cancel within 7 days of approval' : ''}
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                                >
                                  {cancellingApprovedLeaveId === request.id ? 'Cancelling...' : 'Cancel Approved'}
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>

                      {leaveHistory.filter(r => r.status === 'approved').length > 5 && (
                        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                          <span>
                            Showing {showAllApprovedLeaves ? 'all' : '5'} of {leaveHistory.filter(r => r.status === 'approved').length}
                          </span>
                          <button
                            onClick={() => setShowAllApprovedLeaves(prev => !prev)}
                            className="text-blue-600 hover:text-blue-700 font-semibold"
                          >
                            {showAllApprovedLeaves ? 'Show less' : 'View all'}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Leave Statistics Section */}
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Team Leave Analytics</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Track and analyze leave patterns</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <select
                    value={leaveStatsPeriod}
                    onChange={(e) => {
                      setLeaveStatsPeriod(parseInt(e.target.value, 10));
                    }}
                    className="px-2 sm:px-3 py-2 border-2 border-gray-300 rounded-lg text-xs sm:text-sm font-medium focus:ring-2 focus:ring-purple-500"
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={90}>Last 90 Days</option>
                    <option value={365}>Last Year</option>
                  </select>
                  <button
                    onClick={loadLeaveStats}
                    disabled={leaveStatsLoading}
                    className="inline-flex items-center px-3 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-xs sm:text-sm font-medium"
                  >
                    <svg className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${leaveStatsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {leaveStatsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-purple-200 border-t-purple-600 rounded-full"></div>
                </div>
              ) : leaveStats ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
                      <CalendarDaysIcon className="h-8 w-8 text-purple-600 mb-2" />
                      <dt className="text-sm font-medium text-purple-900">Total Requests</dt>
                      <dd className="text-3xl font-bold text-purple-700 mt-2">{leaveStats.total}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
                      <dt className="text-sm font-medium text-green-900">Approved</dt>
                      <dd className="text-3xl font-bold text-green-700 mt-2">{leaveStats.approved}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border-2 border-yellow-200">
                      <ClockIcon className="h-8 w-8 text-yellow-600 mb-2" />
                      <dt className="text-sm font-medium text-yellow-900">Pending</dt>
                      <dd className="text-3xl font-bold text-yellow-700 mt-2">{leaveStats.pending}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border-2 border-red-200">
                      <XCircleIcon className="h-8 w-8 text-red-600 mb-2" />
                      <dt className="text-sm font-medium text-red-900">Rejected</dt>
                      <dd className="text-3xl font-bold text-red-700 mt-2">{leaveStats.rejected}</dd>
                    </div>
                  </div>

                  {/* Leave Type Breakdown */}
                  {Object.keys(leaveStats.byType).length > 0 && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Leave Type Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {Object.entries(leaveStats.byType).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                          <div key={type} className="bg-white rounded-lg p-4 border border-blue-200 hover:shadow-md transition-all">
                            <div className={`text-xs font-semibold mb-1 ${
                              type === 'Short Leave' ? 'text-teal-700' :
                              type === 'Half Day' ? 'text-orange-700' :
                              type === 'Work from Home' ? 'text-green-700' :
                              'text-purple-700'
                            }`}>
                              {type === 'Short Leave' && '⏱️ '}
                              {type === 'Half Day' && '🕐 '}
                              {type}
                            </div>
                            <div className="text-2xl font-bold text-gray-900">{count}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {((count / leaveStats.total) * 100).toFixed(1)}%
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Top Leave Takers */}
                  {Object.keys(leaveStats.byUser).length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-gray-200">
                      <div className="bg-gradient-to-r from-purple-500 to-indigo-600 px-6 py-4">
                        <h4 className="text-lg font-bold text-white">Team Leave Summary</h4>
                        <p className="text-sm text-purple-100">Leave usage by team member</p>
                      </div>
                      <div className="p-6">
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {Object.entries(leaveStats.byUser)
                            .sort((a, b) => b[1].days - a[1].days)
                            .map(([userName, data]) => (
                              <div
                                key={userName}
                                onClick={() => handleOpenLeaveStatsDetails(userName)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    handleOpenLeaveStatsDetails(userName);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                                    {userName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-bold text-gray-900">{userName}</div>
                                    <div className="text-xs text-gray-600">{data.total} requests</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <div className="text-right">
                                    <div className="text-sm font-bold text-purple-700">{data.days.toFixed(1)} days</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {data.approved > 0 && (
                                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                                          ✓ {data.approved}
                                        </span>
                                      )}
                                      {data.pending > 0 && (
                                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded">
                                          ⏳ {data.pending}
                                        </span>
                                      )}
                                      {data.rejected > 0 && (
                                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                                          ✗ {data.rejected}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Timeline Chart */}
                  {leaveStats.timeline && leaveStats.timeline.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Leave Requests Timeline</h4>
                      <div className="overflow-x-auto">
                        <div className="min-w-full inline-flex gap-1">
                          {leaveStats.timeline.map((day, idx) => {
                            const maxCount = Math.max(...leaveStats.timeline.map(d => d.count), 1);
                            const height = day.count > 0 ? (day.count / maxCount) * 100 : 0;
                            const detailEntries = leaveStats.timelineDetails?.[moment(day.date).format('YYYY-MM-DD')] || [];
                            const formatHoverList = (entries) => {
                              return entries.map(entry => {
                                const statusEmoji =
                                  entry.status === 'approved'
                                    ? '✅'
                                    : entry.status === 'pending'
                                    ? '⏳'
                                    : entry.status === 'rejected'
                                    ? '❌'
                                    : '•';
                                const daysText =
                                  entry.totalDays && entry.totalDays > 0
                                    ? ` (${entry.totalDays}d)`
                                    : '';
                                const typeText = entry.type ? ` – ${entry.type}` : '';
                                return `${statusEmoji} ${entry.name}${typeText}${daysText}`;
                              });
                            };
                            const approvedList = formatHoverList(
                              detailEntries.filter(entry => entry.status === 'approved')
                            );
                            const pendingList = formatHoverList(
                              detailEntries.filter(entry => entry.status === 'pending')
                            );
                            const rejectedList = formatHoverList(
                              detailEntries.filter(entry => entry.status === 'rejected')
                            );
                            return (
                              <div key={idx} className="flex-1 min-w-[30px]">
                                <div className="h-32 flex flex-col justify-end items-center">
                                  <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                                    {day.approved > 0 && (
                                      <div 
                                        className="w-full bg-green-400 hover:bg-green-500 transition-colors cursor-pointer"
                                        style={{ height: `${(day.approved / maxCount) * 100}%` }}
                                        title={
                                          approvedList.length > 0
                                            ? `${day.approved} approved:\n${approvedList.join('\n')}`
                                            : `${day.approved} approved`
                                        }
                                      />
                                    )}
                                    {day.pending > 0 && (
                                      <div 
                                        className="w-full bg-yellow-400 hover:bg-yellow-500 transition-colors cursor-pointer"
                                        style={{ height: `${(day.pending / maxCount) * 100}%` }}
                                        title={
                                          pendingList.length > 0
                                            ? `${day.pending} pending:\n${pendingList.join('\n')}`
                                            : `${day.pending} pending`
                                        }
                                      />
                                    )}
                                    {day.rejected > 0 && (
                                      <div 
                                        className="w-full bg-red-400 hover:bg-red-500 transition-colors cursor-pointer"
                                        style={{ height: `${(day.rejected / maxCount) * 100}%` }}
                                        title={
                                          rejectedList.length > 0
                                            ? `${day.rejected} rejected:\n${rejectedList.join('\n')}`
                                            : `${day.rejected} rejected`
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                                <div className="text-center mt-2">
                                  <div className="text-xs font-bold text-gray-700">{day.count || ''}</div>
                                  <div className="text-xs text-gray-500">{moment(day.date).format('MMM D')}</div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-center gap-6 mt-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-green-400 rounded"></div>
                          <span className="text-gray-700">Approved</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-yellow-400 rounded"></div>
                          <span className="text-gray-700">Pending</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 bg-red-400 rounded"></div>
                          <span className="text-gray-700">Rejected</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <CalendarDaysIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No leave data available. Click refresh to load statistics.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Extra Hours Requests Tab */}
        {activeTab === 'extra-hours' && (
          <div className="space-y-6">
            {/* Pending Extra Hours Requests - Top Priority */}
            {pendingExtraHours.length > 0 && (
              <div className="bg-gradient-to-br from-cyan-50 via-cyan-100 to-blue-50 shadow-xl rounded-xl border-2 border-cyan-300 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ClockIcon className="h-6 w-6 text-white mr-3 animate-pulse" />
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        Pending Extra Hours Requests
                      </h2>
                      <span className="ml-3 inline-flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full bg-white text-cyan-600">
                        {pendingExtraHours.length}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    {pendingExtraHours.map((request) => (
                      <div
                        key={request.id}
                        className="relative border-2 border-cyan-200 rounded-xl p-4 sm:p-6 hover:border-cyan-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-cyan-50 transform hover:scale-[1.01]"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-10 h-10 sm:w-12 sm:h-12"
                                fallbackClassName="bg-gradient-to-br from-cyan-400 to-blue-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className="ml-auto px-3 py-1 text-xs font-semibold rounded-full bg-yellow-200 text-yellow-800 border border-yellow-300 animate-pulse">
                                ⏳ Pending
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div className="flex items-center p-2 bg-cyan-50 rounded-lg">
                                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.requestDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center p-2 bg-cyan-50 rounded-lg">
                                <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Requested Hours</div>
                                  <div className="text-sm font-semibold text-cyan-700">
                                    {request.requestedHours} hour{request.requestedHours !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                              {request.startTime && request.endTime && (
                                <div className="flex items-center p-2 bg-cyan-50 rounded-lg sm:col-span-2">
                                  <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-600 mr-2" />
                                  <div>
                                    <div className="text-xs text-gray-500">Time Range</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {moment(request.startTime).format('h:mm A')} - {moment(request.endTime).format('h:mm A')}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {request.user?.Department && (
                                <div className="flex items-center p-2 bg-cyan-50 rounded-lg sm:col-span-2">
                                  <span className="text-xs text-gray-500 mr-2">Department:</span>
                                  <span className="text-sm font-medium text-gray-900">{request.user?.Department}</span>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-cyan-100 rounded-lg border border-cyan-200">
                                <div className="text-xs font-semibold text-cyan-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            <div className="text-xs text-gray-500 mb-3">
                              Submitted {moment(request.createdAt).fromNow()}
                            </div>

                            {selectedExtraHoursRequest === request.id ? (
                              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-cyan-200 space-y-4">
                                {/* Partial Approval Input */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Approve Hours (defaults to requested: {request.requestedHours}h)
                                  </label>
                                  <input
                                    type="number"
                                    min="0.5"
                                    step="0.5"
                                    max={request.requestedHours}
                                    value={extraHoursApprovedHours !== null ? extraHoursApprovedHours : request.requestedHours}
                                    onChange={(e) => {
                                      const value = parseFloat(e.target.value);
                                      if (!isNaN(value) && value >= 0.5 && value <= request.requestedHours) {
                                        setExtraHoursApprovedHours(value);
                                      } else if (e.target.value === '') {
                                        setExtraHoursApprovedHours(null);
                                      }
                                    }}
                                    className="w-full border-2 border-cyan-300 rounded-lg px-3 py-2 text-sm focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder={`Max: ${request.requestedHours} hours`}
                                  />
                                  <p className="mt-1 text-xs text-gray-500">
                                    You can approve fewer hours than requested (partial approval)
                                  </p>
                                </div>

                                {/* Notes */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes (Optional)
                                  </label>
                                  <textarea
                                    value={extraHoursReviewNotes}
                                    onChange={(e) => setExtraHoursReviewNotes(e.target.value)}
                                    rows={3}
                                    className="w-full border-2 border-cyan-300 rounded-lg px-3 py-2 text-sm focus:ring-cyan-500 focus:border-cyan-500"
                                    placeholder="Add any notes for the employee..."
                                  />
                                </div>

                                <div className="flex flex-wrap gap-3">
                                  <button
                                    onClick={() => handleApproveExtraHours(request.id)}
                                    disabled={processingExtraHours === request.id || loadingCCRecipients}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                                  >
                                    <CheckCircleIcon className="h-4 w-4 mr-2" />
                                    {loadingCCRecipients ? 'Loading...' : processingExtraHours === request.id ? 'Processing...' : 'Approve'}
                                  </button>
                                  <button
                                    onClick={() => handleRejectExtraHours(request.id)}
                                    disabled={processingExtraHours === request.id}
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                                  >
                                    <XCircleIcon className="h-4 w-4 mr-2" />
                                    {processingExtraHours === request.id ? 'Processing...' : 'Reject'}
                                  </button>
                                  <button
                                    onClick={() => {
                                      setSelectedExtraHoursRequest(null);
                                      setExtraHoursReviewNotes('');
                                      setExtraHoursApprovedHours(null);
                                    }}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="mt-4">
                                <button
                                  onClick={() => {
                                    setSelectedExtraHoursRequest(request.id);
                                    setExtraHoursApprovedHours(null);
                                    setExtraHoursReviewNotes('');
                                  }}
                                  className="inline-flex items-center px-4 py-2 border-2 border-cyan-400 text-sm font-medium rounded-lg text-cyan-700 bg-cyan-50 hover:bg-cyan-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                                >
                                  <ClockIcon className="h-4 w-4 mr-2" />
                                  Review Request
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Statistics Section */}
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 px-6 py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center">
                    <ChartBarIcon className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">Extra Hours Statistics</h2>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={extraHoursStatsPeriod}
                      onChange={(e) => {
                        setExtraHoursStatsPeriod(parseInt(e.target.value, 10));
                      }}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
                    >
                      <option value={7}>Last 7 Days</option>
                      <option value={30}>Last 30 Days</option>
                      <option value={90}>Last 90 Days</option>
                      <option value={365}>Last Year</option>
                    </select>
                    <button
                      onClick={loadExtraHoursStats}
                      disabled={extraHoursHistoryLoading}
                      className="inline-flex items-center px-4 py-2 bg-white text-cyan-600 rounded-lg hover:bg-cyan-50 transition-all disabled:opacity-50 text-sm font-semibold shadow-md hover:shadow-lg transform hover:scale-105"
                    >
                      <svg className={`w-4 h-4 mr-2 ${extraHoursHistoryLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
              
              {extraHoursHistoryLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-cyan-200 border-t-cyan-600 rounded-full"></div>
                </div>
              ) : extraHoursStats ? (
                <div className="p-6 space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 border-2 border-cyan-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <ClockIcon className="h-8 w-8 text-cyan-600 mb-3" />
                      <dt className="text-sm font-medium text-cyan-900">Total Approved Hours</dt>
                      <dd className="text-3xl font-bold text-cyan-700 mt-2">{extraHoursStats.totalApprovedHours.toFixed(1)}h</dd>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <UserGroupIcon className="h-8 w-8 text-blue-600 mb-3" />
                      <dt className="text-sm font-medium text-blue-900">Total Requests</dt>
                      <dd className="text-3xl font-bold text-blue-700 mt-2">{extraHoursStats.totalRequests}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <ChartBarIcon className="h-8 w-8 text-indigo-600 mb-3" />
                      <dt className="text-sm font-medium text-indigo-900">Avg per Request</dt>
                      <dd className="text-3xl font-bold text-indigo-700 mt-2">{extraHoursStats.avgHoursPerRequest.toFixed(1)}h</dd>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200 transform hover:scale-105 transition-all duration-300 shadow-md hover:shadow-xl">
                      <UserCircleIcon className="h-8 w-8 text-purple-600 mb-3" />
                      <dt className="text-sm font-medium text-purple-900">Unique Users</dt>
                      <dd className="text-3xl font-bold text-purple-700 mt-2">{extraHoursStats.uniqueUsers}</dd>
                    </div>
                  </div>

                  {/* Top Users */}
                  {extraHoursStats.topUsers && extraHoursStats.topUsers.length > 0 && (
                    <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-6 border-2 border-gray-200">
                      <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                        <UserGroupIcon className="h-5 w-5 mr-2 text-cyan-600" />
                        Top Contributors
                      </h3>
                      <div className="space-y-3">
                        {extraHoursStats.topUsers.map((userData, index) => (
                          <div
                            key={userData.user?.id || index}
                            className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all duration-300 transform hover:scale-[1.02]"
                          >
                            <div className="flex items-center">
                              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 text-white font-bold text-lg mr-4">
                                {index + 1}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {userData.user?.firstName} {userData.user?.lastName}
                                </div>
                                <div className="text-sm text-gray-500">{userData.user?.officialEmail}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-cyan-700">{userData.totalApproved.toFixed(1)}h</div>
                              <div className="text-sm text-gray-500">{userData.count} request{userData.count !== 1 ? 's' : ''}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No extra hours data available. Click refresh to load statistics.</p>
                </div>
              )}
            </div>

            {/* History Section */}
            <div className="bg-white shadow-lg rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-600 px-6 py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center">
                    <ClockIcon className="h-6 w-6 text-white mr-3" />
                    <h2 className="text-xl font-bold text-white">Approved Extra Hours History</h2>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={extraHoursHistoryFilters.status || 'approved'}
                      onChange={(e) => setExtraHoursHistoryFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm"
                    >
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="">All</option>
                    </select>
                    <input
                      type="date"
                      value={extraHoursHistoryFilters.startDate}
                      onChange={(e) => setExtraHoursHistoryFilters(prev => ({ ...prev, startDate: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm placeholder-white/70"
                      style={{ colorScheme: 'dark' }}
                    />
                    <input
                      type="date"
                      value={extraHoursHistoryFilters.endDate}
                      onChange={(e) => setExtraHoursHistoryFilters(prev => ({ ...prev, endDate: e.target.value, page: 1 }))}
                      className="px-3 py-2 border-2 border-white/30 bg-white/20 text-white rounded-lg text-sm font-medium focus:ring-2 focus:ring-white/50 backdrop-blur-sm placeholder-white/70"
                      style={{ colorScheme: 'dark' }}
                    />
                  </div>
                </div>
              </div>

              <div className="p-6">
                {extraHoursHistoryLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-12 w-12 border-4 border-blue-200 border-t-blue-600 rounded-full"></div>
                  </div>
                ) : extraHoursHistory.length > 0 ? (
                  <div className="space-y-4">
                    {extraHoursHistory.map((request, index) => (
                      <div
                        key={request.id}
                        className="border-2 border-gray-200 rounded-xl p-5 hover:border-cyan-400 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-white to-gray-50 transform hover:scale-[1.01]"
                        style={{
                          animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-12 h-12"
                                fallbackClassName="bg-gradient-to-br from-cyan-400 to-blue-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className={`ml-auto px-3 py-1 text-xs font-semibold rounded-full ${
                                request.status === 'approved' ? 'bg-green-100 text-green-800' :
                                request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div className="flex items-center">
                                <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.requestDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center">
                                <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Hours</div>
                                  <div className="text-sm font-semibold text-cyan-700">
                                    {request.approvedHours !== null ? (
                                      <>
                                        {request.approvedHours}h approved
                                        {request.approvedHours < request.requestedHours && (
                                          <span className="text-gray-500 ml-1">({request.requestedHours}h requested)</span>
                                        )}
                                      </>
                                    ) : (
                                      `${request.requestedHours}h requested`
                                    )}
                                  </div>
                                </div>
                              </div>
                              {request.reviewedAt && (
                                <div className="flex items-center">
                                  <CheckCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                                  <div>
                                    <div className="text-xs text-gray-500">Reviewed</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {moment(request.reviewedAt).fromNow()}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-cyan-50 rounded-lg border border-cyan-200">
                                <div className="text-xs font-semibold text-cyan-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            {request.reviewerNotes && (
                              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <div className="text-xs font-semibold text-blue-900 mb-1">Manager Notes</div>
                                <p className="text-sm text-gray-700 italic">{request.reviewerNotes}</p>
                              </div>
                            )}

                            <div className="mt-3 text-xs text-gray-500">
                              Requested {moment(request.createdAt).fromNow()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No extra hours requests found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Try adjusting your filters or date range.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Attendance Corrections Tab */}
        {activeTab === 'corrections' && (
          <div className="space-y-6">
            {pendingCorrections.length > 0 ? (
              <div className="bg-gradient-to-br from-red-50 via-orange-100 to-amber-50 shadow-xl rounded-xl border-2 border-orange-300 overflow-hidden">
                <div className="bg-gradient-to-r from-red-500 to-orange-600 px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <ExclamationTriangleIcon className="h-6 w-6 text-white mr-3 animate-pulse" />
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        Pending Attendance Corrections
                      </h2>
                      <span className="ml-3 inline-flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full bg-white text-orange-600">
                        {pendingCorrections.length}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 sm:p-6">
                  <div className="space-y-4">
                    {pendingCorrections.map((request) => (
                      <div
                        key={request.id}
                        className="relative border-2 border-orange-200 rounded-xl p-4 sm:p-6 hover:border-orange-400 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-orange-50 transform hover:scale-[1.01]"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <TeamMemberAvatar
                                member={request.user}
                                className="w-10 h-10 sm:w-12 sm:h-12"
                                fallbackClassName="bg-gradient-to-br from-orange-400 to-red-500 text-white"
                                textClassName="font-bold text-lg"
                              />
                              <div>
                                <h4 className="text-base sm:text-lg font-semibold text-gray-900">
                                  {request.user?.firstName} {request.user?.lastName}
                                </h4>
                                <p className="text-xs sm:text-sm text-gray-500">{request.user?.officialEmail}</p>
                              </div>
                              <span className="ml-auto px-3 py-1 text-xs font-semibold rounded-full bg-yellow-200 text-yellow-800 border border-yellow-300 animate-pulse">
                                ⏳ Pending
                              </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                              <div className="flex items-center p-2 bg-orange-50 rounded-lg">
                                <CalendarDaysIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mr-2" />
                                <div>
                                  <div className="text-xs text-gray-500">Date</div>
                                  <div className="text-sm font-semibold text-gray-900">
                                    {moment(request.requestDate).format('MMM DD, YYYY')}
                                  </div>
                                </div>
                              </div>
                              {(request.requestedCheckInTime || request.requestedCheckOutTime) && (
                                <div className="flex items-center p-2 bg-orange-50 rounded-lg">
                                  <ClockIcon className="h-4 w-4 sm:h-5 sm:w-5 text-orange-600 mr-2" />
                                  <div>
                                    <div className="text-xs text-gray-500">Suggested Times</div>
                                    <div className="text-sm font-semibold text-gray-900">
                                      {request.requestedCheckInTime ? moment(request.requestedCheckInTime).format('h:mm A') : '—'} - {request.requestedCheckOutTime ? moment(request.requestedCheckOutTime).format('h:mm A') : '—'}
                                    </div>
                                  </div>
                                </div>
                              )}
                              {request.user?.Department && (
                                <div className="flex items-center p-2 bg-orange-50 rounded-lg sm:col-span-2">
                                  <span className="text-xs text-gray-500 mr-2">Department:</span>
                                  <span className="text-sm font-medium text-gray-900">{request.user?.Department}</span>
                                </div>
                              )}
                            </div>

                            {request.reason && (
                              <div className="mb-3 p-3 bg-orange-100 rounded-lg border border-orange-200">
                                <div className="text-xs font-semibold text-orange-900 mb-1">Reason</div>
                                <p className="text-sm text-gray-700">{request.reason}</p>
                              </div>
                            )}

                            <div className="text-xs text-gray-500 mb-3">
                              Submitted {moment(request.createdAt).fromNow()}
                            </div>

                            {selectedCorrectionRequest?.id === request.id && (
                              <div className="mt-4 p-4 bg-white rounded-lg border-2 border-orange-200">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Rejection Notes (required to reject)
                                </label>
                                <textarea
                                  value={correctionReviewNotes}
                                  onChange={(e) => setCorrectionReviewNotes(e.target.value)}
                                  rows={3}
                                  className="w-full border-gray-300 rounded-md shadow-sm focus:ring-orange-500 focus:border-orange-500 text-sm"
                                  placeholder="Add rejection notes..."
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 flex flex-wrap gap-2 sm:gap-3">
                          {selectedCorrectionRequest?.id === request.id ? (
                            <>
                              <button
                                onClick={() => handleOpenAttendanceCorrection(request.user, request.requestDate, request)}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-2" />
                                Correct Attendance
                              </button>
                              <button
                                onClick={() => handleRejectCorrectionRequest(request.id)}
                                disabled={processingCorrection === request.id}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                              >
                                <XCircleIcon className="h-4 w-4 mr-2" />
                                {processingCorrection === request.id ? 'Processing...' : 'Reject'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedCorrectionRequest(null);
                                  setCorrectionReviewNotes('');
                                }}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 shadow-sm"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedCorrectionRequest(request);
                                setCorrectionReviewNotes('');
                              }}
                              className="inline-flex items-center px-4 py-2 border-2 border-orange-400 text-sm font-medium rounded-lg text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                            >
                              <ExclamationTriangleIcon className="h-4 w-4 mr-2" />
                              Review Request
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Correction Statistics */}
            <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Correction Analytics</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Attendance correction trends for your team</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <select
                    value={correctionStatsPeriod}
                    onChange={(e) => {
                      setCorrectionStatsPeriod(parseInt(e.target.value, 10));
                    }}
                    className="px-2 sm:px-3 py-2 border-2 border-gray-300 rounded-lg text-xs sm:text-sm font-medium focus:ring-2 focus:ring-orange-500"
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={30}>Last 30 Days</option>
                    <option value={90}>Last 90 Days</option>
                    <option value={365}>Last Year</option>
                  </select>
                  <button
                    onClick={loadCorrectionStats}
                    disabled={correctionStatsLoading}
                    className="inline-flex items-center px-3 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 text-xs sm:text-sm font-medium"
                  >
                    <svg className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${correctionStatsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {correctionStatsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin h-12 w-12 border-4 border-orange-200 border-t-orange-600 rounded-full"></div>
                </div>
              ) : correctionStats ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
                      <ExclamationTriangleIcon className="h-8 w-8 text-orange-600 mb-2" />
                      <dt className="text-sm font-medium text-orange-900">Total Requests</dt>
                      <dd className="text-3xl font-bold text-orange-700 mt-2">{correctionStats.total}</dd>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 mb-2" />
                      <dt className="text-sm font-medium text-green-900">Approved</dt>
                      <dd className="text-3xl font-bold text-green-700 mt-2">{correctionStats.approved}</dd>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border-2 border-yellow-200">
                      <ClockIcon className="h-8 w-8 text-yellow-600 mb-2" />
                      <dt className="text-sm font-medium text-yellow-900">Pending</dt>
                      <dd className="text-3xl font-bold text-yellow-700 mt-2">{correctionStats.pending}</dd>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border-2 border-red-200">
                      <XCircleIcon className="h-8 w-8 text-red-600 mb-2" />
                      <dt className="text-sm font-medium text-red-900">Rejected</dt>
                      <dd className="text-3xl font-bold text-red-700 mt-2">{correctionStats.rejected}</dd>
                    </div>
                  </div>

                  {Object.keys(correctionStats.byUser).length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-gray-200">
                      <div className="bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-4">
                        <h4 className="text-lg font-bold text-white">Corrections by Team Member</h4>
                        <p className="text-sm text-orange-100">Request volume by user</p>
                      </div>
                      <div className="p-6">
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {Object.entries(correctionStats.byUser)
                            .sort((a, b) => b[1].total - a[1].total)
                            .map(([userName, data]) => (
                              <button
                                key={userName}
                                type="button"
                                onClick={() => handleOpenCorrectionStatsDetails(userName)}
                                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg text-left transition-all duration-200 hover:bg-orange-50 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                              >
                                <div className="flex items-center gap-3 flex-1">
                                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-sm">
                                    {userName.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="text-sm font-bold text-gray-900">{userName}</div>
                                    <div className="text-xs text-gray-600">{data.total} requests</div>
                                    <div className="text-[11px] text-orange-600 font-medium mt-1">Click to view request details</div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {data.approved > 0 && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded">
                                      ✓ {data.approved}
                                    </span>
                                  )}
                                  {data.pending > 0 && (
                                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-bold rounded">
                                      ⏳ {data.pending}
                                    </span>
                                  )}
                                  {data.rejected > 0 && (
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">
                                      ✗ {data.rejected}
                                    </span>
                                  )}
                                </div>
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {correctionStats.timeline && correctionStats.timeline.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                      <h4 className="text-lg font-bold text-gray-900 mb-4">Correction Requests Timeline</h4>
                      <div className="overflow-x-auto">
                        <div className="min-w-full inline-flex gap-1">
                          {correctionStats.timeline.map((day, idx) => {
                            const maxCount = Math.max(...correctionStats.timeline.map(d => d.count), 1);
                            return (
                              <div key={idx} className="flex-1 min-w-[30px]">
                                <div className="h-28 flex flex-col justify-end items-center">
                                  <div className="w-full flex flex-col justify-end" style={{ height: '100%' }}>
                                    {day.approved > 0 && (
                                      <div
                                        className="w-full bg-green-400"
                                        style={{ height: `${(day.approved / maxCount) * 100}%` }}
                                        title={`${day.approved} approved`}
                                      />
                                    )}
                                    {day.pending > 0 && (
                                      <div
                                        className="w-full bg-yellow-400"
                                        style={{ height: `${(day.pending / maxCount) * 100}%` }}
                                        title={`${day.pending} pending`}
                                      />
                                    )}
                                    {day.rejected > 0 && (
                                      <div
                                        className="w-full bg-red-400"
                                        style={{ height: `${(day.rejected / maxCount) * 100}%` }}
                                        title={`${day.rejected} rejected`}
                                      />
                                    )}
                                  </div>
                                  <div className="text-[10px] text-gray-500 mt-1">
                                    {moment(day.date).format('MM/DD')}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-500">No correction stats available. Click refresh to load.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Team Attendance Tab */}
        {activeTab === 'team' && (
          <div className="bg-white shadow rounded-lg">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
                <h3 className="text-base sm:text-lg font-medium text-gray-900">
                  Team Member Attendance
                </h3>
                
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="relative w-full sm:w-72">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={attendanceSearchQuery}
                      onChange={(event) => setAttendanceSearchQuery(event.target.value)}
                      placeholder="Search team member"
                      aria-label="Search team member"
                      className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    />
                    {attendanceSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setAttendanceSearchQuery('')}
                        aria-label="Clear team member search"
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* View Toggle Buttons */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => handleAttendanceViewModeChange('list')}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        attendanceViewMode === 'list'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Squares2X2Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">List View</span>
                    </button>
                    <button
                      onClick={() => handleAttendanceViewModeChange('calendar')}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                        attendanceViewMode === 'calendar'
                          ? 'bg-white text-blue-600 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <CalendarDaysIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Calendar View</span>
                    </button>
                  </div>
                </div>
              </div>

              <ExcludedMembersSection excludedMembers={excludedMembers} />

              {/* Date Range Selector */}
              <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Date Range for Team Attendance
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center space-x-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">From</label>
                      <DatePicker
                        selected={startDate}
                        onChange={(date) => setStartDate(date)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate}
                        className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2"
                        dateFormat="MMM dd, yyyy"
                      />
                    </div>
                    <span className="text-gray-500 pt-5">to</span>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">To</label>
                      <DatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        maxDate={new Date()}
                        className="border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm px-3 py-2"
                        dateFormat="MMM dd, yyyy"
                      />
                    </div>
                  </div>
                  
                  {/* Quick Select Buttons */}
                  <div className="flex gap-2 ml-auto">
                    <button
                      onClick={() => {
                        setStartDate(moment().startOf('week').toDate());
                        setEndDate(moment().endOf('week').toDate());
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      This Week
                    </button>
                    <button
                      onClick={() => {
                        setStartDate(moment().startOf('month').toDate());
                        setEndDate(moment().endOf('month').toDate());
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      This Month
                    </button>
                    <button
                      onClick={() => {
                        setStartDate(moment().subtract(1, 'month').startOf('month').toDate());
                        setEndDate(moment().subtract(1, 'month').endOf('month').toDate());
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      Last Month
                    </button>
                    <button
                      onClick={() => {
                        setStartDate(moment().startOf('year').toDate());
                        setEndDate(moment().endOf('year').toDate());
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      This Year
                    </button>
                  </div>
                </div>
                
              <div className="mt-2 text-xs text-gray-500">
                Viewing attendance from {moment(startDate).format('MMM DD, YYYY')} to {moment(endDate).format('MMM DD, YYYY')}
              </div>
            </div>

            <CustomSummaryEmailSection
              summaryEmailForm={summaryEmailForm}
              showSummaryAdvanced={showSummaryAdvanced}
              sendingSummaryEmail={sendingSummaryEmail}
              summaryEmailResult={summaryEmailResult}
              onSummaryFieldChange={handleSummaryFieldChange}
              onToggleAdvanced={() => setShowSummaryAdvanced((prev) => !prev)}
              onSend={handleOpenEmailPreview}
            />
              
            <div ref={attendanceResultsRef} className="scroll-mt-24">
              {teamAttendanceLoading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
                  <p className="mt-4 text-sm text-gray-600">Loading team attendance data...</p>
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-12">
                  <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No team members</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You don't have any team members assigned to you.
                  </p>
                </div>
              ) : filteredTeamMembers.length === 0 ? (
                <div className="text-center py-12">
                  <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No matching team members</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try a different name or email search.
                  </p>
                </div>
              ) : attendanceViewMode === 'calendar' ? (
                // Calendar View
                (() => {
                  const dataByDate = organizeDataByDate(filteredTeamMembers, startDate, endDate);
                  const start = moment(startDate).startOf('day');
                  const end = moment(endDate).startOf('day');
                  const today = moment().format('YYYY-MM-DD');

                  // Group dates by week (Monday to Sunday)
                  const weeks = [];
                  // Start from the Monday of the week containing the start date
                  const weekStart = start.clone().startOf('isoWeek');
                  // End at the Sunday of the week containing the end date
                  const weekEnd = end.clone().endOf('isoWeek');

                  let currentDate = weekStart.clone();
                  
                  while (currentDate.isSameOrBefore(weekEnd, 'day')) {
                    const week = [];
                    for (let i = 0; i < 7; i++) {
                      week.push(currentDate.clone());
                      currentDate.add(1, 'day');
                    }
                    weeks.push(week);
                  }

                  return (
                    <div className="space-y-4">
                      {/* Calendar Navigation */}
                      <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              const daysDiff = moment(endDate).diff(moment(startDate), 'days');
                              const newStart = moment(startDate).subtract(daysDiff + 1, 'days');
                              const newEnd = moment(startDate).subtract(1, 'days');
                              setStartDate(newStart.toDate());
                              setEndDate(newEnd.toDate());
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <ChevronLeftIcon className="w-4 h-4" />
                            <span className="hidden sm:inline">Previous</span>
                          </button>
                          <button
                            onClick={() => {
                              const daysDiff = moment(endDate).diff(moment(startDate), 'days');
                              const newStart = moment(endDate).add(1, 'days');
                              const newEnd = moment(newStart).add(daysDiff, 'days');
                              if (newEnd.isSameOrBefore(moment())) {
                                setStartDate(newStart.toDate());
                                setEndDate(newEnd.toDate());
                              }
                            }}
                            disabled={moment(endDate).add(1, 'day').isAfter(moment())}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRightIcon className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="text-sm font-semibold text-gray-900 text-center">
                          {moment(startDate).format('MMM DD, YYYY')} - {moment(endDate).format('MMM DD, YYYY')}
                        </div>
                        <button
                          onClick={() => {
                            const weekStart = moment().startOf('isoWeek');
                            const weekEnd = moment().endOf('isoWeek');
                            setStartDate(weekStart.toDate());
                            setEndDate(weekEnd.toDate());
                          }}
                          className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-300 rounded-md hover:bg-blue-100 transition-colors whitespace-nowrap"
                        >
                          Today
                        </button>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Legend
                        </span>
                        {CALENDAR_ATTENDANCE_LEGEND.map(({ code, label }) => (
                          <span
                            key={code}
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${CALENDAR_ATTENDANCE_STYLES[code]?.badgeClassName || 'bg-gray-100 text-gray-700'}`}
                          >
                            <span className={`font-semibold ${CALENDAR_ATTENDANCE_STYLES[code]?.emphasisClassName || 'text-gray-900'}`}>{code}</span>
                            <span>=</span>
                            <span>{label}</span>
                          </span>
                        ))}
                      </div>

                      {/* Calendar Grid */}
                      <div className="border-2 border-gray-200 rounded-xl overflow-hidden overflow-x-auto">
                        {/* Week Header */}
                        <div className="grid grid-cols-7 bg-gray-100 border-b-2 border-gray-300 min-w-[700px]">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                            <div key={day} className="px-2 py-2 text-center text-xs font-bold text-gray-700 uppercase border-r border-gray-300 last:border-r-0">
                              {day}
                            </div>
                          ))}
                        </div>

                        {/* Calendar Weeks */}
                        <div className="min-w-[700px]">
                          {weeks.map((week, weekIndex) => (
                            <div key={weekIndex} className="grid grid-cols-7 border-b border-gray-200 last:border-b-0">
                              {week.map((date) => {
                                const dateStr = date.format('YYYY-MM-DD');
                                const isToday = dateStr === today;
                                const isWeekend = date.day() === 0 || date.day() === 6;
                                const isInRange = date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
                                // Only show members for dates within the selected range
                                const membersForDate = isInRange ? (dataByDate[dateStr] || []) : [];
                                const attendanceCounts = buildCalendarAttendanceCounts(membersForDate);

                                return (
                                  <div
                                    key={dateStr}
                                    className={`min-h-[180px] sm:min-h-[200px] border-r border-gray-200 last:border-r-0 p-1 sm:p-2 ${
                                      isWeekend ? 'bg-gray-50' : 'bg-white'
                                    } ${!isInRange ? 'opacity-40' : ''} ${isToday ? 'ring-2 ring-blue-500 ring-inset bg-blue-50/30' : ''}`}
                                  >
                                    {/* Date Header */}
                                    <div className={`mb-1 sm:mb-2 pb-1 sm:pb-2 border-b border-gray-200 ${
                                      isToday ? 'bg-blue-100 rounded px-1 py-0.5 sm:py-1' : ''
                                    }`}>
                                      <div className={`text-xs sm:text-sm font-bold ${isToday ? 'text-blue-700' : 'text-gray-700'}`}>
                                        {date.format('D')}
                                      </div>
                                      <div className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5">
                                        {date.format('MMM')}
                                      </div>
                                    </div>

                                    {isInRange && membersForDate.length > 0 && (
                                      <div className="mb-1.5 flex flex-wrap gap-1 border-b border-dashed border-gray-200 pb-1.5">
                                        {attendanceCounts.map(({ code, count }) => (
                                          <span
                                            key={code}
                                            className={`rounded px-1.5 py-0.5 text-[8px] font-semibold sm:text-[9px] ${CALENDAR_ATTENDANCE_STYLES[code]?.badgeClassName || 'bg-gray-100 text-gray-700'}`}
                                          >
                                            {code} - {count}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Team Members for this date */}
                                    <div className="space-y-0.5 sm:space-y-1 max-h-[130px] sm:max-h-[150px] overflow-y-auto">
                                      {membersForDate.length === 0 ? (
                                        <div className="text-center py-2 sm:py-4">
                                          <p className="text-[9px] sm:text-[10px] text-gray-400">No activity</p>
                                        </div>
                                      ) : (
                                        membersForDate.map((entry, idx) => {
                                          const { member, hours, status, leave, tasks } = entry;
                                          return (
                                            <div
                                              key={`${member.id}-${idx}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenAttendanceCorrection(member, dateStr);
                                              }}
                                              className="cursor-pointer p-0.5 sm:p-1 bg-white rounded border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all group"
                                              title={`Click to correct attendance - ${member.firstName} ${member.lastName}${hours > 0 ? ' - ' + hours.toFixed(1) + 'h' : ''}${leave ? ' - On Leave' : status === 'not-checked-in' ? ' - Not checked in' : ''}${tasks > 0 ? ' - ' + tasks + ' task(s)' : ''}`}
                                            >
                                              <div className="flex items-center gap-1 mb-0.5">
                                                <TeamMemberAvatar
                                                  member={member}
                                                  className="w-4 h-4 sm:w-5 sm:h-5"
                                                  textClassName={`font-medium text-[8px] sm:text-[9px] ${
                                                    status === 'checked-in'
                                                      ? 'text-green-700'
                                                      : status === 'work-from-home'
                                                      ? 'text-emerald-700'
                                                      : status === 'on-leave'
                                                      ? 'text-yellow-700'
                                                      : 'text-red-700'
                                                  }`}
                                                  fallbackClassName={
                                                    status === 'checked-in'
                                                      ? 'bg-green-100'
                                                      : status === 'work-from-home'
                                                      ? 'bg-emerald-100'
                                                      : status === 'on-leave'
                                                      ? 'bg-yellow-100'
                                                      : 'bg-red-100'
                                                  }
                                                />
                                                <div className="flex-1 min-w-0">
                                                  <div className="text-[9px] sm:text-[10px] font-semibold text-gray-900 truncate">
                                                    {member.firstName} {member.lastName}
                                                  </div>
                                                </div>
                                              </div>
                                              <div className="flex items-center gap-0.5 sm:gap-1 flex-wrap ml-5 sm:ml-6">
                                                {hours > 0 && (
                                                  <span className="text-[8px] sm:text-[9px] font-semibold text-blue-700">
                                                    {hours.toFixed(1)}h
                                                  </span>
                                                )}
                                                {leave && (
                                                  <span className={`text-[8px] sm:text-[9px] px-0.5 sm:px-1 py-0.5 rounded ${
                                                    leave.leaveType === 'Work from Home'
                                                      ? 'bg-emerald-100 text-emerald-800'
                                                      : 'bg-yellow-100 text-yellow-800'
                                                  }`}>
                                                    {leave.leaveType === 'Work from Home' ? 'W' : 'L'}
                                                  </span>
                                                )}
                                                {tasks > 0 && (
                                                  <span className="text-[8px] sm:text-[9px] text-gray-600">
                                                    {tasks}⌾
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Scroll Indicator for Mobile */}
                      <div className="mt-2 text-center sm:hidden">
                        <p className="text-xs text-gray-500 flex items-center justify-center gap-2">
                          <ChevronLeftIcon className="w-4 h-4" />
                          Scroll horizontally to view all dates
                          <ChevronRightIcon className="w-4 h-4" />
                        </p>
                      </div>
                    </div>
                  );
                })()
              ) : (
                // List View (existing)
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Employee
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Today
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          This Week
                        </th>
                        <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total Hours
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Avg/Day
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Working Days
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Leaves
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ClickUp History
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTeamMembers.map((member) => {
                  // Calculate total hours including extra hours (hoursWorked + extraHours)
                  const totalHours = member.dailyHours?.reduce((sum, day) => {
                    const regularHours = day.hoursWorked || 0;
                    const extraHours = day.extraHours || 0;
                    return sum + regularHours + extraHours;
                  }, 0) || 0;
                  const workingDays = member.dailyHours?.length || 0;
                  const avgHours = workingDays > 0 ? totalHours / workingDays : 0;
                  const isActiveToday = member.dailyHours?.some(d => 
                    moment(d.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
                  );
                  const todayDay = member.dailyHours?.find(d => 
                    moment(d.date).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')
                  );
                  const todayHours = (todayDay?.hoursWorked || 0) + (todayDay?.extraHours || 0);
                  
                  // Check if employee is on leave today
                  const todayStr = moment().format('YYYY-MM-DD');
                  const isOnLeaveToday = member.leaveRequests?.some(leave => {
                    if (leave.status !== 'approved') return false;
                    const startDate = moment(leave.startDate).format('YYYY-MM-DD');
                    const endDate = moment(leave.endDate).format('YYYY-MM-DD');
                    return todayStr >= startDate && todayStr <= endDate;
                  }) || false;
                  
                  // Determine attendance status for circle color
                  // Priority: Leave > Checked In > Not Checked In
                  let attendanceStatus = 'not-checked-in'; // default: red
                  if (isOnLeaveToday) {
                    attendanceStatus = 'on-leave'; // yellow
                  } else if (todayHours > 0 || isActiveToday) {
                    attendanceStatus = 'checked-in'; // green
                  }
                  
                  // Calculate this week's hours including extra hours (regular + extra)
                  const weekStart = moment().startOf('isoWeek'); // Monday
                  const weekEnd = moment().endOf('isoWeek'); // Sunday
                  const weekStartStr = weekStart.format('YYYY-MM-DD');
                  const weekEndStr = weekEnd.format('YYYY-MM-DD');
                  const thisWeekHours = member.dailyHours?.filter(d => {
                    const dayDateStr = moment(d.date).format('YYYY-MM-DD');
                    return dayDateStr >= weekStartStr && dayDateStr <= weekEndStr;
                  }).reduce((sum, day) => {
                    const regularHours = day.hoursWorked || 0;
                    const extraHours = day.extraHours || 0;
                    return sum + regularHours + extraHours;
                  }, 0) || 0;
                  
                  // Calculate total leave days in the selected period
                  const totalLeaveDays = member.leaveRequests?.reduce((sum, leave) => sum + leave.totalDays, 0) || 0;
                  
                  return (
                    <React.Fragment key={member.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <TeamMemberAvatar
                              member={member}
                              className="h-8 w-8"
                              textClassName={`font-medium text-xs ${
                                attendanceStatus === 'checked-in'
                                  ? 'text-green-700'
                                  : attendanceStatus === 'on-leave'
                                  ? 'text-yellow-700'
                                  : 'text-red-700'
                              }`}
                              fallbackClassName={
                                attendanceStatus === 'checked-in'
                                  ? 'bg-green-100'
                                  : attendanceStatus === 'on-leave'
                                  ? 'bg-yellow-100'
                                  : 'bg-red-100'
                              }
                            />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {member.firstName} {member.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {member.officialEmail}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-blue-900">
                            {todayHours.toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-purple-900">
                            {thisWeekHours.toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-green-900">
                            {totalHours.toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm font-semibold text-orange-900">
                            {avgHours.toFixed(1)}h
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-900">
                            {workingDays}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`text-sm font-semibold ${totalLeaveDays > 0 ? 'text-purple-700' : 'text-gray-400'}`}>
                            {totalLeaveDays}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {clickupLoading ? (
                            <div className="flex items-center justify-center">
                              <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                            </div>
                          ) : clickupTaskCounts[member.officialEmail]?.found ? (
                            <button
                              onClick={() => loadUserTaskDetails(member.officialEmail, `${member.firstName} ${member.lastName}`)}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all duration-200"
                            >
                              History ({clickupTaskCounts[member.officialEmail].count || 0})
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">Not enabled</span>
                          )}
                        </td>
	                        <td className="px-4 py-3 whitespace-nowrap text-center">
	                          <div className="flex flex-col items-center gap-2">
	                            <button
	                              onClick={() => toggleMemberExpanded(member.id)}
                              className="inline-flex items-center px-3 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md transition-colors"
                            >
                              {expandedMembers[member.id] ? (
                                <>
                                  <ChevronUpIcon className="h-4 w-4 mr-1" />
                                  Hide
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="h-4 w-4 mr-1" />
                                  View
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Details Row */}
                      {expandedMembers[member.id] && (
                        <tr>
                          <td colSpan="10" className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              {/* Leave Data */}
                              {member.leaveRequests && member.leaveRequests.length > 0 && (
                                <div>
                                  <h5 className="text-sm font-semibold text-purple-700 mb-2">
                                    Approved Leaves ({member.leaveRequests.length})
                                  </h5>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {member.leaveRequests.map((leave) => (
                                      <div key={leave.id} className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded">
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2">
                                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                              leave.leaveType === 'Short Leave' ? 'bg-teal-100 text-teal-800' :
                                              leave.leaveType === 'Half Day' ? 'bg-orange-100 text-orange-800' :
                                              leave.leaveType === 'Work from Home' ? 'bg-green-100 text-green-800' :
                                              'bg-purple-200 text-purple-800'
                                            }`}>
                                              {leave.leaveType === 'Short Leave' && '⏱️ '}
                                              {leave.leaveType === 'Half Day' && '🕐 '}
                                              {leave.leaveType}
                                            </span>
                                            <span className="text-xs text-gray-600">
                                              {leave.leaveType === 'Short Leave' ? '(No impact)' :
                                               leave.leaveType === 'Half Day' ? '(0.5 day)' :
                                               `${leave.totalDays} day(s)`}
                                            </span>
                                          </div>
                                          <div className="text-xs text-gray-700 mt-1">
                                            {moment(leave.startDate).format('MMM DD')} - {moment(leave.endDate).format('MMM DD, YYYY')}
                                          </div>
                                        </div>
                                        <div className="ml-3">
                                          <button
                                            onClick={() => handleCancelApprovedLeave(leave, member)}
                                            disabled={cancellingApprovedLeaveId === leave.id || !isLeaveCancelableWithinWeek(leave)}
                                            title={!isLeaveCancelableWithinWeek(leave) ? 'Can only cancel within 7 days of approval' : ''}
                                            className="inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                                          >
                                            {cancellingApprovedLeaveId === leave.id ? 'Cancelling...' : 'Cancel'}
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              <MemberAttendanceCalendar
                                member={member}
                                startDate={startDate}
                                endDate={endDate}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            </div>
          </div>
        )}

        {/* Workload Management Tab */}
        {activeTab === 'workload' && (
          <div className="bg-white shadow-lg rounded-xl">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <div>
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Team Workload Management</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">View and manage team member task schedules</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  {/* View Toggle */}
                  <div className="inline-flex rounded-lg border-2 border-gray-200 p-1 bg-gray-50">
                    <button
                      onClick={() => setWorkloadView('timeline')}
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                        workloadView === 'timeline'
                          ? 'bg-teal-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Timeline
                    </button>
                    <button
                      onClick={() => setWorkloadView('cards')}
                      className={`px-4 py-2 rounded-md text-sm font-semibold transition-all ${
                        workloadView === 'cards'
                          ? 'bg-teal-600 text-white shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <svg className="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Cards
                    </button>
                  </div>
                  
                  {workloadView === 'timeline' && (
                    <>
                      <select
                        value={timelineDays}
                        onChange={(e) => setTimelineDays(parseInt(e.target.value))}
                        className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-teal-500"
                      >
                        <option value={7}>7 days</option>
                        <option value={14}>14 days</option>
                        <option value={21}>21 days</option>
                        <option value={30}>30 days</option>
                      </select>
                      
                      <div className="flex items-center gap-1 border-2 border-gray-200 rounded-lg p-1">
                        <button
                          onClick={() => setTimelineStartDate(moment(timelineStartDate).subtract(timelineDays, 'days').toDate())}
                          className="px-2 py-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setTimelineStartDate(moment().startOf('week').toDate())}
                          className="px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded"
                        >
                          Today
                        </button>
                        <button
                          onClick={() => setTimelineStartDate(moment(timelineStartDate).add(timelineDays, 'days').toDate())}
                          className="px-2 py-1 hover:bg-gray-100 rounded"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                  
                  <button
                    onClick={loadWorkloadData}
                    disabled={workloadLoading}
                    className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-4 h-4 mr-2 ${workloadLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {workloadLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin h-16 w-16 border-4 border-teal-200 border-t-teal-600 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading team workload...</p>
                  </div>
                </div>
              ) : !workloadData ? (
                <div className="text-center py-20">
                  <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No workload data available</p>
                  <p className="text-gray-400 text-sm mt-2">Click refresh to load data</p>
                </div>
              ) : workloadView === 'timeline' ? (
                /* Timeline View - ClickUp Style */
                <div className="space-y-4">
                  {/* Date Header */}
                  <div className="overflow-x-auto timeline-scroll">
                    <div className="inline-flex min-w-full">
                      {/* Member Name Column - Sticky */}
                      <div className="sticky left-0 z-20 bg-white border-r-2 border-gray-300 min-w-[200px]">
                        <div className="h-12 flex items-center px-4 bg-gray-100 border-b-2 border-gray-300">
                          <span className="text-xs font-bold text-gray-700 uppercase">Team Member</span>
                        </div>
                      </div>
                      
                      {/* Date Columns */}
                      <div className="flex">
                        {[...Array(timelineDays)].map((_, index) => {
                          const date = moment(timelineStartDate).add(index, 'days');
                          const isToday = date.isSame(moment(), 'day');
                          const isWeekend = date.day() === 0 || date.day() === 6;
                          
                          return (
                            <div key={index} className={`min-w-[80px] border-r border-gray-200 ${isWeekend ? 'bg-gray-50' : ''}`}>
                              <div className={`h-12 flex flex-col items-center justify-center border-b-2 ${
                                isToday ? 'bg-teal-100 border-teal-400' : 'bg-gray-100 border-gray-300'
                              }`}>
                                <span className={`text-xs font-semibold ${isToday ? 'text-teal-700' : 'text-gray-500'}`}>
                                  {date.format('ddd')}
                                </span>
                                <span className={`text-sm font-bold ${isToday ? 'text-teal-900' : 'text-gray-900'}`}>
                                  {date.format('D')}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Team Members Rows */}
                    {workloadData && workloadData.workloadData.map((member) => {
                      // Calculate hours per day and collect tasks per day for UI
                      const hoursPerDay = {};
                      const tasksByDate = {};
                      member.tasks.forEach(task => {
                        if (task.start_date && task.due_date) {
                          const taskStart = moment(parseInt(task.start_date));
                          const taskEnd = moment(parseInt(task.due_date));
                          const taskDays = taskEnd.diff(taskStart, 'days') + 1;
                          const hoursPerTaskDay = task.time_estimate ? (task.time_estimate / 1000 / 60 / 60 / taskDays) : (8 / taskDays);
                          
                          for (let i = 0; i < timelineDays; i++) {
                            const currentDate = moment(timelineStartDate).add(i, 'days');
                            if (currentDate.isBetween(taskStart, taskEnd, 'day', '[]')) {
                              const dateKey = currentDate.format('YYYY-MM-DD');
                              hoursPerDay[dateKey] = (hoursPerDay[dateKey] || 0) + hoursPerTaskDay;
                              if (!tasksByDate[dateKey]) tasksByDate[dateKey] = [];
                              tasksByDate[dateKey].push(task);
                            }
                          }
                        }
                      });
                      
                      const totalHours = Object.values(hoursPerDay).reduce((sum, h) => sum + h, 0);
                      
                      return (
                        <div key={member.userId} className="inline-flex min-w-full border-b border-gray-200 hover:bg-gray-50">
                          {/* Member Info - Sticky */}
                          <div className="sticky left-0 z-10 bg-white border-r-2 border-gray-300 min-w-[200px]">
                            <div className="h-20 flex items-center px-3 py-2">
                              <div className="flex items-center gap-3 w-full">
                                <div className="relative flex-shrink-0">
                                  <TeamMemberAvatar
                                    member={member}
                                    className="h-10 w-10"
                                    roundedClassName="rounded-lg"
                                    fallbackClassName="bg-gradient-to-br from-teal-400 to-teal-600 text-white"
                                    textClassName="text-sm font-bold"
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-bold text-gray-900 truncate">{member.name}</div>
                                  <div className="text-xs text-gray-500 truncate">
                                    {totalHours.toFixed(1)}h/{(timelineDays * 8).toFixed(0)}h
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Date Cells */}
                          <div className="flex relative">
                            {[...Array(timelineDays)].map((_, index) => {
                              const date = moment(timelineStartDate).add(index, 'days');
                              const dateKey = date.format('YYYY-MM-DD');
                              const hours = hoursPerDay[dateKey] || 0;
                              const isWeekend = date.day() === 0 || date.day() === 6;
                              
                              let bgColor = 'bg-white';
                              if (hours > 0) {
                                if (hours >= 8) bgColor = 'bg-orange-200';
                                else if (hours >= 6) bgColor = 'bg-yellow-100';
                                else if (hours >= 3) bgColor = 'bg-green-100';
                                else bgColor = 'bg-teal-50';
                              } else if (isWeekend) {
                                bgColor = 'bg-gray-50';
                              }
                              
                              const dayKey = date.format('YYYY-MM-DD');
                              const dayTasks = tasksByDate[dayKey] || [];
                              const multiCount = dayTasks.length > 1 ? dayTasks.length - 1 : 0;
                              return (
                                <div 
                                  key={index} 
                                  className={`relative group min-w-[80px] h-20 border-r border-gray-200 ${bgColor} flex items-center justify-center transition-colors ${draggingTask ? 'hover:bg-teal-100 hover:ring-2 hover:ring-teal-400' : ''}`}
                                  onDragOver={handleDateCellDragOver}
                                  onDrop={(e) => handleDateCellDrop(e, date.toDate())}
                                >
                                  {hours > 0 && (
                                    <span className={`text-sm font-bold ${
                                      hours >= 8 ? 'text-orange-800' :
                                      hours >= 6 ? 'text-yellow-800' :
                                      hours >= 3 ? 'text-green-800' :
                                      'text-teal-700'
                                    }`}>
                                      {hours.toFixed(1)}h
                                    </span>
                                  )}
                                  {/* If multiple tasks overlap this date, show +N badge */}
                                  {multiCount > 0 && (
                                    <span className="absolute top-1 right-1 text-[10px] font-extrabold bg-gray-900 text-white rounded px-1.5 py-0.5 shadow-sm">
                                      +{multiCount}
                                    </span>
                                  )}
                                  {/* Hover popover with tasks for this date */}
                                  {dayTasks.length > 0 && (
                                    <div className="hidden group-hover:block absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 w-72 max-h-64 overflow-auto rounded-lg border-2 border-gray-200 bg-white shadow-xl p-2 text-left">
                                      <div className="text-xs font-bold text-gray-700 mb-2">
                                        {date.format('MMM DD, YYYY')} • {dayTasks.length} task{dayTasks.length>1?'s':''}
                                      </div>
                                      <div className="space-y-1">
                                        {dayTasks.map(t => (
                                          <div key={t.id} className="flex items-start gap-2 p-2 rounded-md hover:bg-gray-50 border border-gray-100">
                                            <span className={`mt-0.5 inline-block h-2 w-2 rounded-full ${
                                              t.status?.toLowerCase().includes('progress') ? 'bg-yellow-500' :
                                              t.status?.toLowerCase().includes('complete') ? 'bg-green-600' :
                                              'bg-teal-500'
                                            }`}></span>
                                            <div className="min-w-0">
                                              <div className="text-xs font-semibold text-gray-900 truncate" title={t.name}>{t.name}</div>
                                              <div className="text-[10px] text-gray-500 truncate">{t.list} • {t.status}</div>
                                            </div>
                                            <a
                                              href={t.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="ml-auto text-[10px] font-bold text-teal-700 hover:underline"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              Open
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            
                            {/* Task Bars */}
                            {member.tasks.filter(task => task.start_date && task.due_date).map((task) => {
                              const taskStart = moment(parseInt(task.start_date));
                              const taskEnd = moment(parseInt(task.due_date));
                              const timelineStart = moment(timelineStartDate);
                              
                              // Calculate position and width
                              let startDay = taskStart.diff(timelineStart, 'days');
                              let endDay = taskEnd.diff(timelineStart, 'days');
                              
                              if (endDay < 0 || startDay >= timelineDays) return null;
                              
                              startDay = Math.max(0, startDay);
                              endDay = Math.min(timelineDays - 1, endDay);
                              const duration = endDay - startDay + 1;
                              
                              const isOverdue = taskEnd.isBefore(moment(), 'day');
                              
                              return (
                                <div
                                  key={task.id}
                                  draggable
                                  className="absolute h-10 top-5 group cursor-move select-none"
                                  style={{
                                    left: `${startDay * 80 + 6}px`,
                                    width: `${duration * 80 - 12}px`,
                                    minWidth: '60px'
                                  }}
                                  onDragStart={(e) => handleTaskDragStart(e, task, member.name, member.teamId)}
                                  onDragEnd={handleTaskDragEnd}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditTaskModal(task, member.name, member.teamId);
                                  }}
                                >
                                  <div className={`relative h-full rounded-md shadow-md px-2.5 py-1.5 flex items-center gap-1 overflow-hidden ${
                                    isOverdue ? 'bg-red-400 border-2 border-red-600' :
                                    task.status?.toLowerCase().includes('progress') ? 'bg-yellow-300 border-2 border-yellow-500' :
                                    task.status?.toLowerCase().includes('complete') ? 'bg-green-300 border-2 border-green-500' :
                                    'bg-teal-300 border-2 border-teal-500'
                                  } group-hover:shadow-xl group-hover:scale-105 transition-all duration-200`}>
                                    {/* Left resize handle */}
                                    <div
                                      className="absolute left-0 top-0 h-full w-2 cursor-ew-resize bg-black/0 hover:bg-black/10"
                                      draggable
                                      onDragStart={(e) => handleTaskDragStart(e, task, member.name, member.teamId, 'resize-left')}
                                      onDragEnd={handleTaskDragEnd}
                                      title="Drag to extend earlier"
                                    />
                                    {/* Content */}
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                      <div className="text-xs font-bold text-gray-900 truncate leading-tight" title={task.name}>
                                        {task.name}
                                      </div>
                                      {task.time_estimate && (
                                        <div className="text-xs text-gray-700 truncate leading-tight">
                                          {(task.time_estimate / 1000 / 60 / 60).toFixed(1)}h
                                        </div>
                                      )}
                                    </div>
                                    {/* Right resize handle */}
                                    <div
                                      className="absolute right-0 top-0 h-full w-2 cursor-ew-resize bg-black/0 hover:bg-black/10"
                                      draggable
                                      onDragStart={(e) => handleTaskDragStart(e, task, member.name, member.teamId, 'resize-right')}
                                      onDragEnd={handleTaskDragEnd}
                                      title="Drag to extend later"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Unscheduled Tasks Section (Accordion per user) */}
                    {workloadData && workloadData.workloadData.some(m => m.tasks.some(t => !t.start_date || !t.due_date)) && (
                      <div className="mt-6 border-t-2 border-gray-300 pt-4">
                        <h4 className="text-sm font-bold text-gray-700 mb-3 px-4">Unscheduled Tasks</h4>
                        <div className="divide-y divide-gray-200">
                          {workloadData.workloadData.map((member) => {
                            const unscheduledTasks = member.tasks.filter(t => !t.start_date || !t.due_date);
                            if (unscheduledTasks.length === 0) return null;
                            const isOpen = openUnscheduledIds.has(member.userId);
                            return (
                              <div key={member.userId} className="">
                                <button
                                  type="button"
                                  onClick={() => toggleUnscheduledAccordion(member.userId)}
                                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${unscheduledTasks.length>3?'bg-red-100 text-red-700':unscheduledTasks.length>1?'bg-yellow-100 text-yellow-700':'bg-gray-100 text-gray-700'}`}>{unscheduledTasks.length}</span>
                                    <span className="text-sm font-semibold text-gray-800">{member.name}</span>
                                    <span className="text-xs text-gray-500">{member.email}</span>
                                  </div>
                                  <svg className={`w-4 h-4 text-gray-500 transition-transform ${isOpen?'rotate-180':''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                  </svg>
                                </button>
                                {isOpen && (
                                  <div className="px-4 pb-4">
                                    <div className="flex flex-wrap gap-2">
                                      {unscheduledTasks.map(task => (
                                        <button
                                          key={task.id}
                                          onClick={() => openEditTaskModal(task, member.name, member.teamId, member.userId)}
                                          className="inline-flex items-center px-3 py-2 bg-gray-100 hover:bg-gray-200 border-2 border-gray-300 rounded-lg text-xs font-medium text-gray-900 transition-colors max-w-xs"
                                          title={task.name}
                                        >
                                          <svg className="w-3 h-3 mr-1.5 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                          <span className="truncate">{task.name}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Cards View */
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-6 border-2 border-teal-200">
                      <UserGroupIcon className="h-8 w-8 text-teal-600 mb-2" />
                      <dt className="text-sm font-medium text-teal-900">Team Members</dt>
                      <dd className="text-3xl font-bold text-teal-700 mt-2">{workloadData.teamSize}</dd>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <ChartBarIcon className="h-8 w-8 text-blue-600 mb-2" />
                      <dt className="text-sm font-medium text-blue-900">Total Tasks</dt>
                      <dd className="text-3xl font-bold text-blue-700 mt-2">
                        {workloadData.workloadData.reduce((sum, m) => sum + m.taskCount, 0)}
                      </dd>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
                      <ClockIcon className="h-8 w-8 text-purple-600 mb-2" />
                      <dt className="text-sm font-medium text-purple-900">Avg per Person</dt>
                      <dd className="text-3xl font-bold text-purple-700 mt-2">
                        {workloadData.teamSize > 0 
                          ? (workloadData.workloadData.reduce((sum, m) => sum + m.taskCount, 0) / workloadData.teamSize).toFixed(1)
                          : 0
                        }
                      </dd>
                    </div>
                  </div>

                  {/* Team Members Workload - Card View (Accordion + Lazy Load) */}
                  <div className="space-y-6">
                    {(workloadMembers?.members || workloadData.workloadData).map((member) => (
                      <div key={member.userId} className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300">
                        {/* Member Header */}
                        <button
                          type="button"
                          onClick={() => toggleMemberAccordion(member.userId)}
                          className="w-full text-left bg-gradient-to-r from-slate-50 via-gray-50 to-slate-50 px-6 py-5 border-b border-gray-200"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center gap-4">
                              <div className="relative">
                                <TeamMemberAvatar
                                  member={member}
                                  className="h-14 w-14 shadow-lg ring-4 ring-teal-50"
                                  roundedClassName="rounded-xl"
                                  fallbackClassName="bg-gradient-to-br from-teal-400 via-teal-500 to-teal-600 text-white"
                                  textClassName="text-xl font-bold"
                                />
                                <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full border-2 border-white ${
                                  (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 20 ? 'bg-red-500' :
                                  (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 10 ? 'bg-orange-500' :
                                  'bg-green-500'
                                }`}></div>
                              </div>
                              <div>
                                <h4 className="text-lg font-bold text-gray-900">{member.name}</h4>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-sm text-gray-600">{member.email}</span>
                                  <span className="text-gray-300">•</span>
                                  <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2.5 py-0.5 rounded-full">
                                    {member.department || 'N/A'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className={`px-6 py-3 rounded-xl ${
                                (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 20 ? 'bg-red-50 border-2 border-red-200' :
                                (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 10 ? 'bg-orange-50 border-2 border-orange-200' :
                                'bg-green-50 border-2 border-green-200'
                              }`}>
                                <div className="text-xs font-semibold text-gray-600 uppercase mb-1">Active Tasks</div>
                                <div className={`text-3xl font-bold ${
                                  (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 20 ? 'text-red-600' :
                                  (memberTasks[member.userId]?.taskCount || member.taskCount || 0) > 10 ? 'text-orange-600' :
                                  'text-green-600'
                                }`}>
                                  {memberTasks[member.userId]?.taskCount ?? member.taskCount ?? '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Accordion Content */}
                        {openMemberIds.has(member.userId) && (
                          <div className="p-6">
                            {memberTasks[member.userId]?.loading && (
                              <div className="py-8 text-center text-gray-500">Loading tasks...</div>
                            )}
                            {!memberTasks[member.userId]?.loading && (
                              memberTasks[member.userId]?.tasks?.length > 0 ? (
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                  {memberTasks[member.userId].tasks.map((task) => (
                                    <div key={task.id} className="group bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-5 hover:border-teal-300 hover:shadow-md transition-all duration-200">
                                  {/* Task Header */}
                                  <div className="flex items-start justify-between mb-4">
                                    <div className="flex-1 min-w-0 pr-4">
                                      <h5 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-teal-700 transition-colors" title={task.name}>
                                        {task.name}
                                      </h5>
                                      <div className="flex flex-wrap items-center gap-2 mb-3">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
                                          📋 {task.list}
                                        </span>
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${
                                          task.status?.toLowerCase().includes('progress') 
                                            ? 'bg-yellow-50 text-yellow-800 border-yellow-200'
                                            : task.status?.toLowerCase().includes('complete')
                                            ? 'bg-green-50 text-green-800 border-green-200'
                                            : 'bg-gray-50 text-gray-800 border-gray-200'
                                        }`}>
                                          ⚡ {task.status}
                                        </span>
                                        {task.priority && task.priority !== 'None' && (
                                          <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold border ${
                                            task.priority.toLowerCase() === 'urgent' 
                                              ? 'bg-red-50 text-red-800 border-red-200'
                                              : task.priority.toLowerCase() === 'high'
                                              ? 'bg-orange-50 text-orange-800 border-orange-200'
                                              : 'bg-blue-50 text-blue-800 border-blue-200'
                                          }`}>
                                            🎯 {task.priority}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Task Dates */}
                                  <div className="space-y-2 mb-4">
                                    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        <span className="text-xs font-semibold text-gray-600">Start</span>
                                      </div>
                                      <span className="text-sm font-bold text-gray-900">
                                        {task.start_date ? moment(parseInt(task.start_date)).format('MMM DD, YYYY') : 'Not set'}
                                      </span>
                                    </div>
                                    <div className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-200">
                                      <div className="flex items-center gap-2">
                                        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-xs font-semibold text-gray-600">Due</span>
                                      </div>
                                      <span className={`text-sm font-bold ${
                                        task.due_date 
                                          ? moment(parseInt(task.due_date)).isBefore(moment())
                                            ? 'text-red-600'
                                            : 'text-gray-900'
                                          : 'text-gray-400'
                                      }`}>
                                        {task.due_date ? moment(parseInt(task.due_date)).format('MMM DD, YYYY') : 'Not set'}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Task Actions */}
                                      <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                                        <button
                                          onClick={() => openEditTaskModal(task, member.name, memberTasks[member.userId]?.teamId || member.teamId, member.userId)}
                                          className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-all duration-200 border-2 border-teal-200 hover:border-teal-300"
                                        >
                                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                          </svg>
                                          Edit Dates
                                        </button>
                                        <a
                                          href={task.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-bold text-gray-700 bg-white hover:bg-gray-50 rounded-lg transition-all duration-200 border-2 border-gray-200 hover:border-gray-300"
                                        >
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                          </svg>
                                        </a>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="p-12 text-center">
                                  {memberTasks[member.userId]?.error ? (
                                    <div className="inline-flex items-center gap-3 px-6 py-4 bg-orange-50 text-orange-700 rounded-xl border-2 border-orange-200">
                                      <ExclamationTriangleIcon className="h-6 w-6" />
                                      <span className="font-semibold">{memberTasks[member.userId]?.error}</span>
                                    </div>
                                  ) : (
                                    <div className="inline-flex items-center gap-3 px-6 py-4 bg-green-50 text-green-700 rounded-xl border-2 border-green-200">
                                      <CheckCircleIcon className="h-6 w-6" />
                                      <span className="font-semibold">No active tasks - All clear!</span>
                                    </div>
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Performance Tab */}
        {activeTab === 'performance' && (
          <div className="bg-white shadow-lg rounded-xl">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900">Team Performance Metrics</h3>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <select
                    value={performanceDays}
                    onChange={(e) => setPerformanceDays(parseInt(e.target.value))}
                    className="border-2 border-gray-300 rounded-lg px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value={7}>Last 7 Days</option>
                    <option value={14}>Last 14 Days</option>
                    <option value={30}>Last 30 Days</option>
                  </select>
                  <button
                    onClick={loadPerformanceData}
                    disabled={performanceLoading}
                    className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-4 h-4 mr-2 ${performanceLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {performanceLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin h-16 w-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading performance data...</p>
                  </div>
                </div>
              ) : !performanceData ? (
                <div className="text-center py-20">
                  <ChartBarIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 font-medium">No performance data available</p>
                  <p className="text-gray-400 text-sm mt-2">Click refresh to load data</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border-2 border-green-200">
                      <div className="flex items-center justify-between mb-2">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <dt className="text-sm font-medium text-green-900">Tasks Completed</dt>
                      <dd className="text-3xl font-bold text-green-700 mt-2">{performanceData.summary.totalCompleted}</dd>
                      <p className="text-xs text-green-600 mt-2">Last {performanceDays} days</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border-2 border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <PlusCircleIcon className="h-8 w-8 text-blue-600" />
                      </div>
                      <dt className="text-sm font-medium text-blue-900">New Tasks</dt>
                      <dd className="text-3xl font-bold text-blue-700 mt-2">{performanceData.summary.totalNewTasks}</dd>
                      <p className="text-xs text-blue-600 mt-2">Last {performanceDays} days</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border-2 border-purple-200">
                      <div className="flex items-center justify-between mb-2">
                        <UserGroupIcon className="h-8 w-8 text-purple-600" />
                      </div>
                      <dt className="text-sm font-medium text-purple-900">Your Team Members</dt>
                      <dd className="text-3xl font-bold text-purple-700 mt-2">
                        {performanceData.summary.teamMemberCount || performanceData.summary.totalUsers}
                      </dd>
                      <p className="text-xs text-purple-600 mt-2">
                        {performanceData.summary.totalUsers > 0 
                          ? `${performanceData.summary.totalUsers} with task data` 
                          : 'Your direct reports'}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border-2 border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <ChartBarIcon className="h-8 w-8 text-orange-600" />
                      </div>
                      <dt className="text-sm font-medium text-orange-900">Avg Completion Rate</dt>
                      <dd className="text-3xl font-bold text-orange-700 mt-2">{performanceData.summary.avgCompletionRate}</dd>
                      <p className="text-xs text-orange-600 mt-2">Tasks/user/{performanceDays}d</p>
                    </div>
                  </div>

                  {/* Top Performers */}
                  {performanceData.users && performanceData.users.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-4">
                        <h4 className="text-lg font-bold text-white">Team Performance Rankings</h4>
                        <p className="text-sm text-indigo-100">Sorted by tasks completed</p>
                      </div>
                      
                      <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 uppercase">Rank</th>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-left text-xs font-bold text-gray-700 uppercase">User</th>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-xs font-bold text-gray-700 uppercase" title="Tasks that were active yesterday but not today (marked done, closed, or reassigned)">
                                <span className="hidden sm:inline">Tasks Completed</span>
                                <span className="sm:hidden">Done</span>
                              </th>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-xs font-bold text-gray-700 uppercase" title="Tasks that appear today but weren't in yesterday's snapshot (newly assigned or created)">
                                <span className="hidden sm:inline">New Tasks</span>
                                <span className="sm:hidden">New</span>
                              </th>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-xs font-bold text-gray-700 uppercase" title="Total active (non-closed) tasks currently assigned to the user">
                                <span className="hidden sm:inline">Current Tasks</span>
                                <span className="sm:hidden">Active</span>
                              </th>
                              <th className="px-2 sm:px-6 py-2 sm:py-3 text-center text-xs font-bold text-gray-700 uppercase" title="Net Change = New Tasks - Tasks Completed. Negative (green): Reducing backlog ✓. Positive (red): Growing backlog ⚠. Zero (gray): Stable workload">
                                <span className="hidden sm:inline">Net Change</span>
                                <span className="sm:hidden">Net</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {performanceData.users.map((user, idx) => (
                              <tr key={user.userId} className="hover:bg-indigo-50 transition-colors">
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                  <div className={`flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-full font-bold text-xs sm:text-sm ${
                                    idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                    idx === 1 ? 'bg-gray-100 text-gray-700' :
                                    idx === 2 ? 'bg-orange-100 text-orange-700' :
                                    'bg-blue-50 text-blue-600'
                                  }`}>
                                    {idx + 1}
                                  </div>
                                </td>
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center shadow-md">
                                      <span className="text-xs sm:text-sm font-bold text-white">
                                        {user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                      </span>
                                    </div>
                                    <div className="ml-2 sm:ml-4">
                                      <div className="text-xs sm:text-sm font-medium text-gray-900">{user.name}</div>
                                      <div className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">{user.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                  <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold bg-green-100 text-green-800">
                                    {user.totalCompleted}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                  <span className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold bg-blue-100 text-blue-800">
                                    {user.totalNewTasks}
                                  </span>
                                </td>
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                  <span className="text-xs sm:text-sm font-semibold text-gray-900">{user.currentTasks}</span>
                                </td>
                                <td className="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-center">
                                  <span className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold ${
                                    (user.totalNewTasks - user.totalCompleted) > 0 
                                      ? 'bg-red-100 text-red-800'
                                      : (user.totalNewTasks - user.totalCompleted) < 0
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-gray-100 text-gray-600'
                                  }`}>
                                    {(user.totalNewTasks - user.totalCompleted) > 0 ? '+' : ''}
                                    {user.totalNewTasks - user.totalCompleted}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No Data Message */}
                  {(!performanceData.users || performanceData.users.length === 0) && (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium">No performance data available yet</p>
                      <p className="text-gray-500 text-sm mt-2">
                        Performance tracking starts after the first daily snapshot (runs at 11:59 PM IST)
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Leave Stats Detail Modal */}
      {showLeaveStatsModal && selectedLeaveStatsUser && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity"
              onClick={handleCloseLeaveStatsModal}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 px-6 py-6 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/20 text-2xl font-bold shadow-inner">
                      {selectedLeaveStatsUser.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .substring(0, 2)
                        .toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">
                        {selectedLeaveStatsUser.name}
                      </h3>
                      <div className="mt-1 space-y-1 text-sm text-white/90">
                        {selectedLeaveStatsUser.email && (
                          <div>{selectedLeaveStatsUser.email}</div>
                        )}
                        {selectedLeaveStatsUser.department && (
                          <div>{selectedLeaveStatsUser.department}</div>
                        )}
                        {selectedLeaveStatsUser.title && (
                          <div>{selectedLeaveStatsUser.title}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseLeaveStatsModal}
                    className="ml-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <span className="text-xl font-bold leading-none">&times;</span>
                  </button>
                </div>

                {selectedLeaveStatsUser.metrics && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Total Requests</div>
                      <div className="text-xl font-bold">{selectedLeaveStatsUser.metrics.total}</div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Approved</div>
                      <div className="text-xl font-bold text-green-200">
                        {selectedLeaveStatsUser.metrics.approved}
                      </div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Pending</div>
                      <div className="text-xl font-bold text-yellow-200">
                        {selectedLeaveStatsUser.metrics.pending}
                      </div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Rejected</div>
                      <div className="text-xl font-bold text-rose-200">
                        {selectedLeaveStatsUser.metrics.rejected}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto space-y-4">
                {selectedLeaveStatsRequests.map((request) => {
                  const statusClass = getLeaveStatusClass(request.status);
                  return (
                    <div
                      key={request.id}
                      className="border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <span
                              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusClass}`}
                            >
                              {request.status}
                            </span>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200">
                              {request.leaveType}
                            </span>
                          </div>
                          <div className="mt-3 text-sm font-semibold text-gray-900">
                            {moment(request.startDate).format('MMM DD, YYYY')} -{' '}
                            {moment(request.endDate).format('MMM DD, YYYY')}
                            {request.totalDays ? (
                              <span className="ml-2 text-xs font-medium text-gray-500">
                                ({request.totalDays} day{request.totalDays > 1 ? 's' : ''})
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            Requested on {moment(request.createdAt).format('MMM DD, YYYY [at] HH:mm')}
                          </div>
                          {request.updatedAt && request.updatedAt !== request.createdAt && (
                            <div className="text-xs text-gray-400">
                              Updated on {moment(request.updatedAt).format('MMM DD, YYYY [at] HH:mm')}
                            </div>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          <div className="font-semibold text-gray-900">Manager</div>
                          <div>{request.manager?.name || request.managerName || 'Not assigned'}</div>
                          {request.reviewerNotes && (
                            <div className="mt-2 text-xs bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-indigo-800">
                              Manager Notes: {request.reviewerNotes}
                            </div>
                          )}
                        </div>
                      </div>

                      {request.reason && (
                        <div className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Employee Reason
                          </div>
                          <p className="mt-1 whitespace-pre-wrap">{request.reason}</p>
                        </div>
                      )}

                      {request.documents && request.documents.length > 0 && (
                        <div className="mt-3 text-xs text-gray-500">
                          Attachments: {request.documents.length}
                        </div>
                      )}
                    </div>
                  );
                })}

                {selectedLeaveStatsRequests.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <CalendarDaysIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No leave details found for this user.</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={handleCloseLeaveStatsModal}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCorrectionStatsModal && selectedCorrectionStatsUser && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity"
              onClick={handleCloseCorrectionStatsModal}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-6 text-white">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-white/20 text-2xl font-bold shadow-inner">
                      {selectedCorrectionStatsUser.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .substring(0, 2)
                        .toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">{selectedCorrectionStatsUser.name}</h3>
                      <div className="mt-1 space-y-1 text-sm text-white/90">
                        {selectedCorrectionStatsUser.email && (
                          <div>{selectedCorrectionStatsUser.email}</div>
                        )}
                        {selectedCorrectionStatsUser.department && (
                          <div>{selectedCorrectionStatsUser.department}</div>
                        )}
                        <div>Attendance correction requests</div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseCorrectionStatsModal}
                    className="ml-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <span className="text-xl font-bold leading-none">&times;</span>
                  </button>
                </div>

                {selectedCorrectionStatsUser.metrics && (
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Total Requests</div>
                      <div className="text-xl font-bold">{selectedCorrectionStatsUser.metrics.total}</div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Approved</div>
                      <div className="text-xl font-bold text-green-200">{selectedCorrectionStatsUser.metrics.approved}</div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Pending</div>
                      <div className="text-xl font-bold text-yellow-200">{selectedCorrectionStatsUser.metrics.pending}</div>
                    </div>
                    <div className="bg-white/15 rounded-xl p-3 text-center">
                      <div className="text-xs uppercase tracking-wide text-white/70">Rejected</div>
                      <div className="text-xl font-bold text-rose-200">{selectedCorrectionStatsUser.metrics.rejected}</div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto space-y-4">
                {selectedCorrectionStatsRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${getCorrectionStatusClass(request.status)}`}>
                            {request.status}
                          </span>
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">
                            {moment(request.requestDate).format('MMM DD, YYYY')}
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested Check-in</div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {request.requestedCheckInTime
                                ? moment(request.requestedCheckInTime).format('MMM DD, YYYY hh:mm A')
                                : 'Not provided'}
                            </div>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Requested Check-out</div>
                            <div className="mt-1 font-semibold text-gray-900">
                              {request.requestedCheckOutTime
                                ? moment(request.requestedCheckOutTime).format('MMM DD, YYYY hh:mm A')
                                : 'Not provided'}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 text-xs text-gray-500">
                          Requested on {moment(request.createdAt).format('MMM DD, YYYY [at] HH:mm')}
                        </div>
                        {request.reviewedAt && (
                          <div className="text-xs text-gray-400">
                            Reviewed on {moment(request.reviewedAt).format('MMM DD, YYYY [at] HH:mm')}
                          </div>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 sm:max-w-xs">
                        <div className="font-semibold text-gray-900">Reviewer Notes</div>
                        <div className="mt-1 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-indigo-800 text-xs whitespace-pre-wrap">
                          {request.reviewerNotes || 'No reviewer notes added.'}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employee Reason</div>
                      <p className="mt-1 whitespace-pre-wrap">{request.reason || 'No reason provided.'}</p>
                    </div>
                  </div>
                ))}

                {selectedCorrectionStatsRequests.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <ClockIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                    <p>No correction requests found for this user.</p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={handleCloseCorrectionStatsModal}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-orange-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Today Modal */}
      {showActiveTodayModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity"
              onClick={() => setShowActiveTodayModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">Today’s Attendance</h3>
                    <p className="mt-1 text-sm text-green-100">
                      {moment().format('dddd, MMMM DD, YYYY')} • {todayAttendanceSummary.activeToday.length} active team member{todayAttendanceSummary.activeToday.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowActiveTodayModal(false)}
                    className="ml-4 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/20 hover:bg-white/30 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white"
                  >
                    <span className="text-xl font-bold leading-none">&times;</span>
                  </button>
                </div>
              </div>

              <div className="bg-white px-6 py-6 max-h-[70vh] overflow-y-auto space-y-4">
                {todayAttendanceSummary.activeToday.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No team members have checked in today.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {todayAttendanceSummary.activeToday.map((member) => (
                      <div
                        key={member.id || member.email}
                        className="border border-green-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow duration-200"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div className="flex items-start gap-4">
                            <TeamMemberAvatar
                              member={member}
                              className="h-12 w-12"
                              roundedClassName="rounded-2xl"
                              fallbackClassName="bg-green-100 text-green-700"
                              textClassName="font-bold text-lg"
                            />
                            <div>
                              <div className="text-base font-semibold text-gray-900">
                                {member.name}
                              </div>
                              <div className="text-xs text-gray-500 mt-1">{member.email}</div>
                              <div className="inline-flex items-center gap-2 mt-2 px-3 py-1 text-xs font-semibold rounded-full bg-green-50 text-green-700 border border-green-200">
                                <span className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-green-500"></span>
                                {member.status === 'Completed' ? 'Completed' : 'Active'}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600">
                            <div className="font-semibold text-gray-900">Department</div>
                            <div>{member.department}</div>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                          <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                            <div className="text-xs uppercase font-semibold text-green-700">
                              Check-in
                            </div>
                            <div className="text-lg font-bold text-green-900">
                              {member.checkIn || '—'}
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              {member.deviceNumber ? `Device ${member.deviceNumber}` : 'Device info unavailable'}
                            </div>
                          </div>
                          <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                            <div className="text-xs uppercase font-semibold text-green-700">
                              Check-out
                            </div>
                            <div className="text-lg font-bold text-green-900">
                              {member.checkOut || 'In progress'}
                            </div>
                            <div className="text-xs text-green-600 mt-1">
                              {member.checkOut ? 'Day completed' : 'No checkout yet'}
                            </div>
                          </div>
                          <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3">
                            <div className="text-xs uppercase font-semibold text-indigo-700">
                              Hours Worked
                            </div>
                            <div className="text-lg font-bold text-indigo-900">
                              {member.hoursWorked.toFixed(2)}h
                            </div>
                            <div className="mt-2">
                              <div className="flex items-center justify-between text-xs text-indigo-600">
                                <span>{member.requiredHours}h required</span>
                                <span>{Math.round(member.completionPercentage)}%</span>
                              </div>
                              <div className="mt-1 h-2 w-full bg-indigo-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full"
                                  style={{ width: `${member.completionPercentage}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {member.lastUpdated && (
                          <div className="mt-3 text-xs text-gray-500">
                            Last update: {moment(member.lastUpdated).fromNow()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end">
                <button
                  onClick={() => setShowActiveTodayModal(false)}
                  className="inline-flex items-center px-5 py-2.5 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AbsenteesModal
        isOpen={showAbsenteesModal}
        onClose={() => setShowAbsenteesModal(false)}
        dateLabel={moment().format('dddd, MMMM DD, YYYY')}
        totalCount={todayAttendanceSummary.absenteesTotal}
        onLeaveMembers={todayAttendanceSummary.onLeaveToday}
        absentMembers={todayAttendanceSummary.absentToday}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title={confirmConfig.title}
        message={confirmConfig.message}
        confirmText={confirmConfig.confirmText || 'Confirm'}
        type={confirmConfig.type}
        onConfirm={confirmConfig.onConfirm}
      />

      {/* CC Selection Modal */}
      {showCCModal && pendingApproval && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCCModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Select Email Recipients (CC)
                    </h3>
                    <div className="mt-4">
                      <p className="text-sm text-gray-500 mb-4">
                        Your team members are pre-selected. You can optionally add upper managers and same-department users.
                      </p>

                      {/* Request Details */}
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                        {pendingApproval.bulk ? (
                          <>
                            <p className="text-sm font-medium text-blue-800">
                              Approving {(pendingApproval.requestIds || []).length} {pendingApproval.type === 'checkout' ? 'checkout' : 'leave'} request(s)
                            </p>
                            <p className="text-xs text-blue-700 mt-1">
                              The selected CC recipients and note below apply to every request in this batch.
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-blue-800">
                              {pendingApproval.type === 'checkout' ? 'Checkout Request' :
                               pendingApproval.type === 'extra-hours' ? 'Extra Hours Request' :
                               'Leave Request'} for {pendingApproval.request?.user?.firstName} {pendingApproval.request?.user?.lastName}
                            </p>
                            {pendingApproval.type === 'checkout' ? (
                              <p className="text-xs text-blue-700 mt-1">
                                Date: {moment(pendingApproval.request?.requestDate).format('MMM DD, YYYY')}
                              </p>
                            ) : pendingApproval.type === 'extra-hours' ? (
                              <p className="text-xs text-blue-700 mt-1">
                                Date: {moment(pendingApproval.request?.requestDate).format('MMM DD, YYYY')} | Hours: {pendingApproval.approvedHours || pendingApproval.request?.requestedHours} hour(s)
                              </p>
                            ) : (
                              <p className="text-xs text-blue-700 mt-1">
                                {moment(pendingApproval.request?.startDate).format('MMM DD')} - {moment(pendingApproval.request?.endDate).format('MMM DD, YYYY')} ({pendingApproval.request?.totalDays} days)
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      {/* Select All Checkbox */}
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        {(() => {
                          const filteredRecipients = ccRecipients.filter(recipient => {
                            if (!ccSearchTerm) return true;
                            const searchText = `${recipient.firstName} ${recipient.lastName} ${recipient.officialEmail} ${recipient.Department || ''}`.toLowerCase();
                            return searchText.includes(ccSearchTerm);
                          });
                          const filteredEmails = filteredRecipients
                            .map(r => normalizeEmail(r.officialEmail))
                            .filter(e => e);
                          const allFilteredSelected = filteredEmails.length > 0 && filteredEmails.every(email => selectedCCEmails.includes(email));
                          
                          return (
                            <label className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="checkbox"
                                checked={allFilteredSelected}
                                onChange={toggleAllCC}
                                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-900">
                                Select All ({filteredRecipients.length})
                              </span>
                            </label>
                          );
                        })()}
                      </div>

                      {/* Search Box */}
                      <div className="mb-3">
                        <input
                          type="text"
                          placeholder="Search by name, email, or department..."
                          onChange={(e) => {
                            const searchTerm = e.target.value.toLowerCase();
                            setCCSearchTerm(searchTerm);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                        />
                      </div>

                      {/* Recipients List - Divided by Team and Others */}
                      <div className="max-h-80 overflow-y-auto space-y-4">
                        {loadingCCRecipients ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                              <div className="animate-spin h-12 w-12 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading recipients...</p>
                            </div>
                          </div>
                        ) : (
                          /* Helper function to filter and display recipients */
                          (() => {
                          const filteredRecipients = ccRecipients.filter(recipient => {
                            if (!ccSearchTerm) return true;
                            const searchText = `${recipient.firstName} ${recipient.lastName} ${recipient.officialEmail} ${recipient.Department || ''}`.toLowerCase();
                            return searchText.includes(ccSearchTerm);
                          });
                          
                          // Separate team members from others
                          const teamMembers = filteredRecipients.filter(r => r.type === 'team_member' || r.type === 'current_manager' || r.type === 'request_sender');
                          const otherRecipients = filteredRecipients.filter(r => r.type === 'same_department' || r.type === 'upper_manager');
                          
                          const renderRecipient = (recipient) => {
                            const normalizedEmail = normalizeEmail(recipient.officialEmail);
                            const isChecked = normalizedEmail ? selectedCCEmails.includes(normalizedEmail) : false;
                            console.log(`📌 Rendering ${recipient.firstName} ${recipient.lastName}:`, { email: recipient.officialEmail, normalizedEmail, isChecked, selectedCCEmails });
                            return (
                              <label
                                key={recipient.id}
                                className="flex items-start cursor-pointer hover:bg-gray-50 p-2 rounded"
                              >
                              <input
                                  type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleCCRecipient(recipient.officialEmail)}
                                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded mt-1"
                                />
                              <div className="ml-3 flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {recipient.firstName} {recipient.lastName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {recipient.officialEmail}
                                </div>
                                {recipient.Department && (
                                  <div className="text-xs text-gray-400">
                                    {recipient.Department}
                                  </div>
                                )}
                              </div>
                            </label>
                            );
                          };
                          
                          return (
                            <>
                              {/* Your Team Section */}
                              {teamMembers.length > 0 && (
                                <div>
                                  <div className="flex items-center mb-2">
                                    <h4 className="text-sm font-semibold text-gray-900">Your Team ({teamMembers.length})</h4>
                                    <span className="ml-2 text-xs text-blue-600 font-medium">✓ Pre-selected</span>
                                  </div>
                                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 space-y-1">
                                    {teamMembers.map(renderRecipient)}
                                  </div>
                                </div>
                              )}
                              
                              {/* Other Recipients Section */}
                              {otherRecipients.length > 0 && (
                                <div>
                                  <div className="flex items-center mb-2">
                                    <h4 className="text-sm font-semibold text-gray-700">Other Recipients ({otherRecipients.length})</h4>
                                    <span className="ml-2 text-xs text-gray-500">Optional</span>
                                  </div>
                                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 space-y-1">
                                    {otherRecipients.map(recipient => (
                                      <div key={recipient.id}>
                                        {renderRecipient(recipient)}
                                        {recipient.type && (
                                          <div className="ml-8 text-xs text-gray-500 italic">
                                            {recipient.type === 'upper_manager' ? 'Upper Manager' :
                                             recipient.type === 'same_department' ? 'Same Department' : ''}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()
                        )}
                      </div>

                      <div className="mt-4 text-sm text-gray-600">
                        <strong>{selectedCCEmails.length}</strong> recipient(s) selected
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleFinalApproval}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Approve & Send Email
                </button>
                <button
                  onClick={() => {
                    setShowCCModal(false);
                    setPendingApproval(null);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Summary Preview Modal */}
      {showEmailPreviewModal && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowEmailPreviewModal(false)}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-6 py-4 flex items-center justify-between border-b border-slate-700">
                <div className="flex items-center gap-2">
                  <svg className="h-6 w-6 text-blue-400 animate-pulse" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                  </svg>
                  <h3 className="text-lg font-bold text-white">
                    Email Summary Preview
                  </h3>
                </div>
                <button
                  onClick={() => setShowEmailPreviewModal(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-white p-6 space-y-4">
                {/* Email Headers Card */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 shadow-sm space-y-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm">
                    <span className="w-16 font-semibold text-slate-500">To:</span>
                    <span className="text-slate-800 bg-white px-2 py-1 rounded border border-slate-200 font-mono text-xs">{summaryEmailForm.toEmail || user?.officialEmail || user?.email}</span>
                  </div>
                  {summaryEmailForm.ccEmails && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm border-t border-slate-100 pt-2">
                      <span className="w-16 font-semibold text-slate-500">CC:</span>
                      <span className="text-slate-800 bg-white px-2 py-1 rounded border border-slate-200 font-mono text-xs">{summaryEmailForm.ccEmails}</span>
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm border-t border-slate-100 pt-2">
                    <span className="w-16 font-semibold text-slate-500">Subject:</span>
                    <span className="text-slate-800 font-medium">{summaryEmailForm.subject || `Custom Team Summary - ${moment(startDate).format('MMM DD, YYYY')} to ${moment(endDate).format('MMM DD, YYYY')}`}</span>
                  </div>
                </div>

                {/* Sort Option Control */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-700">Sort team members inside report:</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={`${emailSortBy}-${emailSortOrder}`}
                      onChange={(e) => {
                        const [by, order] = e.target.value.split('-');
                        setEmailSortBy(by);
                        setEmailSortOrder(order);
                        fetchEmailPreview(by, order);
                      }}
                      disabled={loadingEmailPreview}
                      className="rounded-lg border-slate-300 shadow-sm text-sm px-3 py-1.5 focus:ring-blue-500 focus:border-blue-500 bg-white cursor-pointer"
                    >
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="attendancePercent-desc">Attendance Ratio (High to Low)</option>
                      <option value="attendancePercent-asc">Attendance Ratio (Low to High)</option>
                      <option value="hoursWorked-desc">Working Hours (High to Low)</option>
                      <option value="hoursWorked-asc">Working Hours (Low to High)</option>
                    </select>
                  </div>
                </div>

                {/* Live Template Container */}
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-inner bg-slate-100">
                  {loadingEmailPreview ? (
                    <div className="flex flex-col items-center justify-center h-[450px] bg-white">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
                      <p className="mt-4 text-sm text-slate-600 font-medium">Generating attendance report preview...</p>
                    </div>
                  ) : emailPreviewHtml ? (
                    <iframe
                      title="Email HTML Preview"
                      srcDoc={emailPreviewHtml}
                      className="w-full h-[450px] border-none bg-white"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-[450px] bg-white text-slate-500">
                      Failed to load email preview.
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200">
                <span className="text-xs text-slate-500 italic">
                  Please review the matched team members & stats before sending.
                </span>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEmailPreviewModal(false)}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSendSummaryEmail}
                    disabled={sendingSummaryEmail || loadingEmailPreview}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors shadow-md"
                  >
                    {sendingSummaryEmail ? 'Sending...' : 'Confirm & Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Task Dates Modal */}
      {showEditTaskModal && editingTask && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" 
              onClick={() => setShowEditTaskModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                      <CalendarDaysIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">Edit Task Dates</h3>
                      <p className="text-sm text-teal-100 mt-0.5">{editingTask.memberName}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowEditTaskModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-6">
                <div className="space-y-6">
                  {/* Task Info */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Task Details</h4>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-base font-medium text-gray-900">{taskEditForm.taskName}</div>
                      {editingTask.url && (
                        <a
                          href={editingTask.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors hover:underline"
                        >
                          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open in ClickUp
                        </a>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-medium">
                        {editingTask.list}
                      </span>
                      <span className={`px-2 py-1 rounded font-medium ${
                        editingTask.status?.toLowerCase().includes('progress') 
                          ? 'bg-yellow-100 text-yellow-800'
                          : editingTask.status?.toLowerCase().includes('complete')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {editingTask.status}
                      </span>
                      {editingTask.priority && editingTask.priority !== 'None' && (
                        <span className={`px-2 py-1 rounded font-medium ${
                          editingTask.priority.toLowerCase() === 'urgent' 
                            ? 'bg-red-100 text-red-800'
                            : editingTask.priority.toLowerCase() === 'high'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {editingTask.priority}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Date Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={taskEditForm.start_date}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, start_date: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Current: {editingTask.start_date ? moment(parseInt(editingTask.start_date)).format('MMM DD, YYYY') : 'Not set'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Due Date
                      </label>
                      <input
                        type="date"
                        value={taskEditForm.due_date}
                        onChange={(e) => setTaskEditForm({ ...taskEditForm, due_date: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Current: {editingTask.due_date ? moment(parseInt(editingTask.due_date)).format('MMM DD, YYYY') : 'Not set'}
                      </p>
                    </div>
                  </div>

                  {/* Info Notice */}
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          Changes will be synced directly to ClickUp and visible to all team members.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditTaskModal(false)}
                  disabled={savingTask}
                  className="px-6 py-3 border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTaskDates}
                  disabled={savingTask}
                  className="inline-flex items-center px-6 py-3 border-2 border-transparent rounded-lg text-sm font-bold text-white bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 focus:outline-none focus:ring-4 focus:ring-teal-300 shadow-lg transition-all duration-200 disabled:opacity-50"
                >
                  {savingTask ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Hierarchy Tab */}
      {activeTab === 'hierarchy' && (
        <div className="bg-white shadow-lg rounded-xl">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Team Hierarchy</h3>
                {hierarchyData?.allSubordinates && hierarchyData.allSubordinates.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    Total Team Members: <span className="font-semibold text-blue-600">{hierarchyData.allSubordinates.length}</span>
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-400 rounded"></div>
                  <span>Management</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-400 rounded"></div>
                  <span>You</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-400 rounded"></div>
                  <span>Your Team</span>
                </div>
              </div>
            </div>
            
            <div className="h-[900px] w-full">
              <TeamHierarchyFlow 
                hierarchyData={hierarchyData} 
                loading={hierarchyLoading} 
              />
            </div>
          </div>
        </div>
      )}

      {/* ClickUp Task Details Modal */}
      {showTaskModal && selectedUserTasks && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" 
              onClick={() => setShowTaskModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                      <ChartBarIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">
                        ClickUp History - {selectedUserTasks.userName}
                      </h3>
                      <p className="text-sm text-blue-100 mt-0.5">
                        {selectedUserTasks.clickupUser?.username || selectedUserTasks.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTaskModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-6 max-h-[600px] overflow-y-auto">
                {selectedUserTasks.loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading tasks...</p>
                    </div>
                  </div>
                ) : selectedUserTasks.error ? (
                  <div className="text-center py-12">
                    <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600">{selectedUserTasks.error}</p>
                  </div>
                ) : selectedUserTasks.tasks.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Tasks</h3>
                    <p className="text-gray-500">This user has no active tasks in ClickUp</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const rows = buildClickupHistoryRows(selectedUserTasks.tasks);
                      return (
                        <div className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className="grid grid-cols-12 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3">
                            <div className="col-span-1">S. No</div>
                            <div className="col-span-4">Parent Task</div>
                            <div className="col-span-3">Subtask Name</div>
                            <div className="col-span-3">Subtask URL</div>
                            <div className="col-span-1 text-center">Status</div>
                          </div>
                          <div className="divide-y divide-gray-200">
                            {rows.map((row, idx) => (
                              <div key={`${row.parentName}-${row.subtaskName}-${idx}`} className="grid grid-cols-12 px-4 py-3 text-sm">
                                <div className="col-span-1 text-gray-500 font-semibold">{row.serial}</div>
                                <div className="col-span-4 font-medium text-gray-900">{row.parentName}</div>
                                <div className="col-span-3 text-gray-700">
                                  {row.url ? (
                                    <a
                                      href={row.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-700 hover:underline"
                                    >
                                      {row.subtaskName}
                                    </a>
                                  ) : (
                                    row.subtaskName
                                  )}
                                </div>
                                <div className="col-span-3">
                                  {row.url ? (
                                    <a
                                      href={row.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline break-all"
                                    >
                                      Open
                                    </a>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </div>
                                <div className="col-span-1 text-center text-xs font-semibold text-gray-700">
                                  {row.status || '—'}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-sm text-gray-500">
                    {selectedUserTasks?.hasMore ? 'More tasks available.' : 'All tasks loaded.'}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {selectedUserTasks?.tasks?.length > 0 && (
                      <button
                        onClick={downloadClickupHistoryCsv}
                        className="w-full sm:w-auto inline-flex justify-center rounded-lg border-2 border-green-600 shadow-sm px-6 py-3 bg-green-600 text-base font-semibold text-white hover:bg-green-700 hover:border-green-700 focus:outline-none focus:ring-4 focus:ring-green-200 transition-all duration-200"
                      >
                        Download CSV
                      </button>
                    )}
                    {selectedUserTasks?.hasMore && (
                      <button
                        onClick={handleLoadMoreTasks}
                        disabled={loadingTaskDetails}
                        className="w-full sm:w-auto inline-flex justify-center rounded-lg border-2 border-blue-600 shadow-sm px-6 py-3 bg-blue-600 text-base font-semibold text-white hover:bg-blue-700 hover:border-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {loadingTaskDetails ? 'Loading...' : 'Load More'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowTaskModal(false)}
                      className="w-full sm:w-auto inline-flex justify-center rounded-lg border-2 border-gray-300 shadow-sm px-6 py-3 bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Correction Modal */}
      {showAttendanceCorrectionModal && selectedMemberForCorrection && selectedDateForCorrection && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" 
              onClick={() => setShowAttendanceCorrectionModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-600 to-red-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-xl bg-white/20 backdrop-blur-md border-2 border-white/30">
                      <ClockIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-xl font-bold text-white">
                        Correct Attendance
                      </h3>
                      <p className="text-sm text-orange-100 mt-0.5">
                        {selectedMemberForCorrection.firstName} {selectedMemberForCorrection.lastName} - {moment(selectedDateForCorrection).format('MMM DD, YYYY')}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowAttendanceCorrectionModal(false)}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white px-6 py-6">
                <div className="space-y-6">
                  {/* Check-in Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Check-in Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={correctionCheckInTime}
                      onChange={(e) => setCorrectionCheckInTime(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  {/* Checkout Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Checkout Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={correctionCheckOutTime}
                      onChange={(e) => setCorrectionCheckOutTime(e.target.value)}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      required
                    />
                  </div>

                  {/* Purpose/Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Purpose of Correction <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={correctionPurpose}
                      onChange={(e) => setCorrectionPurpose(e.target.value)}
                      rows={4}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="Please provide a reason for correcting this attendance (e.g., System error, Manual entry required, etc.)"
                      required
                    />
                  </div>

                  {/* Info Box */}
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-blue-700">
                          This correction will update the attendance records and recalculate working hours for this date. The changes will be reflected across all attendance reports and calculations.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowAttendanceCorrectionModal(false)}
                  disabled={savingCorrection}
                  className="px-6 py-3 rounded-lg border-2 border-gray-300 shadow-sm bg-white text-base font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200 transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAttendanceCorrection}
                  disabled={savingCorrection}
                  className="px-6 py-3 rounded-lg border-2 border-transparent shadow-sm bg-gradient-to-r from-orange-600 to-red-600 text-base font-semibold text-white hover:from-orange-700 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-orange-200 transition-all duration-200 disabled:opacity-50"
                >
                  {savingCorrection ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    'Save Correction'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    <UserProfilePhotoUploadModal
      isOpen={showProfilePhotoUpload}
      onClose={() => setShowProfilePhotoUpload(false)}
      user={user}
      onSaved={handleProfileSaved}
    />
    </>
  );
}

export default ManagerDashboard;
